import * as THREE from "three";
import { ARENA_KL, ARENA_BOMB } from "@neon-purge/shared";
import { lowSpec, gfxLevel, warmTexture } from "./renderer.js";
import { partsFor, materialForPart, partGeometryFor } from "./viewmodel.js";
import { furTexture, hideTexture } from "./procTextures.js";
import { mapTextures, worldProjectUV } from "./mapTextures.js";
import { vehicleAlbedo, vehicleMaps } from "./vehicleTextures.js";
import { roundCrossSection } from "./avatar.js";
// the creature build toolkit the kaiju are made of — the dragon and the
// sandworm below are built on exactly the same primitives (2026-08-01)
import { vol, taperVol, surf, spike, bake, scaleUV, type Piece, type SkinOpts } from "./creature.js";
import { propMaps, propAlbedo, type PropSurface } from "./propTextures.js";
import { firePuffTexture, smokePuffTexture, emberTexture, shockRingTexture, debrisTexture } from "./combatTextures.js";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";

/** The PROP analogue of `creature.ts`'s `surf` (2026-08-01, the prop pass):
 *  one merged, world-UV'd, procedurally-surfaced mesh. Same contract — pieces
 *  sharing a material become ONE draw call, and the surface detail comes from
 *  the module-cached `propTextures` families rather than a flat colour.
 *
 *  Kept here rather than in `creature.ts` because that file's `surf` is bound
 *  to `MonsterSurface`; a chest is not a creature and the two families must not
 *  bleed into one another. */
function psurf(
  surface: PropSurface, color: string, tile: number, pieces: Piece[], opts: SkinOpts = {},
): THREE.Mesh {
  const geo = bake(pieces);
  if (opts.uv === "own") scaleUV(geo, tile);
  else worldProjectUV(geo, tile);
  const params: THREE.MeshStandardMaterialParameters = {
    color,
    roughness: opts.rough ?? 0.8,
    metalness: opts.metal ?? 0,
    ...(opts.emissive ? { emissive: opts.emissive, emissiveIntensity: opts.ei ?? 0.9 } : {}),
  };
  const m = new THREE.Mesh(geo, new THREE.MeshStandardMaterial(propMaps(surface, params)));
  m.castShadow = opts.shadow ?? true;
  return m;
}

/** A rounded slab with a real bevel — the prop workhorse. `round` 0 = box,
 *  1 = fully elliptical section (see `creature.vol`).
 *
 *  Segment count is chosen from the piece's SIZE: `creature.vol` always spends
 *  2 (300 tris a box), which is right for a 6 m kaiju flank and absurd for a
 *  0.09 m chest bracket. Anything under ~0.55 m drops to 1 segment (108 tris) —
 *  the bevel is what kills the "stacked cubes" read, the tessellation inside it
 *  is not, and there are 25 pickups on a map. */
function slab(w: number, h: number, d: number, round = 0.25): THREE.BufferGeometry {
  if (Math.max(w, h, d) >= 0.55) return vol(w, h, d, round);
  const g = new RoundedBoxGeometry(w, h, d, 1, Math.min(w, h, d) * 0.24) as unknown as THREE.BufferGeometry;
  roundCrossSection(g, w, d, round);
  return g;
}

/** Deep-free a group's GPU resources: geometries, materials, their textures.
 *  scene.remove alone LEAKED every despawned entity for the tab lifetime
 *  (arena throws hundreds of bombs — VRAM climbed all match). */
function disposeGroup(g: THREE.Object3D): void {
  g.traverse((o) => {
    const m = o as THREE.Mesh;
    if ((m.geometry as { userData?: { shared?: boolean } })?.userData?.shared) return; // pooled/shared — owned elsewhere
    m.geometry?.dispose?.();
    const mats = Array.isArray(m.material) ? m.material : m.material ? [m.material] : [];
    for (const mat of mats) {
      const t = mat as THREE.MeshStandardMaterial;
      // module-cached textures (fur/hide/facade/map families) are handed out
      // to MANY meshes — disposing one mount's hide killed the cache for all
      if (t.map && !(t.map.userData as { shared?: boolean })?.shared) t.map.dispose?.();
      mat.dispose?.();
    }
  });
}
import { RoundedBoxGeometry } from "three/examples/jsm/geometries/RoundedBoxGeometry.js";
import { HorseModel, horseModelArmed, horseModelLoaded } from "./horseModel.js";
import { CamelModel, camelModelArmed, camelModelLoaded } from "./camelModel.js";
import { buildTankHull, buildGunshipHull } from "./vehicleHulls.js";

/** POOLED projectile lights — adding/removing a PointLight per grenade/knife/
 *  rocket changed the scene's light COUNT, forcing three.js to recompile every
 *  material's shader (a guaranteed hitch on each throw AND each detonation).
 *  A fixed pool lives in the scene forever; projectiles borrow from it. */
const sharedTexLoader = new THREE.TextureLoader();
// Mount gait footfall phases (leg index 0=LF 1=LH 2=RF 3=RH). Compile-time
// constants — hoisted to module scope so VehicleMesh.update() doesn't rebuild
// them every frame per mount (invariant 6).
const HORSE_LEG_PHASE = [Math.PI / 2, 0, (3 * Math.PI) / 2, Math.PI];
const CAMEL_LEG_PHASE = [0, 0, Math.PI, Math.PI];
const FX_LIGHT_POOL: THREE.PointLight[] = [];
let fxPoolScene: THREE.Scene | null = null;
/** Build the pool for this scene. EXPORTED so the map loader can prewarm it —
 *  lazily building on the first knife/grenade meant the light count jumped
 *  mid-combat: the exact recompile hitch the pool exists to prevent. */
export function ensureFxPool(scene: THREE.Scene): void {
  // NB: guard on the scene ONLY — an empty array just means all 10 are
  // borrowed; rebuilding then would grow the light count (recompile storm)
  if (fxPoolScene === scene) return;
  FX_LIGHT_POOL.length = 0;
  fxPoolScene = scene;
  for (let i = 0; i < 10; i++) {
    const l = new THREE.PointLight("#ffffff", 0, 4, 2);
    scene.add(l);
    FX_LIGHT_POOL.push(l);
  }
}
function acquireFxLight(scene: THREE.Scene): THREE.PointLight | null {
  if (gfxLevel() >= 1) return null; // cost ladder: dynamic lights are shed first
  ensureFxPool(scene);
  return FX_LIGHT_POOL.pop() ?? null; // pool dry → no light (invisible in chaos)
}
function releaseFxLight(scene: THREE.Scene, l: THREE.PointLight | null): void {
  if (!l) return;
  l.intensity = 0;
  l.removeFromParent();
  scene.add(l); // back under the scene root, still counted, still cheap at 0
  FX_LIGHT_POOL.push(l);
}
import {
  PICKUPS,
  pickupInfo,
  GRENADE,
  ARENA_MINE_LIVE_MS,
  VEHICLES,
  CABLE,
  ELEVATOR,
  cablePos,
  pushDyn,
  practiceMoverColliderBoxes,
  practiceMoverPose,
  practiceMovers,
  skyhavenDiscoBall,
  skyhavenDiscoColliderBoxes,
  skyhavenDiscoPose,
  type DynBox,
  type CableRoute,
  type MapDef,
  type PracticeMover,
  type SkyhavenDiscoBall,
} from "@neon-purge/shared";

/** A floating, spinning pickup (health cross / armor shield). */
export class PickupMesh {
  readonly group = new THREE.Group();
  private spin = 0;
  private label: THREE.Sprite;
  private shopLabel = false;
  /** The faint additive glow sphere (opacity 0.12). It is one extra transparent
   *  draw call per pickup and, being additive, contributes only a pixel or two
   *  of glow past ~40 m — invisible, but still submitted. FAR-LOD it: the icon +
   *  body + spin still mark the pickup, so hiding the halo at distance costs no
   *  findability and drops a draw call per far pickup (they scatter map-wide, so
   *  most are far from you at any moment). Hysteretic to avoid a flip when a
   *  pickup sits right at the threshold. */
  private halo: THREE.Mesh | null = null;
  private haloFar = false;

  constructor(scene: THREE.Scene, kind: string) {
    const color =
      kind === "armor" ? PICKUPS.armor.color
      : kind === "grenade" ? PICKUPS.grenade.color
      : kind === "speed" ? PICKUPS.speed.color
      : kind === "dmg" ? PICKUPS.dmg.color
      : kind === "cloak" ? PICKUPS.cloak.color
      : PICKUPS.health.color;
    const mat = new THREE.MeshStandardMaterial({
      color,
      emissive: color,
      emissiveIntensity: 0.5,
      roughness: 0.4,
      metalness: 0.2,
    });
    if (kind === "weaponchest") {
      // DEATHMATCH weapon chest: a BIG floating gun (the real rifle model,
      // giant-sized and gilded) — unmistakably "touch me for a new weapon"
      const { parts } = partsFor("rifle");
      const gun = new THREE.Group();
      for (const part of parts) {
        const m = new THREE.Mesh(
          partGeometryFor(part), // shared, shaped geometry — not a raw box
          materialForPart(part, true),
        );
        m.position.set(part.p[0], part.p[1], part.p[2]);
        gun.add(m);
      }
      gun.scale.setScalar(2.6);
      gun.position.y = 0.75;
      gun.rotation.z = -0.18; // jaunty tilt
      // the gilded plinth ring: a machined band with eight studs, one mesh
      const band: Piece[] = [{ g: new THREE.TorusGeometry(1.15, 0.07, 8, 28), r: [Math.PI / 2, 0, 0] }];
      for (let i = 0; i < 8; i++) {
        const a = (i / 8) * Math.PI * 2;
        band.push({ g: slab(0.14, 0.1, 0.14, 0.6), p: [Math.cos(a) * 1.15, 0.04, Math.sin(a) * 1.15] });
      }
      const ring2 = psurf("steel", "#f4c542", 0.3, band, { rough: 0.35, metal: 0.7, emissive: "#a0631f", ei: 0.9 });
      ring2.position.y = 0.1;
      this.group.add(gun, ring2);
    } else if (kind === "music") {
      // GREEN MUSIC CHEST — a planked crate with a ♪ branded on the front and
      // an iron-banded lid, on the same build as the supply chests.
      const box: Piece[] = [{ g: slab(0.8, 0.5, 0.55, 0.2) }];
      for (let i = 0; i < 3; i++) box.push({ g: slab(0.2, 0.44, 0.05, 0.5), p: [-0.26 + i * 0.26, 0, 0.28] });
      const lidPieces: Piece[] = [
        { g: slab(0.86, 0.16, 0.6, 0.45), p: [0, 0.32, 0] },
        { g: slab(0.9, 0.05, 0.09, 0.5), p: [0, 0.36, 0.22] },
        { g: slab(0.9, 0.05, 0.09, 0.5), p: [0, 0.36, -0.22] },
      ];
      const note = new THREE.Mesh(new THREE.TorusGeometry(0.09, 0.03, 6, 10), new THREE.MeshBasicMaterial({ color: "#0b0e14" }));
      note.position.set(0, 0.02, 0.29);
      const stem = new THREE.Mesh(new THREE.BoxGeometry(0.03, 0.24, 0.03), new THREE.MeshBasicMaterial({ color: "#0b0e14" }));
      stem.position.set(0.08, 0.13, 0.29);
      this.group.add(
        psurf("crate", "#2ecc71", 0.4, box, { rough: 0.75, emissive: "#1dff8a", ei: 0.55 }),
        psurf("steel", "#0e3d24", 0.28, lidPieces, { rough: 0.5, metal: 0.5 }),
        note, stem,
      );
    } else if (kind.startsWith("buy_")) {
      // PURGE buy station: a glowing vendor kiosk — pillar + floating sign
      const kc = kind === "buy_ammo" ? "#ffd166" : kind === "buy_weapon" ? "#ff5a2a" : "#b39dff";
      // A KIOSK, not a post with a sign on it: a treaded plinth, a fluted
      // machined column with a cable conduit up the back, a canopy over a
      // bevelled backlit sign, and a service hatch. Three merged meshes.
      const plinth: Piece[] = [
        { g: new THREE.CylinderGeometry(0.6, 0.76, 0.14, 12), p: [0, -0.92, 0] },
        { g: new THREE.CylinderGeometry(0.5, 0.6, 0.16, 12), p: [0, -0.78, 0] },
      ];
      const column: Piece[] = [
        { g: vol(0.36, 1.5, 0.36, 0.5), p: [0, -0.1, 0] },
        { g: slab(0.1, 1.42, 0.1, 0.9), p: [0, -0.1, -0.22] },          // conduit
        { g: slab(0.44, 0.1, 0.44, 0.4), p: [0, 0.42, 0] },             // capital
        { g: slab(0.3, 0.34, 0.06, 0.3), p: [0, -0.42, 0.19] },         // service hatch
        { g: slab(1.0, 0.09, 0.36, 0.4), p: [0, 1.09, -0.02] },         // canopy
        { g: slab(0.12, 0.24, 0.3, 0.5), p: [0, 0.99, -0.16] },         // canopy bracket
      ];
      const signPieces: Piece[] = [
        { g: slab(0.86, 0.56, 0.16, 0.3), p: [0, 0.75, 0] },
        { g: slab(0.94, 0.08, 0.2, 0.5), p: [0, 0.46, 0] },             // sign shelf
      ];
      const icon = new THREE.Mesh(
        kind === "buy_ammo" ? new THREE.CylinderGeometry(0.1, 0.1, 0.36, 8)
        : kind === "buy_weapon" ? new THREE.BoxGeometry(0.5, 0.12, 0.12)
        : new THREE.SphereGeometry(0.16, 10, 8),
        new THREE.MeshBasicMaterial({ color: "#0b0e14" }));
      icon.position.set(0, 0.75, 0.1);
      this.group.add(
        psurf("tread", "#141a22", 0.4, plinth, { rough: 0.7, metal: 0.4 }),
        psurf("steel", "#141a22", 0.5, column, { rough: 0.55, metal: 0.6 }),
        psurf("enamel", kc, 0.3, signPieces, { rough: 0.22, emissive: kc, ei: 0.9 }),
        icon,
      );
      // SKY BEACON: a tall additive light column so the shop is findable from
      // across the map ("players don't know where to spend points")
      const beacon = new THREE.Mesh(
        new THREE.CylinderGeometry(0.32, 0.55, 26, 8, 1, true),
        new THREE.MeshBasicMaterial({
          color: kc, transparent: true, opacity: 0.28,
          blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide,
        }),
      );
      beacon.position.y = 13.2;
      this.group.add(beacon);
    } else if (kind === "armor") {
      // ARMOUR — was five flat boxes. Now a plate carrier with real volume: a
      // CURVED chest plate (elliptical section, so it wraps a body instead of
      // being a signboard), shoulder yokes, a cummerbund, three mag pouches and
      // a hard trauma plate. Two merged meshes — a woven carrier and its steel
      // furniture — down from five.
      const carrier: Piece[] = [
        { g: vol(0.74, 0.62, 0.3, 0.95), p: [0, 0.06, 0] },       // chest plate, wrapped
        { g: vol(0.68, 0.24, 0.28, 0.9), p: [0, -0.34, 0] },      // cummerbund
        { g: slab(0.16, 0.5, 0.24, 0.6), p: [-0.29, 0.32, 0], r: [0, 0, 0.3] },  // shoulder yoke
        { g: slab(0.16, 0.5, 0.24, 0.6), p: [0.29, 0.32, 0], r: [0, 0, -0.3] },
        { g: vol(0.5, 0.14, 0.26, 0.9), p: [0, 0.44, -0.02] },    // collar
      ];
      for (const px of [-0.22, 0, 0.22]) {                        // mag pouches
        carrier.push({ g: slab(0.19, 0.22, 0.13, 0.35), p: [px, -0.3, 0.19] });
      }
      for (const py of [0.3, 0.16, 0.02, -0.12]) {                // MOLLE ladder
        carrier.push({ g: slab(0.62, 0.045, 0.05, 0.6), p: [0, py, 0.16] });
      }
      const vest = psurf("weave", color, 0.3, carrier, { rough: 0.92, emissive: color, ei: 0.28 });
      const kit: Piece[] = [
        { g: slab(0.26, 0.2, 0.06, 0.35), p: [0, 0.34, 0.17] },   // hard trauma plate, high on the sternum
        { g: slab(0.1, 0.14, 0.05, 0.5), p: [-0.24, -0.02, 0.2] }, // radio clip
        { g: slab(0.06, 0.06, 0.05, 0.8), p: [0.24, -0.02, 0.2] }, // side D-ring
        { g: slab(0.1, 0.12, 0.06, 0.4), p: [0, -0.3, 0.27] },    // buckle
      ];
      for (const px of [-0.22, 0, 0.22]) kit.push({ g: slab(0.15, 0.05, 0.05, 0.5), p: [px, -0.2, 0.25] }); // pouch flap clips
      for (const sx of [-0.29, 0.29]) kit.push({ g: slab(0.11, 0.09, 0.26, 0.4), p: [sx, 0.5, 0] });        // shoulder clips
      const webbing = psurf("steel", "#2a333d", 0.22, kit, { rough: 0.5, metal: 0.65 });
      this.group.add(vest, webbing);
    } else if (kind === "grenade") {
      // GRENADE — was a bare capsule and a peg. Now a real fragmentation body:
      // a fluted casing scored into a waffle by four raised bands, a fuse
      // assembly, a safety SPOON down one flank and a pull ring.
      const shell: Piece[] = [{ g: new THREE.CapsuleGeometry(0.23, 0.26, 6, 14) }];
      for (const by of [-0.15, -0.05, 0.05, 0.15]) {
        shell.push({ g: new THREE.TorusGeometry(0.235, 0.018, 5, 16), p: [0, by, 0], r: [Math.PI / 2, 0, 0] });
      }
      const frag = psurf("steel", "#8faa4a", 0.22, shell, { rough: 0.62, metal: 0.35, emissive: "#3a4a1a", ei: 0.35 });
      const fuse: Piece[] = [
        { g: new THREE.CylinderGeometry(0.075, 0.09, 0.14, 10), p: [0, 0.31, 0] },  // fuse body
        { g: new THREE.CylinderGeometry(0.05, 0.05, 0.06, 8), p: [0, 0.4, 0] },     // striker cap
        { g: slab(0.05, 0.42, 0.09, 0.5), p: [0, 0.16, 0.2], r: [0.16, 0, 0] },     // safety spoon
        { g: new THREE.TorusGeometry(0.07, 0.017, 6, 14), p: [0.13, 0.34, 0], r: [0, Math.PI / 2, 0] }, // pull ring
      ];
      this.group.add(frag, psurf("steel", "#1c1f26", 0.16, fuse, { rough: 0.4, metal: 0.8 }));
    } else if (kind === "speed") {
      // OVERDRIVE — a winged running shoe. Was six flat boxes/cones; now a
      // woven upper with a tongue and a heel counter, a treaded sole with a
      // real toe spring, and swept wings. Three merged meshes, down from six.
      const upper: Piece[] = [
        { g: vol(0.66, 0.24, 0.32, 0.85), p: [0.02, -0.08, 0] },
        { g: taperVol(0.3, 0.2, 0.34, 0.6, 0.9), p: [0.42, -0.05, 0], r: [0, 0, Math.PI / 2] }, // toe box
        { g: slab(0.22, 0.2, 0.26, 0.7), p: [-0.16, 0.06, 0] },     // tongue
        { g: vol(0.16, 0.26, 0.3, 0.9), p: [-0.3, 0.0, 0] },        // heel counter
      ];
      const sole: Piece[] = [
        { g: slab(0.8, 0.09, 0.36, 0.4), p: [0.02, -0.23, 0] },
        { g: slab(0.24, 0.07, 0.34, 0.5), p: [0.44, -0.19, 0], r: [0, 0, -0.35] }, // toe spring
      ];
      const wings: Piece[] = [];
      for (const wz of [-0.22, 0.22]) {
        wings.push({ g: taperVol(0.34, 0.42, 0.07, 0.12, 0.6), p: [-0.26, 0.14, wz], r: [0, 0, -Math.PI / 2 - 0.25 * Math.sign(wz)] });
      }
      wings.push({ g: slab(0.09, 0.5, 0.07, 0.3), p: [0.18, 0.24, 0], r: [0, 0, 0.55] }); // bolt
      this.group.add(
        psurf("weave", "#1d2732", 0.24, upper, { rough: 0.8 }),
        psurf("tread", color, 0.3, sole, { rough: 0.6, emissive: color, ei: 0.5 }),
        psurf("enamel", color, 0.22, wings, { rough: 0.25, emissive: color, ei: 0.8 }),
      );
    } else if (kind === "dmg") {
      // OVERCHARGE — a spiked damage core. Was eleven separate meshes (a
      // faceted core plus eight cones plus a slash); now the core and its
      // eight blades are ONE merged, enamelled buffer with a steel cage.
      const core: Piece[] = [{ g: new THREE.IcosahedronGeometry(0.32, 1) }];
      const cage: Piece[] = [];
      for (let i = 0; i < 8; i++) {
        const a2 = (i / 8) * Math.PI * 2;
        core.push({ g: spike(0.085, 0.4), p: [Math.cos(a2) * 0.42, Math.sin(a2) * 0.42, 0], r: [0, 0, -a2 + Math.PI / 2] });
        cage.push({ g: slab(0.05, 0.05, 0.05, 0.8), p: [Math.cos(a2) * 0.26, Math.sin(a2) * 0.26, 0] }); // collar studs
      }
      cage.push({ g: new THREE.TorusGeometry(0.29, 0.028, 6, 24) });
      cage.push({ g: slab(0.92, 0.07, 0.07, 0.5), r: [0, 0, -0.65] }); // the slash
      this.group.add(
        psurf("enamel", color, 0.2, core, { rough: 0.24, metal: 0.3, emissive: color, ei: 0.7 }),
        psurf("steel", "#f4f8ff", 0.18, cage, { rough: 0.3, metal: 0.8, emissive: "#ffffff", ei: 0.45 }),
      );
    } else if (kind === "cloak") {
      // CLOAK — a ghost hood. Was eight meshes in a flat colour; now one merged
      // woven shroud (hood, cowl brow, shoulders, a tapering robe and five
      // hanging hem points) plus the two eyes.
      const shroud: Piece[] = [
        { g: new THREE.SphereGeometry(0.34, 16, 10, 0, Math.PI * 2, 0, Math.PI * 0.72), p: [0, 0.18, 0] },
        { g: new THREE.TorusGeometry(0.3, 0.055, 6, 18), p: [0, 0.2, 0.04], r: [1.35, 0, 0] }, // cowl brow
        { g: taperVol(0.72, 0.34, 0.5, 1.35, 0.95), p: [0, -0.1, 0] },                        // shoulders
        { g: new THREE.ConeGeometry(0.44, 0.72, 18, 1, true), p: [0, -0.18, 0] },
      ];
      for (let i = -2; i <= 2; i++) {
        shroud.push({ g: spike(0.1, 0.26), p: [i * 0.16, -0.58, Math.abs(i) * 0.05 - 0.05], r: [Math.PI, 0, i * 0.12] });
      }
      const eyeMat = new THREE.MeshBasicMaterial({ color: "#0b0f18" });
      const eyeL = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.08, 0.04), eyeMat);
      eyeL.position.set(-0.12, 0.25, 0.31);
      const eyeR = eyeL.clone(); eyeR.position.x = 0.12;
      this.group.add(psurf("weave", color, 0.28, shroud, { rough: 0.9, emissive: color, ei: 0.4 }), eyeL, eyeR);
    } else if (kind === "jetpack" || kind === "rocket" || kind === "imperion" || kind === "antigrav" || kind === "decoy" || kind === "fartblaster" || kind === "beigechest" || kind === "waterchest") {
      // a CHEST — colored crate with a lid + a glowing icon on top
      const isJet = kind === "jetpack";
      const isImp = kind === "imperion" || kind === "antigrav";
      const isAg = kind === "antigrav";
      const isDec = kind === "decoy"; // BODY DOUBLE: hologram pink
      const isFart = kind === "fartblaster"; // HAZARD: bright red, green fumes
      const isBeige = kind === "beigechest"; // PENIS GUN: beige crate, white spray
      const isWater = kind === "waterchest"; // WATER GUN: purple crate, water mist
      if (isBeige || isWater) {
        // creamy white spray puffs orbiting the lid (like the fart chest's green
        // fumes, but white) — hints at what's inside
        const spray = new THREE.MeshBasicMaterial({ color: isWater ? "#b778ff" : "#f2efe6", transparent: true, opacity: 0.32, blending: THREE.AdditiveBlending, depthWrite: false });
        for (let i = 0; i < 3; i++) {
          const puff = new THREE.Mesh(new THREE.SphereGeometry(0.2 + i * 0.07, 8, 8), spray);
          const a = (i / 3) * Math.PI * 2;
          puff.position.set(Math.cos(a) * 0.55, 0.85 + i * 0.22, Math.sin(a) * 0.55);
          this.group.add(puff);
        }
      }
      if (isFart) {
        // billowing green fumes: three translucent puffs orbiting the lid —
        // moved by the shared pickup bob/spin (children of the group), so no
        // extra per-frame work; additive so they read as gas, not solids
        const gas = new THREE.MeshBasicMaterial({ color: "#39ff5c", transparent: true, opacity: 0.28, blending: THREE.AdditiveBlending, depthWrite: false });
        for (let i = 0; i < 3; i++) {
          const puff = new THREE.Mesh(new THREE.SphereGeometry(0.22 + i * 0.07, 8, 8), gas);
          const a = (i / 3) * Math.PI * 2;
          puff.position.set(Math.cos(a) * 0.55, 0.85 + i * 0.22, Math.sin(a) * 0.55);
          this.group.add(puff);
        }
      }
      // THE CHEST ITSELF (rebuilt 2026-08-01, the prop pass). Was four boxes:
      // a slab body, a slab lid and two flat trim bands. Now a planked crate
      // with a BARREL-VAULTED lid, iron corner brackets, hinges, a hasp and
      // foot rails — one merged `crate` mesh + one merged `steel` mesh, i.e.
      // far more volume for FEWER draw calls than the four boxes cost.
      const staveW = 1.06 / 4;
      const body: Piece[] = [{ g: slab(1.06, 0.56, 0.78, 0.18), p: [0, 0.3, 0] }];
      // vertical staves standing proud of the body, so the silhouette is
      // grooved rather than a smooth slab
      for (let i = 0; i < 4; i++) {
        const x = -1.06 / 2 + staveW * (i + 0.5);
        body.push({ g: slab(staveW * 0.82, 0.5, 0.06, 0.5), p: [x, 0.3, 0.4] });
        body.push({ g: slab(staveW * 0.82, 0.5, 0.06, 0.5), p: [x, 0.3, -0.4] });
      }
      // the lid: a half-cylinder vault running along the chest's length
      const vault = new THREE.CylinderGeometry(0.4, 0.4, 1.08, 18, 1, false, 0, Math.PI);
      body.push({ g: vault, p: [0, 0.58, 0], r: [0, 0, Math.PI / 2], s: [1, 1, 0.92] });
      const crate = psurf("crate", isWater ? "#4b1b86" : isBeige ? "#d9c8a0" : isFart ? "#a01a1a" : isDec ? "#2a1430" : isAg ? "#1d1230" : isImp ? "#151d2c" : isJet ? "#3a6ea5" : "#8a4a2a", 0.5, body, { rough: 0.9 });
      const ironCol = isWater ? "#c17bff" : isBeige ? "#c9b482" : isFart ? "#ff3b30" : isDec ? "#ff2ec4" : isAg ? "#c17bff" : isImp ? "#00f0ff" : isJet ? "#9fe0ff" : "#f4c542";
      const fit: Piece[] = [];
      for (const bx of [-0.48, 0.48]) {
        // corner brackets: an L wrapping the body's vertical edge, front + back
        for (const bz of [-0.37, 0.37]) {
          fit.push({ g: slab(0.09, 0.6, 0.1, 0.4), p: [bx, 0.3, bz] });
          fit.push({ g: slab(0.16, 0.09, 0.1, 0.4), p: [bx * 0.86, 0.03, bz] }); // foot
        }
        fit.push({ g: new THREE.TorusGeometry(0.11, 0.026, 6, 12), p: [bx * 0.98, 0.34, 0], r: [0, Math.PI / 2, 0] }); // carry ring
      }
      // hinges along the back of the vault, and the front hasp + lock plate
      for (const hx of [-0.32, 0.32]) fit.push({ g: slab(0.12, 0.1, 0.22, 0.5), p: [hx, 0.66, -0.34] });
      fit.push({ g: slab(0.2, 0.24, 0.07, 0.35), p: [0, 0.5, 0.4] });      // lock plate
      fit.push({ g: slab(0.09, 0.2, 0.05, 0.5), p: [0, 0.63, 0.41] });      // hasp
      fit.push({ g: new THREE.TorusGeometry(0.045, 0.016, 6, 10), p: [0, 0.44, 0.44], r: [Math.PI / 2, 0, 0] }); // keyhole ring
      const ironwork = psurf("steel", ironCol, 0.35, fit, { rough: 0.42, metal: 0.6, emissive: ironCol, ei: isImp || isDec || isFart || isWater ? 0.5 : 0.25 });
      this.group.add(crate, ironwork);
      // floating icon: up-arrows for jetpack, a rocket for the launcher, a
      // hollow "mirror frame" for the body double
      const iconMat = new THREE.MeshBasicMaterial({ color: isWater ? "#d7b6ff" : isBeige ? "#e7c9cf" : isFart ? "#39ff5c" : isDec ? "#ff2ec4" : isJet ? "#9dff00" : "#ff5a2a" });
      const icon = new THREE.Mesh(
        isBeige ? new THREE.SphereGeometry(0.2, 12, 10) // a little pink glans
        : isFart ? new THREE.TorusGeometry(0.24, 0.09, 8, 14) // a little smoke ring
        : isDec ? new THREE.TorusGeometry(0.2, 0.05, 6, 4)
        : isJet ? new THREE.ConeGeometry(0.18, 0.4, 4)
        : new THREE.CylinderGeometry(0.08, 0.08, 0.5, 8), iconMat);
      icon.position.y = 1.15;
      if (!isJet && !isDec && !isFart && !isBeige) icon.rotation.z = Math.PI / 2;
      if (isDec) icon.rotation.z = Math.PI / 4; // diamond-on-point frame
      this.group.add(icon);
    } else {
      // HEALTH — was two bare boxes crossed. Now a moulded medkit: a bevelled
      // enamel case in the shape of the cross, a raised centre boss, a recessed
      // white cross inlay on both faces and steel corner clasps with a handle.
      const arms: Piece[] = [
        { g: slab(0.86, 0.3, 0.3, 0.45) },
        { g: slab(0.3, 0.86, 0.3, 0.45) },
        { g: slab(0.34, 0.34, 0.36, 0.7) }, // centre boss
      ];
      const shell = psurf("enamel", color, 0.32, arms, { rough: 0.22, metal: 0.15, emissive: color, ei: 0.35 });
      const inlay: Piece[] = [];
      for (const z of [-0.19, 0.19]) {
        inlay.push({ g: slab(0.56, 0.17, 0.03, 0.2), p: [0, 0, z] });
        inlay.push({ g: slab(0.17, 0.56, 0.03, 0.2), p: [0, 0, z] });
      }
      const cross = psurf("enamel", "#f7fbff", 0.32, inlay, { rough: 0.2, emissive: "#dfeeff", ei: 0.5 });
      const clasps: Piece[] = [];
      for (const [cx, cy] of [[-0.43, 0], [0.43, 0], [0, -0.43], [0, 0.43]] as const) {
        clasps.push({ g: slab(cx ? 0.06 : 0.24, cy ? 0.06 : 0.24, 0.34, 0.5), p: [cx, cy, 0] });
      }
      clasps.push({ g: new THREE.TorusGeometry(0.11, 0.025, 6, 12), p: [0, 0.5, 0], r: [Math.PI / 2, 0, 0] });
      const trim2 = psurf("steel", "#c9d4de", 0.24, clasps, { rough: 0.35, metal: 0.75 });
      this.group.add(shell, cross, trim2);
    }
    // glow halo
    const halo = new THREE.Mesh(
      new THREE.SphereGeometry(0.7, 12, 12),
      new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.12, blending: THREE.AdditiveBlending }),
    );
    this.halo = halo;
    halo.name = "npHalo";
    this.group.add(halo);
    const info = pickupInfo(kind);
    this.label = makePickupLabel(info.name, info.effect, info.keyHint, info.action);
    this.shopLabel = info.action === "shop";
    // shop labels sit higher and larger — they must read from further away
    if (this.shopLabel) {
      this.label.position.y = 2.3;
      this.label.scale.set(5.4, 2.02, 1);
    } else {
      // 1.2 (was 1.65) so the label clears LOW ceilings (the pirate ship's
      // gun-deck hold) — up higher it was cropped by the deck above
      this.label.position.y = 1.2;
      this.label.scale.set(3.4, 1.27, 1);
    }
    this.group.add(this.label);
    scene.add(this.group);
  }

  update(x: number, y: number, z: number, active: boolean, dt: number, t: number, camDist = 0): void {
    this.group.visible = active !== false;
    this.spin += dt * 1.6;
    this.group.position.set(x, y + 1.0 + Math.sin(t * 2) * PICKUPS.bob, z);
    this.group.rotation.y = this.spin;
    // info labels read only UP CLOSE — full-size labels at any distance
    // visually buried the item models ("the items disappeared"), and walls
    // occlude them now (depthTest). Shops read from further out (their
    // beacon already marks them map-wide).
    const near = this.shopLabel ? 22 : 9;
    const far = this.shopLabel ? 30 : 13;
    const o = camDist > far ? 0 : camDist < near ? 1 : (far - camDist) / (far - near);
    this.label.visible = o > 0.02;
    (this.label.material as THREE.SpriteMaterial).opacity = o;
    // FAR-LOD the glow halo (42 m on / 36 m off). Only touches .visible on a
    // real transition, so per frame this is one compare — no churn.
    if (this.halo) {
      const wantFar = camDist > (this.haloFar ? 36 : 42);
      if (wantFar !== this.haloFar) {
        this.haloFar = wantFar;
        this.halo.visible = !wantFar;
      }
    }
  }

  dispose(scene: THREE.Scene): void {
    scene.remove(this.group);
    disposeGroup(this.group);
  }
}

function makePickupLabel(name: string, effect: string, key: string, action: string): THREE.Sprite {
  const canvas = document.createElement("canvas");
  // 320x120 (was 512x192): labels only render within ~13m — the fat canvas
  // was pure VRAM/upload waste ×25 pickups per map. Drawing code keeps its
  // 512x192 coordinate space via the transform below.
  canvas.width = 320;
  canvas.height = 120;
  const ctx = canvas.getContext("2d")!;
  ctx.scale(320 / 512, 120 / 192);
  ctx.clearRect(0, 0, 512, 192);
  ctx.fillStyle = "rgba(5,8,14,0.86)";
  roundRect(ctx, 16, 18, 480, 142, 16);
  ctx.fill();
  ctx.strokeStyle = action === "shop" ? "#ffd166" : action === "inventory" ? "#37d67a" : action === "equip" ? "#ff2ec4" : "#00f0ff";
  ctx.lineWidth = 4;
  ctx.stroke();
  ctx.font = "700 32px Courier New, monospace";
  ctx.fillStyle = "#eaf3ff";
  ctx.textAlign = "center";
  ctx.fillText(name.toUpperCase(), 256, 62);
  ctx.font = "24px Courier New, monospace";
  ctx.fillStyle = "#9dff00";
  ctx.fillText(effect, 256, 100);
  ctx.font = "20px Courier New, monospace";
  ctx.fillStyle = "#9aa8ba";
  ctx.fillText(`${key} - ${action.toUpperCase()}`, 256, 132);
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  // depthTest ON: labels must NOT read through walls (x-ray item vision)
  const mat = new THREE.SpriteMaterial({ map: tex, transparent: true, depthWrite: false });
  const sprite = new THREE.Sprite(mat);
  sprite.scale.set(3.8, 1.42, 1);
  sprite.renderOrder = 20;
  return sprite;
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

/** One wheel position on a vehicle. `r`/`w` are the tyre radius/width; `spokes`
 *  = 0 gives a plain disc rim (road wheels, cart wheels). */
export interface WheelSpot { x: number; y: number; z: number; r: number; w: number; spokes?: number }

/**
 * A treaded tyre as a LATHE of the real cross-section: a rim seat, a rounded
 * shoulder, a flat contact patch. Every wheel in the game used to be a 10-12
 * segment `CylinderGeometry` in flat black — a hexagonal drum with no rim, no
 * shoulder, no tread. This is the single biggest silhouette tell that a
 * vehicle is a stack of boxes rather than a machine.
 *
 * UVs are rewritten so the tread texture wraps AROUND the circumference (one
 * repeat per `TREAD_TILE` metres of rolling circumference) and spans the tread
 * face exactly once across — the lathe's own u/v are the other way round.
 */
const TREAD_TILE = 1.2;
function tyreGeometry(r: number, w: number): THREE.BufferGeometry {
  const hw = w / 2;
  const geo = new THREE.LatheGeometry([
    new THREE.Vector2(r * 0.5, -hw * 0.98),
    new THREE.Vector2(r * 0.72, -hw),
    new THREE.Vector2(r * 0.9, -hw * 0.94),
    new THREE.Vector2(r * 0.985, -hw * 0.66),
    new THREE.Vector2(r, -hw * 0.24),
    new THREE.Vector2(r, hw * 0.24),
    new THREE.Vector2(r * 0.985, hw * 0.66),
    new THREE.Vector2(r * 0.9, hw * 0.94),
    new THREE.Vector2(r * 0.72, hw),
    new THREE.Vector2(r * 0.5, hw * 0.98),
  ], 22);
  const uv = geo.attributes.uv;
  const around = (2 * Math.PI * r) / TREAD_TILE;
  for (let i = 0; i < uv.count; i++) {
    const theta = uv.getX(i), prof = uv.getY(i);
    uv.setXY(i, prof, theta * around);
  }
  uv.needsUpdate = true;
  geo.rotateZ(Math.PI / 2); // lathe spins about Y; every axle here is X
  return geo;
}

/** Rim + hub + spokes for one wheel, as a single geometry. */
function rimGeometry(r: number, w: number, spokes: number): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];
  const barrel = new THREE.CylinderGeometry(r * 0.56, r * 0.56, w * 0.9, 16);
  barrel.rotateZ(Math.PI / 2);
  parts.push(barrel.toNonIndexed());
  const hub = new THREE.CylinderGeometry(r * 0.2, r * 0.24, w * 1.02, 12);
  hub.rotateZ(Math.PI / 2);
  parts.push(hub.toNonIndexed());
  for (let i = 0; i < spokes; i++) {
    const a = (i / spokes) * Math.PI * 2;
    const sp = new THREE.BoxGeometry(w * 0.34, r * 0.62, r * 0.17);
    sp.translate(0, r * 0.34, 0);
    sp.rotateX(a);
    parts.push(sp.toNonIndexed());
  }
  const merged = mergeGeometries(parts, false)!;
  for (const p of parts) p.dispose();
  worldProjectUV(merged, 0.35);
  return merged;
}

/**
 * All of a vehicle's wheels in TWO meshes: one merged tyre geometry, one
 * merged rim geometry. Nothing here rotates (no vehicle in this game spins its
 * wheels), so merging is free — and it means the new, far more detailed wheel
 * costs FEWER draw calls than the old flat cylinders: a truck went from 8 to 2.
 */
function wheelSet(spots: WheelSpot[], tyreMat: THREE.Material, rimMat: THREE.Material): THREE.Mesh[] {
  const tyres: THREE.BufferGeometry[] = [];
  const rims: THREE.BufferGeometry[] = [];
  for (const s of spots) {
    const t = tyreGeometry(s.r, s.w).toNonIndexed();
    t.translate(s.x, s.y, s.z);
    tyres.push(t);
    const rm = rimGeometry(s.r, s.w, s.spokes ?? 5);
    rm.translate(s.x, s.y, s.z);
    rims.push(rm);
  }
  const out: THREE.Mesh[] = [];
  for (const [geos, mat] of [[tyres, tyreMat], [rims, rimMat]] as const) {
    const merged = mergeGeometries(geos as THREE.BufferGeometry[], false)!;
    for (const g of geos as THREE.BufferGeometry[]) g.dispose();
    const m = new THREE.Mesh(merged, mat as THREE.Material);
    m.castShadow = true;
    out.push(m);
  }
  return out;
}

/**
 * hwaccel 2026-08-25: a parked/driven vehicle is a RIGID machine — every part
 * that moves relative to the hull (the rotor, the aimed turret, the orbiting
 * bomb rack) is a sub-`THREE.Group`, the nameplate is a `THREE.Sprite`, and the
 * wheels never spin (see wheelSet). So EVERY direct `THREE.Mesh` child of the
 * group is static and can be merged by (material, shadow flags) into one draw
 * call per bucket — exactly the critter / pickup / dragon merge, and safe by
 * construction: the filter only ever touches flat static panels (`isMesh`,
 * single-material, not instanced, not `userData.shared`), so sub-Groups and the
 * Sprite are left untouched and all animation is byte-identical. The kept
 * material INSTANCES are reused unchanged, so setDriver's per-driver retint
 * (bodyMat / accentMat / underglowMats) still repaints the merged meshes.
 *
 * A parked kart was ~14 draw calls, a truck ~20; arena renders one kart per
 * player, so this is a persistent per-frame draw-call cut, not a transient one.
 *
 * hwaccel 2026-08-26: extended to horse/camel — the head/neck and tail bob per
 * frame (flagged `userData.animated`, skipped below) and the legs are sub-Groups
 * (skipped by the `isMesh` gate), but the torso, ears, saddle rig, bridle and
 * reins are all rigid direct meshes, ~30 of the ~62 per mount. medieval_castle
 * spawns SIX horses, so this is the single biggest per-frame vehicle draw-call
 * cut in the fleet.
 */
export function mergeVehicleBody(group: THREE.Group): void {
  interface Bucket { mat: THREE.Material; cast: boolean; recv: boolean; geos: THREE.BufferGeometry[]; meshes: THREE.Mesh[]; }
  const buckets = new Map<string, Bucket>();
  let matId = 0;
  const matKey = new Map<THREE.Material, number>();
  for (const child of group.children) {
    const m = child as THREE.Mesh;
    // Groups (rotor/turret/bombs/doors) -> isMesh false; Sprite (plate) ->
    // isMesh false; InstancedMesh -> instances would collapse. Multi-material
    // and shared/pooled geometries are left as their own draw call.
    if (!m.isMesh || (m as unknown as THREE.InstancedMesh).isInstancedMesh) continue;
    if (Array.isArray(m.material) || !m.material) continue;
    if ((m.geometry as { userData?: { shared?: boolean } }).userData?.shared) continue;
    // per-frame animated direct meshes (mount head/neck bob + tail sway) must
    // keep their own transform — merging would freeze them into the hull.
    if (m.userData?.animated) continue;
    const mat = m.material as THREE.Material;
    let id = matKey.get(mat);
    if (id === undefined) { id = matId++; matKey.set(mat, id); }
    const key = `${id}|${m.castShadow ? 1 : 0}|${m.receiveShadow ? 1 : 0}`;
    let b = buckets.get(key);
    if (!b) { b = { mat, cast: m.castShadow, recv: m.receiveShadow, geos: [], meshes: [] }; buckets.set(key, b); }
    m.updateMatrix();
    const baked = m.geometry.clone().applyMatrix4(m.matrix);
    // normalise so mergeGeometries never sees mixed indexing (some parts —
    // wheels — arrive non-indexed already; only convert the indexed ones)
    const flat = baked.index ? baked.toNonIndexed() : baked;
    if (flat !== baked) baked.dispose();
    b.geos.push(flat);
    b.meshes.push(m);
  }
  for (const b of buckets.values()) {
    if (b.meshes.length < 2) { for (const g of b.geos) g.dispose(); continue; } // nothing to save
    const merged = mergeGeometries(b.geos, false);
    for (const g of b.geos) g.dispose();
    if (!merged) continue; // mismatched attributes — keep the originals untouched
    for (const m of b.meshes) { group.remove(m); m.geometry.dispose(); }
    const one = new THREE.Mesh(merged, b.mat);
    one.castShadow = b.cast;
    one.receiveShadow = b.recv;
    group.add(one);
  }
}

/** A drivable vehicle — a low-poly kart or helicopter with a spinning rotor. */
export class VehicleMesh {
  readonly group = new THREE.Group();
  readonly kind: string;
  private rotor: THREE.Object3D | null = null;
  private rotorAxis: "y" | "z" = "y";
  /** Gunship tail rotor (spins about +x). */
  private tailRotor: THREE.Object3D | null = null;
  private turret: THREE.Group | null = null;
  private bombGroup: THREE.Group | null = null;
  private bombCount = -1;
  private bombT = 0;
  private horseLegs: THREE.Object3D[] = [];
  /** Head+neck cluster (bobs with the walk as ONE rigid unit) — its meshes live
   *  under this sub-Group and are merged by material (hwaccel), so the whole
   *  head is a handful of draw calls that nod by moving the group, not N. */
  private headGroup: THREE.Group | null = null;
  private horseTail: THREE.Object3D | null = null;
  /** Lower-leg pivots (knee/hock) — flexed opposite the stride for real gait. */
  private horseShins: THREE.Object3D[] = [];
  private legRootY = 1.0;
  private horseT = 0;
  /** The realistic horse (horseModel.ts), once the module is armed. While set,
   *  the procedural body is hidden and the gait comes from its clips. */
  private horseModel: HorseModel | null = null;
  /** The realistic camel (camelModel.ts) — same contract as horseModel. */
  private camelModel: CamelModel | null = null;
  private hmX = NaN;
  private hmZ = 0;
  private hmSpeed = 0;
  /** Drift skid-smoke pool (arena dérapage animation). */
  private skids: { m: THREE.Mesh; die: number; base: number }[] = [];
  private skidMat: THREE.MeshBasicMaterial | null = null;
  private lastSkidAt = 0;
  private bodyMat!: THREE.MeshStandardMaterial;
  private accentMat!: THREE.MeshStandardMaterial;
  /** Shared hide/fur texture (mounts) restored when a rider dismounts — must
   *  NOT be disposed like a per-driver skin, it's cached and shared. */
  private defaultBodyMap: THREE.Texture | null = null;
  private origBody = "";
  private origAccent = "";
  private plate: THREE.Sprite | null = null;
  private plateName: string | null = null;

  /** Occupied vehicle: hide-the-driver's stand-in — tint the machine with the
   *  driver's colors and float their nameplate above it (same style as the
   *  avatar plates). Pass name "" to revert to stock colors. */
  /** Bombkart neon underglow parts (retinted per driver in arena). */
  private underglowMats: (THREE.MeshStandardMaterial | THREE.MeshBasicMaterial)[] = [];
  private underglowLight: THREE.PointLight | null = null;
  /** A borrowed FX_LIGHT_POOL light riding in the group — returned on dispose. */
  private pooledLight: THREE.PointLight | null = null;
  /** The pooled light's configured (lit) intensity, captured at construction so
   *  dormant()/wake() can zero and restore it without knowing the kind. */
  private pooledLightIntensity = 0;

  setDriver(name: string, bodyColor = "", glowColor = "", skinUrl = ""): void {
    if (name === this.plateName) return;
    this.plateName = name;
    if (name) {
      if (glowColor) this.accentMat.color.set(glowColor);
      if (glowColor && this.underglowMats.length) {
        // the whole underglow (strips + light) wears the driver's neon
        for (const m of this.underglowMats) {
          m.color.set(glowColor);
          if ((m as THREE.MeshStandardMaterial).emissive) (m as THREE.MeshStandardMaterial).emissive.set(glowColor);
        }
        this.underglowLight?.color.set(glowColor);
      }
      if (skinUrl) {
        // custom-drawn skin → paint the HULL with the actual picture (this is
        // the driver's visible identity, not their base color)
        const tex = sharedTexLoader.load(skinUrl, (t) => { warmTexture(t); this.bodyMat.needsUpdate = true; });
        tex.colorSpace = THREE.SRGBColorSpace;
        if (this.bodyMat.map && this.bodyMat.map !== this.defaultBodyMap) this.bodyMat.map.dispose();
        this.bodyMat.map = tex;
        this.bodyMat.color.set("#ffffff");
        this.bodyMat.needsUpdate = true;
      } else if (bodyColor) {
        // no skin: keep the hide/fur texture (default map), just retint it
        if (this.bodyMat.map && this.bodyMat.map !== this.defaultBodyMap) { this.bodyMat.map.dispose(); this.bodyMat.map = this.defaultBodyMap; }
        this.bodyMat.color.set(bodyColor);
        this.bodyMat.needsUpdate = true;
      }
      const cvs = document.createElement("canvas");
      cvs.width = 256; cvs.height = 64;
      const c = cvs.getContext("2d")!;
      c.font = "bold 30px monospace";
      c.textAlign = "center"; c.textBaseline = "middle";
      c.fillStyle = "rgba(0,0,0,0.55)";
      const w = Math.min(244, c.measureText(name).width + 24);
      c.fillRect(128 - w / 2, 10, w, 44);
      c.fillStyle = "#ffffff";
      c.strokeStyle = "#000"; c.lineWidth = 4;
      c.strokeText(name, 128, 33); c.fillText(name, 128, 33);
      const tex = new THREE.CanvasTexture(cvs);
      if (!this.plate) {
        this.plate = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, transparent: true, depthTest: true }));
        this.plate.scale.set(3.2, 0.8, 1);
        this.plate.position.set(0, this.kind === "camel" ? 3.75 : this.kind === "horse" ? 3.35 : this.kind === "heli" || this.kind === "truck" || this.kind === "miniplane" ? 3.6 : 2.6, 0);
        this.group.add(this.plate);
      } else {
        (this.plate.material as THREE.SpriteMaterial).map?.dispose();
        (this.plate.material as THREE.SpriteMaterial).map = tex;
        (this.plate.material as THREE.SpriteMaterial).needsUpdate = true;
      }
      this.plate.visible = true;
    } else {
      this.accentMat.color.set(this.origAccent);
      // dispose a per-driver SKIN, but restore (never dispose) the shared
      // hide/fur default — nulling it stripped the texture off riderless mounts
      if (this.bodyMat.map && this.bodyMat.map !== this.defaultBodyMap) this.bodyMat.map.dispose();
      this.bodyMat.map = this.defaultBodyMap;
      this.bodyMat.color.set(this.origBody);
      this.bodyMat.needsUpdate = true;
      if (this.plate) this.plate.visible = false;
    }
  }

  constructor(scene: THREE.Scene, kind: string) {
    this.kind = kind;
    const cfg = VEHICLES[kind] ?? VEHICLES.kart;
    // PAINTED / PLATED surfaces (2026-08-01 `graphics`): every vehicle material
    // used to be a flat colour, so a 6 m cargo box and a 0.3 m bumper caught
    // light identically. `vehicleMaps` composites its detail over white and
    // leaves `color` alone, so `setDriver`'s retint still works untouched.
    const body = new THREE.MeshStandardMaterial(vehicleMaps("paint", { color: cfg.color, roughness: 0.42, metalness: 0.55 }));
    const accent = new THREE.MeshStandardMaterial(vehicleMaps("paint", { color: cfg.accent, roughness: 0.4, metalness: 0.5 }));
    this.bodyMat = body; this.accentMat = accent;
    this.defaultBodyMap = vehicleAlbedo("paint"); // restored when a driver leaves
    this.origBody = cfg.color; this.origAccent = cfg.accent;
    const dark = new THREE.MeshStandardMaterial(vehicleMaps("metal", { color: "#16181d", roughness: 0.62, metalness: 0.62 }));
    /** Heavy industrial plate — riveted seams + weld beads. */
    const plate = (color: string, rough = 0.72, metal = 0.35): THREE.MeshStandardMaterial =>
      new THREE.MeshStandardMaterial(vehicleMaps("plate", { color, roughness: rough, metalness: metal }));
    /** Windows: smudged, mirror-smooth, slightly tinted. */
    const glassMat = (color: string, opacity = 0.82): THREE.MeshStandardMaterial =>
      new THREE.MeshStandardMaterial(vehicleMaps("glass", {
        color, roughness: 0.08, metalness: 0.55, transparent: true, opacity,
      }, 0.6));
    const box = (w: number, h: number, d: number, mat: THREE.Material, p: [number, number, number], tile = 0.9): THREE.Mesh => {
      // chamfered hull panels — vehicles read as machines, not crates
      const r = Math.min(0.08, Math.min(w, h, d) * 0.25);
      const geo = new RoundedBoxGeometry(w, h, d, 2, r);
      // WORLD-UNIT texel density: without this every panel would stretch one
      // tile of rivets across whatever size it happens to be.
      worldProjectUV(geo, tile);
      const m = new THREE.Mesh(geo, mat);
      m.position.set(p[0], p[1], p[2]);
      m.castShadow = true;
      return m;
    };
    /** A rounded hull volume: same call as `box` but the cross-section is
     *  elliptical, so fuselages/cabins/noses stop being slabs. */
    const hull = (w: number, h: number, d: number, mat: THREE.Material, p: [number, number, number], round = 0.85, tile = 0.9): THREE.Mesh => {
      const geo = new RoundedBoxGeometry(w, h, d, 3, Math.min(0.1, Math.min(w, h, d) * 0.25));
      roundCrossSection(geo, w, d, round);
      worldProjectUV(geo, tile);
      const m = new THREE.Mesh(geo, mat);
      m.position.set(p[0], p[1], p[2]);
      m.castShadow = true;
      return m;
    };
    /** Treaded wheels for a whole vehicle in TWO draw calls (tyres merged,
     *  rims+spokes merged) — see `wheelSet`. Replaces the flat 10-12 segment
     *  cylinders every wheeled vehicle used to be shod with. */
    const wheels = (spots: WheelSpot[], tyreMat: THREE.Material, rimMat: THREE.Material): void => {
      for (const m of wheelSet(spots, tyreMat, rimMat)) this.group.add(m);
    };

    if (kind === "delorean") {
      // DMC-12 TIME MACHINE: a low stainless-steel wedge with gull-wing doors
      // thrown open, the louvered rear window, and the rear reactor / vent stack
      // / flux cabling. Stationary in gameplay; the cinematic drives it.
      // brushed STAINLESS, not painted: the DMC-12's whole look is the unpainted
      // panel grain, which the flat material could not show at all
      const steel = new THREE.MeshStandardMaterial(vehicleMaps("metal", { color: "#c4ccd4", roughness: 0.3, metalness: 0.95 }, 1.2));
      const trim = new THREE.MeshStandardMaterial(vehicleMaps("metal", { color: "#171a1f", roughness: 0.58, metalness: 0.45 }));
      const glass = new THREE.MeshStandardMaterial(vehicleMaps("glass", { color: "#8fe4ff", emissive: "#1fb8ff", emissiveIntensity: 0.35, roughness: 0.09, metalness: 0.6, transparent: true, opacity: 0.8 }, 0.6));
      const flux = new THREE.MeshBasicMaterial({ color: "#34f7ff" });
      const hot = new THREE.MeshBasicMaterial({ color: "#ff7a2a" });
      const cyl = (r1: number, r2: number, h: number, mat: THREE.Material, p: [number, number, number], axis: "x" | "y" | "z" = "y"): THREE.Mesh => {
        const m = new THREE.Mesh(new THREE.CylinderGeometry(r1, r2, h, 16), mat);
        if (axis === "z") m.rotation.x = Math.PI / 2;
        if (axis === "x") m.rotation.z = Math.PI / 2;
        m.position.set(p[0], p[1], p[2]); m.castShadow = true; return m;
      };
      // lower body: black rocker skirt + stainless tub, hood sloping to a low nose
      this.group.add(box(2.02, 0.22, 3.95, trim, [0, 0.34, 0]));
      this.group.add(box(1.98, 0.42, 3.8, steel, [0, 0.62, 0]));
      this.group.add(box(1.86, 0.16, 1.5, steel, [0, 0.82, -1.28]));  // hood
      this.group.add(box(1.9, 0.3, 0.5, steel, [0, 0.66, -1.98]));    // nose
      this.group.add(box(1.94, 0.14, 0.3, trim, [0, 0.5, -2.06]));    // front bumper
      this.group.add(box(1.5, 0.09, 0.22, hot, [0, 0.64, -2.02]));    // headlight bar
      // cabin: low greenhouse set back, raked windshield, side glass
      this.group.add(box(1.66, 0.5, 1.7, steel, [0, 1.02, 0.15]));
      const ws = box(1.5, 0.5, 0.12, glass, [0, 1.13, -0.66]); ws.rotation.x = 0.5; this.group.add(ws);
      for (const sx of [-1, 1]) this.group.add(box(0.09, 0.4, 1.25, glass, [sx * 0.8, 1.06, 0.2]));
      // the ICONIC louvered rear window (stacked slats over the engine deck)
      for (let i = 0; i < 6; i++) this.group.add(box(1.36, 0.04, 0.12, trim, [0, 1.28 - i * 0.085, 0.6 + i * 0.11]));
      // gull-wing doors, hinged at the roof edge and swung UP + OUT
      for (const sx of [-1, 1]) {
        const door = new THREE.Group();
        door.add(box(0.06, 1.05, 1.35, steel, [0, -0.5, 0]));
        door.add(box(0.05, 0.66, 0.98, glass, [sx * 0.02, -0.42, -0.02]));
        door.position.set(sx * 0.72, 1.42, 0.12);
        door.rotation.z = -sx * 1.15;
        this.group.add(door);
      }
      this.group.add(box(0.13, 0.09, 1.8, flux, [0, 1.33, 0.12])); // roof flux spine
      // rear deck + bumper + glowing OUTATIME grille
      this.group.add(box(1.98, 0.4, 0.5, steel, [0, 0.72, 1.95]));
      this.group.add(box(2.06, 0.16, 0.32, trim, [0, 0.5, 2.06]));
      this.group.add(box(1.7, 0.26, 0.14, hot, [0, 0.74, 2.14]));
      // reactor drum (Mr Fusion) on the rear deck + twin louvered vent stacks
      this.group.add(cyl(0.33, 0.33, 0.5, trim, [0, 1.15, 1.52]));
      this.group.add(cyl(0.36, 0.26, 0.14, flux, [0, 1.42, 1.52]));
      for (const sx of [-0.64, 0.64]) {
        this.group.add(box(0.34, 0.6, 0.5, trim, [sx, 1.04, 1.62]));
        for (let i = 0; i < 4; i++) this.group.add(box(0.35, 0.03, 0.52, flux, [sx, 0.85 + i * 0.13, 1.62]));
      }
      // flux cabling looping down the flanks + a side reactor pod each side
      for (const sx of [-1, 1]) {
        this.group.add(cyl(0.05, 0.05, 3.0, flux, [sx * 1.0, 0.98, 0.2], "z"));
        this.group.add(box(0.18, 0.18, 0.5, hot, [sx * 0.72, 0.95, 1.9]));
      }
      // low-profile treaded wheels on glowing flux rims (8 meshes -> 2)
      wheels(
        [-0.98, 0.98].flatMap((sx) => [-1.25, 1.35].map((sz) => ({ x: sx, y: 0.36, z: sz, r: 0.36, w: 0.3, spokes: 5 }))),
        new THREE.MeshStandardMaterial(vehicleMaps("tire", { color: "#26282e", roughness: 0.95, metalness: 0.05 })),
        flux,
      );
      {
        // POOLED: vehicles spawn/despawn mid-match — a raw light here changed
        // the scene light count per (de)spawn = full shader recompile hitch.
        // The pooled light rides inside the group and is released on dispose.
        const l = acquireFxLight(scene);
        if (l) {
          l.color.set("#34f7ff"); l.intensity = 2.0; l.distance = 8;
          l.position.set(0, 0.9, 1.5);
          this.group.add(l);
          this.pooledLight = l;
          this.pooledLightIntensity = l.intensity;
        }
      }
    } else if (kind === "truck") {
      // COLOSSUS: tall cab + long cargo box + big wheels — unmistakably a truck.
      // The cab is now a ROUNDED volume with a sloped roof fairing, the cargo
      // box wears riveted plate, and the wheels sit under real fenders.
      const boxPlate = plate(cfg.accent, 0.7, 0.3);
      this.group.add(hull(2.6, 2.0, 2.2, body, [0, 1.9, -2.3], 0.5, 1.2)); // cab
      this.group.add(box(2.36, 0.5, 1.5, body, [0, 3.02, -2.0], 1.2));     // roof air fairing
      this.group.add(box(2.44, 0.16, 1.7, accent, [0, 3.3, -1.85], 1.2));  // fairing cap
      this.group.add(box(2.4, 0.9, 0.4, dark, [0, 1.35, -3.45], 0.35));    // grille (fine slats via the metal grain)
      const wsMat = glassMat("#9fd8ff", 0.86);
      this.group.add(box(2.2, 0.8, 0.2, wsMat, [0, 2.45, -3.35], 1.1));    // windshield
      for (const sx of [-1, 1]) this.group.add(box(0.14, 0.7, 1.2, wsMat, [sx * 1.32, 2.3, -2.4], 1.1)); // door glass
      this.group.add(box(2.9, 0.5, 0.6, dark, [0, 0.7, -3.2], 0.5));       // bull bar
      this.group.add(box(2.8, 2.4, 4.6, boxPlate, [0, 1.9, 0.9], 1.1));    // cargo box
      this.group.add(box(2.86, 0.14, 4.66, dark, [0, 3.16, 0.9], 0.6));    // cargo box cap rail
      this.group.add(box(2.9, 0.5, 7.4, dark, [0, 0.55, 0], 0.7));         // chassis
      for (const sx of [-1, 1]) { // mirror arms + fuel/air tanks under the deck
        this.group.add(box(0.1, 0.1, 0.55, dark, [sx * 1.42, 2.7, -3.0], 0.3));
        this.group.add(box(0.08, 0.5, 0.34, dark, [sx * 1.5, 2.6, -3.28], 0.3));
        const tank = new THREE.Mesh(new THREE.CylinderGeometry(0.34, 0.34, 1.5, 14), plate("#9aa3ae", 0.42, 0.8));
        tank.rotation.x = Math.PI / 2; tank.position.set(sx * 1.5, 0.95, -0.6); tank.castShadow = true;
        worldProjectUV(tank.geometry, 0.6); this.group.add(tank);
      }
      const WZ = [-2.7, -0.4, 1.6, 3.0];
      for (const wz of WZ) for (const sx of [-1, 1]) { // fenders over each axle
        this.group.add(box(0.62, 0.16, 1.5, dark, [sx * 1.45, 1.36, wz], 0.6));
      }
      wheels(
        WZ.flatMap((wz) => [-1.45, 1.45].map((wx) => ({ x: wx, y: 0.62, z: wz, r: 0.62, w: 0.5, spokes: 6 }))),
        new THREE.MeshStandardMaterial(vehicleMaps("tire", { color: "#26282e", roughness: 0.96, metalness: 0.04 })),
        plate("#8d949f", 0.4, 0.85),
      );
      for (const sx of [-1.15, 1.15]) { // twin chromed exhaust stacks
        const stack = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.12, 1.3, 10), plate("#b6bcc6", 0.32, 0.9));
        stack.position.set(sx, 3.4, -1.4); worldProjectUV(stack.geometry, 0.5); this.group.add(stack);
      }
    } else if (kind === "miniufo") {
      // SCOUT SAUCER: flat disc + belly cone + glass dome + glowing rim
      const disc = new THREE.Mesh(new THREE.CylinderGeometry(1.9, 1.5, 0.5, 28), body);
      worldProjectUV(disc.geometry, 1.1);
      disc.position.y = 1.0; disc.castShadow = true; this.group.add(disc);
      const belly = new THREE.Mesh(new THREE.ConeGeometry(1.5, 0.8, 28), body);
      worldProjectUV(belly.geometry, 1.1);
      belly.rotation.x = Math.PI; belly.position.y = 0.45; this.group.add(belly);
      const dome = new THREE.Mesh(
        new THREE.SphereGeometry(0.75, 20, 12, 0, Math.PI * 2, 0, Math.PI / 2),
        glassMat("#7ff3ff", 0.85),
      );
      (dome.material as THREE.MeshStandardMaterial).emissive.set("#39c6ff");
      (dome.material as THREE.MeshStandardMaterial).emissiveIntensity = 0.5;
      dome.position.y = 1.25; this.group.add(dome);
      const ring = new THREE.Mesh(new THREE.TorusGeometry(1.65, 0.12, 8, 24), new THREE.MeshBasicMaterial({ color: cfg.accent }));
      ring.rotation.x = Math.PI / 2; ring.position.y = 0.95; this.group.add(ring);
      for (let i = 0; i < 6; i++) {
        const a = (i / 6) * Math.PI * 2;
        const pod = new THREE.Mesh(new THREE.SphereGeometry(0.14, 8, 8), new THREE.MeshBasicMaterial({ color: i % 2 ? "#ff2fd0" : "#9dff00" }));
        pod.position.set(Math.cos(a) * 1.35, 0.7, Math.sin(a) * 1.35);
        this.group.add(pod);
      }
      // slow-spinning rim = the rotor slot (reuses the heli rotor animation)
      this.rotor = new THREE.Group();
      this.rotor.add(ring.clone());
      this.rotor.position.y = 0.02;
      this.rotorAxis = "y";
      this.group.add(this.rotor);
    } else if (kind === "miniplane" || kind === "interceptor") {
      // SPARROW / ARROW: ROUND fuselage + tapered wings + tail + nose prop.
      // The fuselage was a 0.7 m square bar; a plane whose body has corners is
      // the clearest "stack of boxes" tell in the air.
      this.group.add(hull(0.78, 0.72, 3.0, body, [0, 1.0, 0], 1, 0.8)); // fuselage barrel
      this.group.add(hull(0.62, 0.56, 0.7, body, [0, 1.0, -1.72], 1, 0.6)); // nose cone
      this.group.add(hull(0.42, 0.38, 0.8, body, [0, 1.12, 1.62], 1, 0.6)); // tail cone
      this.group.add(box(5.6, 0.12, 1.1, accent, [0, 1.15, -0.3], 1.0)); // main wing
      for (const sx of [-1, 1]) { // wing-tip fairings + a strut fillet: a real wing has ends
        this.group.add(hull(0.26, 0.16, 0.9, accent, [sx * 2.76, 1.16, -0.3], 1, 0.5));
        this.group.add(box(0.16, 0.3, 0.9, body, [sx * 0.44, 1.06, -0.3], 0.5));
      }
      this.group.add(box(2.0, 0.1, 0.7, accent, [0, 1.35, 1.5], 0.8)); // tail wing
      this.group.add(box(0.1, 0.9, 0.7, accent, [0, 1.6, 1.5], 0.8)); // tail fin
      this.group.add(hull(0.52, 0.42, 0.62, glassMat("#9fd8ff", 0.8), [0, 1.36, -0.7], 1, 0.7)); // bubble canopy
      this.group.add(box(0.14, 0.5, 0.14, dark, [-0.5, 0.45, -0.4], 0.3), box(0.14, 0.5, 0.14, dark, [0.5, 0.45, -0.4], 0.3)); // landing struts
      wheels([-0.5, 0.5].map((sx) => ({ x: sx, y: 0.18, z: -0.4, r: 0.18, w: 0.11, spokes: 0 })),
        new THREE.MeshStandardMaterial(vehicleMaps("tire", { color: "#232529", roughness: 0.95, metalness: 0.05 })),
        plate("#9aa3ae", 0.4, 0.85));
      this.rotor = new THREE.Group();
      this.rotor.add(box(0.12, 2.0, 0.08, dark, [0, 0, 0])); // prop blade
      this.rotor.position.set(0, 1.0, -1.6);
      this.rotorAxis = "z";
      this.group.add(this.rotor);
    } else if (kind === "submarine") {
      // NAUTILUS: a torpedo hull, conning tower + periscope, dive planes, a
      // stern propeller (reuses the rotor spin) and glowing portholes.
      // riveted pressure hull, not painted plastic
      const pressure = plate(cfg.color, 0.55, 0.62);
      const pressureHull = new THREE.Mesh(new THREE.CapsuleGeometry(1.1, 4.4, 8, 20), pressure);
      worldProjectUV(pressureHull.geometry, 1.0);
      pressureHull.rotation.x = Math.PI / 2; pressureHull.position.y = 1.1; pressureHull.castShadow = true; this.group.add(pressureHull);
      for (const bz of [-1.9, -0.6, 0.7, 2.0]) { // hull frames: a sub is a ribbed can
        const rib = new THREE.Mesh(new THREE.TorusGeometry(1.11, 0.05, 6, 20), plate("#8d949f", 0.5, 0.8));
        rib.position.set(0, 1.1, bz); worldProjectUV(rib.geometry, 0.4); this.group.add(rib);
      }
      this.group.add(hull(0.9, 1.0, 1.4, pressure, [0, 2.2, -0.2], 0.7, 0.9));   // conning tower (sail)
      this.group.add(box(0.12, 0.9, 0.12, dark, [0, 3.0, -0.4], 0.3)); // periscope
      this.group.add(box(0.5, 0.12, 0.12, accent, [0, 3.4, -0.4], 0.3)); // periscope head
      this.group.add(box(3.4, 0.12, 0.7, accent, [0, 1.1, 1.9], 0.9));  // aft dive plane
      this.group.add(box(2.4, 0.12, 0.5, accent, [0, 1.1, -2.4], 0.9)); // bow dive plane
      this.group.add(box(0.12, 1.2, 0.8, accent, [0, 1.6, 2.4], 0.9));  // rudder / tail fin
      for (const sx of [-1.0, 1.0]) for (const pz of [-1.5, -0.4, 0.7]) {
        const port = new THREE.Mesh(new THREE.CircleGeometry(0.16, 12), new THREE.MeshBasicMaterial({ color: "#ffe08a" }));
        port.position.set(sx, 1.2, pz); port.rotation.y = sx > 0 ? -Math.PI / 2 : Math.PI / 2; this.group.add(port);
      }
      this.rotor = new THREE.Group();
      for (let i = 0; i < 4; i++) { const bl = box(0.1, 0.9, 0.16, dark, [0, 0, 0]); bl.rotation.z = i * Math.PI / 2; this.rotor.add(bl); }
      this.rotor.position.set(0, 1.1, 2.7);
      this.rotorAxis = "z";
      this.group.add(this.rotor);
    } else if (kind === "cannon") {
      // SHIP CANNON: a stout iron barrel on a wooden carriage with cart wheels.
      // The barrel points −Z (the ride's forward / muzzle), so it lines up with
      // the fire direction and the player's seated look.
      const oak = plate(cfg.color, 0.85, 0.05);
      this.group.add(box(1.4, 0.5, 1.7, oak, [0, 0.3, 0.2], 0.55));  // carriage bed (planked + bolted)
      for (const sx of [-0.62, 0.62]) this.group.add(box(0.14, 0.34, 1.5, oak, [sx, 0.6, 0.25], 0.5)); // cheek pieces
      // The barrel assembly rides its own PIVOT so it can track where the
      // gunner is actually aiming (yaw + elevation), instead of being welded
      // to the carriage. Pivot sits at the trunnions; parts are offset from it.
      const turret = new THREE.Group();
      turret.position.set(0, 0.75, 0.2);
      // yaw THEN pitch: with the default XYZ order, pitch is applied about the
      // parent's X after yaw, so a barrel aimed 90 deg off the hull rolls
      // instead of elevating
      turret.rotation.order = "YXZ";
      const iron = new THREE.MeshStandardMaterial(vehicleMaps("metal", { color: "#2a2c33", roughness: 0.55, metalness: 0.85 }, 1.3));
      const barrel = new THREE.Mesh(new THREE.CylinderGeometry(0.34, 0.42, 2.2, 20), iron);
      worldProjectUV(barrel.geometry, 0.5);
      barrel.rotation.x = Math.PI / 2; barrel.position.set(0, 0, -0.7); barrel.castShadow = true; turret.add(barrel);
      for (const rz of [-1.5, -0.35, 0.25] as const) { // reinforce rings: cast iron is stepped, not a smooth tube
        const ring = new THREE.Mesh(new THREE.TorusGeometry(rz < -1 ? 0.36 : rz < 0 ? 0.4 : 0.44, 0.05, 6, 18), iron);
        ring.position.set(0, 0, rz); worldProjectUV(ring.geometry, 0.3); turret.add(ring);
      }
      const cascabel = new THREE.Mesh(new THREE.SphereGeometry(0.3, 14, 10), iron);
      cascabel.position.set(0, 0, 0.5); worldProjectUV(cascabel.geometry, 0.4); turret.add(cascabel); // breech (rear ball)
      const muzzle = new THREE.Mesh(new THREE.TorusGeometry(0.36, 0.08, 8, 16), accent);
      muzzle.rotation.y = Math.PI / 2; muzzle.position.set(0, 0, -1.8); turret.add(muzzle); // gold muzzle ring
      this.group.add(turret);
      this.turret = turret;
      this.turretPitches = true; // cannons elevate; the tank turret only yaws
      // spoked cart wheels (iron-tyred oak), 2 meshes for the pair
      wheels([-0.75, 0.75].map((sx) => ({ x: sx, y: 0.35, z: 0.5, r: 0.4, w: 0.18, spokes: 8 })),
        new THREE.MeshStandardMaterial(vehicleMaps("metal", { color: "#2f3138", roughness: 0.72, metalness: 0.7 })), oak);
    } else if (kind === "bombkart") {
      // small bright GO-KART (arena bomb battle) — low chassis, bucket seat,
      // roll hoop, 4 fat wheels; no turret (bombs don't aim)
      // Reshaped 2026-08-01 (`graphics`): the pan is a rounded moulded tub, the
      // nose is a pointed cone, the seat has a back and side bolsters, the roll
      // hoop is a round tube, and the rear tyres are FAT slicks — a kart's
      // silhouette is front-narrow / rear-wide, which stacked boxes never gave.
      this.group.add(hull(1.3, 0.35, 2.1, body, [0, 0.42, 0], 0.75, 0.7)); // chassis tub
      this.group.add(hull(0.9, 0.3, 0.9, body, [0, 0.5, -1.15], 1, 0.6));  // pointed nose cone
      this.group.add(hull(1.5, 0.2, 0.42, accent, [0, 0.55, -1.02], 0.9, 0.6)); // front bumper
      this.group.add(hull(1.5, 0.22, 0.46, accent, [0, 0.58, 1.02], 0.9, 0.6)); // rear bumper
      this.group.add(hull(0.66, 0.46, 0.66, dark, [0, 0.75, 0.4], 0.85, 0.5)); // seat pan
      this.group.add(hull(0.62, 0.62, 0.22, dark, [0, 0.95, 0.72], 0.85, 0.5)); // seat back
      for (const sx of [-1, 1]) this.group.add(hull(0.16, 0.34, 0.6, dark, [sx * 0.4, 0.86, 0.4], 1, 0.4)); // bolsters
      for (const sx of [-0.35, 0.35]) { // round roll-hoop tubes
        const t = new THREE.Mesh(new THREE.CylinderGeometry(0.065, 0.065, 0.6, 10), dark);
        t.position.set(sx, 1.05, 0.35); worldProjectUV(t.geometry, 0.3); t.castShadow = true; this.group.add(t);
      }
      const hoop = new THREE.Mesh(new THREE.TorusGeometry(0.41, 0.06, 8, 16, Math.PI), accent);
      hoop.position.set(0, 1.32, 0.35); worldProjectUV(hoop.geometry, 0.3); this.group.add(hoop); // roll hoop top
      this.group.add(box(0.5, 0.1, 0.5, dark, [0, 0.85, -0.85], 0.4)); // steering column base
      { // steering wheel: a ring on a raked column — the kart had no wheel at all
        const col = new THREE.Mesh(new THREE.CylinderGeometry(0.045, 0.045, 0.42, 8), dark);
        col.position.set(0, 1.0, -0.72); col.rotation.x = 0.5; worldProjectUV(col.geometry, 0.25); this.group.add(col);
        const rim = new THREE.Mesh(new THREE.TorusGeometry(0.18, 0.035, 7, 16), accent);
        rim.position.set(0, 1.16, -0.62); rim.rotation.x = 1.05; worldProjectUV(rim.geometry, 0.25); this.group.add(rim);
      }
      wheels([
        { x: -0.72, y: 0.3, z: -0.78, r: 0.3, w: 0.26, spokes: 5 },
        { x: 0.72, y: 0.3, z: -0.78, r: 0.3, w: 0.26, spokes: 5 },
        { x: -0.78, y: 0.36, z: 0.78, r: 0.36, w: 0.42, spokes: 5 }, // fat rear slicks
        { x: 0.78, y: 0.36, z: 0.78, r: 0.36, w: 0.42, spokes: 5 },
      ], new THREE.MeshStandardMaterial(vehicleMaps("tire", { color: "#22242a", roughness: 0.95, metalness: 0.05 })),
        new THREE.MeshStandardMaterial(vehicleMaps("metal", { color: "#c9ced6", roughness: 0.3, metalness: 0.9 })));
      // NEON UNDERGLOW — the kart lights itself up (reads as neon, pops at night):
      // emissive strips along the chassis + underbody strips + a coloured light.
      // Refs are kept so setDriver can repaint them in the DRIVER'S arena neon.
      const neonMat = new THREE.MeshStandardMaterial({ color: "#00e5ff", emissive: "#00e5ff", emissiveIntensity: 2.2, roughness: 0.3 });
      const glowMat = new THREE.MeshBasicMaterial({ color: "#00e5ff", transparent: true, opacity: 0.55 });
      this.underglowMats = [neonMat, glowMat];
      for (const sx of [-0.68, 0.68]) {
        const strip = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.1, 1.9), neonMat);
        strip.position.set(sx, 0.5, 0); this.group.add(strip);
        const under = new THREE.Mesh(new THREE.BoxGeometry(0.14, 0.04, 2.0), glowMat);
        under.position.set(sx, 0.18, 0); this.group.add(under);
      }
      { // emissive strips still glow without the light — POOLED (see delorean)
        const underLight = acquireFxLight(scene);
        if (underLight) {
          underLight.color.set("#00e5ff"); underLight.intensity = 2.2; underLight.distance = 6;
          underLight.position.set(0, 0.25, 0);
          this.group.add(underLight);
          this.underglowLight = underLight;
          this.pooledLight = underLight;
          this.pooledLightIntensity = underLight.intensity;
        }
      }
      // ARENA (MKDD): the kart you SEE is exactly one MKDD kart length
      // (ARENA_KL) long — every arena distance (blast radius, throw range, the
      // chase-cam boom) is measured in kart lengths, so the model must match.
      // (Was a flat 0.78 = ~2.2 m, 1.4 KL.) One-off at construction.
      {
        const len = new THREE.Box3().setFromObject(this.group).getSize(new THREE.Vector3()).z;
        this.group.scale.setScalar(len > 0 ? ARENA_KL / len : 0.56);
      }
    } else if (kind === "speeder") {
      // NEON SPEEDER — a low sleek hoverbike: long tapered body, a seat, a
      // forward cowl, and two glowing hover-pads instead of wheels
      this.group.add(hull(0.5, 0.28, 2.0, body, [0, 0.55, 0], 1, 0.6)); // tapered spine
      this.group.add(hull(0.7, 0.24, 0.9, body, [0, 0.62, -0.55], 1, 0.6)); // front cowl
      this.group.add(hull(0.34, 0.2, 0.5, body, [0, 0.6, -1.12], 1, 0.5)); // nose fairing
      this.group.add(hull(0.42, 0.4, 0.6, dark, [0, 0.85, 0.35], 0.9, 0.4)); // seat
      { // round handlebars on a raked stem, not a plank
        const bar = new THREE.Mesh(new THREE.CylinderGeometry(0.045, 0.045, 0.62, 8), accent);
        bar.rotation.z = Math.PI / 2; bar.position.set(0, 0.95, -0.95);
        worldProjectUV(bar.geometry, 0.25); this.group.add(bar);
        for (const sx of [-0.31, 0.31]) this.group.add(hull(0.09, 0.09, 0.2, dark, [sx, 0.95, -0.95], 1, 0.2));
      }
      const padMat = new THREE.MeshBasicMaterial({ color: cfg.accent, transparent: true, opacity: 0.7 });
      for (const pz of [-0.8, 0.8]) {
        const pad = new THREE.Mesh(new THREE.CylinderGeometry(0.34, 0.4, 0.14, 16), padMat);
        pad.position.set(0, 0.2, pz); this.group.add(pad);
        const shroud = new THREE.Mesh(new THREE.TorusGeometry(0.38, 0.06, 7, 18), dark); // turbine shroud
        shroud.rotation.x = Math.PI / 2; shroud.position.set(0, 0.28, pz);
        worldProjectUV(shroud.geometry, 0.3); this.group.add(shroud);
      }
      this.group.add(box(0.16, 0.5, 0.16, accent, [0, 0.4, 0.9], 0.4)); // tail fin
    } else if (kind === "horse" || kind === "camel") {
      const isCamel = kind === "camel";
      // HIDE TEXTURE: map multiplies × colour, so the mount keeps its per-rider
      // paint but now reads as combed fur (horse) / mottled hide (camel). These
      // materials are per-instance (built in this ctor), so karts stay smooth.
      const coat = isCamel ? hideTexture() : furTexture();
      this.defaultBodyMap = coat; // restored by setDriver when a rider leaves
      // a mount is NOT painted metal: drop the paint-flake normal/roughness the
      // shared ctor wired in, or a horse catches light like a car door
      body.map = coat; body.normalMap = null; body.roughnessMap = null;
      body.roughness = 0.92; body.metalness = 0.0; body.needsUpdate = true;
      accent.map = coat; accent.normalMap = null; accent.roughnessMap = null;
      accent.roughness = 0.9; accent.metalness = 0.0; accent.needsUpdate = true;
      const mane = new THREE.MeshStandardMaterial({ color: isCamel ? "#3b2415" : "#1a0d05", roughness: 0.9, metalness: 0.01, map: coat });
      const leather = new THREE.MeshStandardMaterial({ color: "#332012", roughness: 0.78, metalness: 0.04 });
      const cloth = new THREE.MeshStandardMaterial({ color: isCamel ? "#2f5c70" : "#7a2c1e", roughness: 0.82, metalness: 0.02 });
      // the mount wears the VEHICLE paint (fur = body, shade = accent): the
      // rider's color/skin wraps the animal like any other ride — requested
      const fur = body;
      const shade = accent;
      const blaze = new THREE.MeshStandardMaterial({ color: "#e8e2d6", roughness: 0.9, metalness: 0.01, map: coat });
      // parts built while headCollect is true are captured so the whole head +
      // neck can bob together with the walk cycle (their base pos is stashed)
      let headCollect = false;
      const capture = (m: THREE.Object3D): void => {
        if (!headCollect) return;
        // The head/neck nods as ONE rigid unit (uniform translation, see the
        // gait loop). Re-parent the whole cluster under headGroup (positions are
        // preserved — headGroup sits at the group origin) and merge it below, so
        // it costs a few draw calls that nod by moving the group, not N meshes
        // each carrying their own transform.
        if (!this.headGroup) { this.headGroup = new THREE.Group(); this.group.add(this.headGroup); }
        this.headGroup.add(m);
      };
      const ell = (sx: number, sy: number, sz: number, mat: THREE.Material, x: number, y: number, z: number, rx = 0): THREE.Mesh => {
        const m = new THREE.Mesh(new THREE.SphereGeometry(0.5, 18, 12), mat);
        m.scale.set(sx, sy, sz);
        m.position.set(x, y, z);
        m.rotation.x = rx;
        m.castShadow = true;
        m.receiveShadow = true;
        this.group.add(m);
        capture(m);
        return m;
      };
      const cyl = (rTop: number, rBot: number, len: number, mat: THREE.Material, x: number, y: number, z: number, rx = 0): THREE.Mesh => {
        const m = new THREE.Mesh(new THREE.CylinderGeometry(rTop, rBot, len, 12), mat);
        m.position.set(x, y, z);
        m.rotation.x = rx;
        m.castShadow = true;
        this.group.add(m);
        capture(m);
        return m;
      };
      // Ride animals v4 — real anatomy: withers/haunch massing, a crested
      // (horse) or S-curved (camel) neck, long tapered head with jaw + brow,
      // two-segment legs with knees/hocks that FLEX during the stride, a
      // flowing tail, and a full saddle rig (blanket, girth, stirrups, reins).
      const dark = new THREE.MeshBasicMaterial({ color: "#0a0705" }); // eyes/nostrils
      const rein = (from: THREE.Vector3, ctrl: THREE.Vector3, to: THREE.Vector3): void => {
        const curve = new THREE.QuadraticBezierCurve3(from, ctrl, to);
        const m = new THREE.Mesh(new THREE.TubeGeometry(curve, 14, 0.018, 6), leather);
        m.castShadow = true;
        this.group.add(m);
      };
      if (!isCamel) {
        // ================= HORSE =================
        // torso: barrel + shoulder mass (withers slightly higher) + round rump
        ell(1.02, 0.92, 1.6, fur, 0, 1.42, 0.1);
        ell(0.94, 0.98, 1.0, fur, 0, 1.5, -0.62);
        ell(0.96, 0.9, 1.1, fur, 0, 1.44, 0.75);
        ell(0.74, 0.55, 1.35, shade, 0, 1.14, 0.1); // belly shadow
        ell(0.52, 0.55, 0.45, fur, 0, 1.28, -1.02); // breast
        for (const sx of [-1, 1]) {
          ell(0.28, 0.52, 0.58, shade, sx * 0.3, 1.38, -0.6);  // shoulder muscle
          ell(0.3, 0.6, 0.68, shade, sx * 0.3, 1.34, 0.8);     // haunch muscle
        }
        // crested neck (an upright ellipsoid, not a pipe) + mane ridge on top
        headCollect = true;
        ell(0.46, 1.3, 0.65, fur, 0, 1.92, -1.14, -0.72);
        ell(0.16, 0.95, 0.3, mane, 0, 2.06, -1.02, -0.72);
        // head: skull + jaw + long tapering muzzle, tipped down-forward
        ell(0.36, 0.42, 0.55, fur, 0, 2.34, -1.62, -0.3);
        ell(0.28, 0.3, 0.36, fur, 0, 2.16, -1.58); // jaw
        ell(0.24, 0.26, 0.6, fur, 0, 2.2, -2.0, -0.45); // muzzle
        ell(0.2, 0.18, 0.22, shade, 0, 2.12, -2.24); // nose
        ell(0.09, 0.24, 0.55, blaze, 0, 2.34, -1.8, -0.45); // face blaze
        ell(0.12, 0.22, 0.18, mane, 0, 2.58, -1.52); // forelock between the ears
        headCollect = false;
        for (const sx of [-1, 1]) {
          const ear = new THREE.Mesh(new THREE.ConeGeometry(0.07, 0.24, 8), fur);
          ear.position.set(sx * 0.12, 2.64, -1.44);
          ear.rotation.z = -sx * 0.3;
          ear.castShadow = true;
          this.group.add(ear);
          const eye = new THREE.Mesh(new THREE.SphereGeometry(0.045, 8, 6), dark);
          eye.position.set(sx * 0.18, 2.4, -1.68);
          this.group.add(eye);
          const nostril = new THREE.Mesh(new THREE.SphereGeometry(0.03, 6, 5), dark);
          nostril.position.set(sx * 0.09, 2.13, -2.3);
          this.group.add(nostril);
        }
        // bridle: noseband + browband + cheek straps, reins draped to the saddle
        {
          const strap = new THREE.Mesh(new THREE.TorusGeometry(0.15, 0.016, 6, 16), leather);
          strap.position.set(0, 2.18, -2.02);
          strap.rotation.x = Math.PI / 2 - 0.45;
          this.group.add(strap);
          this.group.add(box(0.28, 0.03, 0.03, leather, [0, 2.5, -1.52]));
          for (const sx of [-1, 1]) {
            // cheek strap leans BACK toward the ear (forward it reads as a rod
            // stabbing past the forehead)
            const cheek = box(0.025, 0.28, 0.025, leather, [sx * 0.19, 2.3, -1.88]);
            cheek.rotation.x = 0.5;
            this.group.add(cheek);
            rein(
              new THREE.Vector3(sx * 0.16, 2.2, -2.0),
              new THREE.Vector3(sx * 0.34, 1.8, -1.0),
              new THREE.Vector3(sx * 0.05, 2.0, -0.15),
            );
          }
        }
        // flowing tail: ONE continuous curved tube rooted inside the rump,
        // sweeping out then down, with a thick tuft wrapping its lower half
        // (stacked ellipsoids only touched at their tips — read as floating)
        {
          const tailCurve = new THREE.QuadraticBezierCurve3(
            new THREE.Vector3(0, 1.7, 1.05),
            new THREE.Vector3(0, 1.6, 1.55),
            new THREE.Vector3(0, 1.0, 1.5),
          );
          const tail = new THREE.Mesh(new THREE.TubeGeometry(tailCurve, 12, 0.055, 7), mane);
          tail.castShadow = true;
          tail.userData.animated = true; // swings per frame — exclude from the body merge
          this.group.add(tail);
          this.horseTail = tail;
          ell(0.16, 0.44, 0.18, mane, 0, 1.12, 1.5);
        }
        // saddle rig: low-profile blanket hugging the spine, slim seat with a
        // modest cantle + horn (tall ones read as ears from the side)
        ell(0.95, 0.16, 0.85, cloth, 0, 1.86, 0.02);
        ell(0.44, 0.18, 0.62, leather, 0, 1.95, 0.08);
        ell(0.26, 0.14, 0.14, leather, 0, 2.0, 0.32); // cantle
        ell(0.09, 0.16, 0.09, leather, 0, 2.03, -0.2); // pommel horn
        {
          const girth = new THREE.Mesh(new THREE.TorusGeometry(0.56, 0.03, 6, 20), leather);
          girth.position.set(0, 1.42, 0.05);
          girth.scale.set(1, 1.02, 1); // rings the barrel (torus lies in XY ⟂ z)
          this.group.add(girth);
        }
        for (const sx of [-1, 1]) {
          this.group.add(box(0.05, 0.32, 0.09, leather, [sx * 0.53, 1.7, 0.05]));
          const iron = new THREE.Mesh(new THREE.TorusGeometry(0.07, 0.015, 6, 14), leather);
          iron.position.set(sx * 0.53, 1.5, 0.05);
          iron.rotation.y = Math.PI / 2;
          this.group.add(iron);
        }
      } else {
        // ================= CAMEL (dromedary) =================
        // tall narrow torso with a sloping rump and ONE big hump set back
        ell(0.9, 0.84, 1.75, fur, 0, 1.7, 0.1);
        ell(0.8, 0.88, 0.95, fur, 0, 1.72, -0.6);
        ell(0.82, 0.76, 1.0, fur, 0, 1.64, 0.82);
        ell(0.62, 0.52, 1.35, shade, 0, 1.4, 0.1); // belly
        ell(0.78, 0.9, 1.05, fur, 0, 2.26, 0.28); // ONE tall hump
        // S-curve neck: dips out of the chest, rises steeply, crooks forward
        headCollect = true;
        ell(0.34, 0.5, 0.75, fur, 0, 1.66, -1.0, -1.15);
        ell(0.3, 0.95, 0.36, fur, 0, 2.12, -1.42, -0.3);
        ell(0.26, 0.55, 0.32, fur, 0, 2.6, -1.58, -0.08);
        // small head held HIGH and level, drooping camel muzzle
        ell(0.28, 0.32, 0.46, fur, 0, 2.9, -1.68, 0.1);
        ell(0.2, 0.22, 0.46, fur, 0, 2.82, -2.02, 0.5);
        ell(0.16, 0.15, 0.2, shade, 0, 2.72, -2.2); // split-lip nose
        headCollect = false;
        for (const sx of [-1, 1]) {
          const ear = new THREE.Mesh(new THREE.ConeGeometry(0.05, 0.14, 7), fur);
          ear.position.set(sx * 0.11, 3.06, -1.56);
          ear.rotation.z = -sx * 0.3;
          this.group.add(ear);
          const eye = new THREE.Mesh(new THREE.SphereGeometry(0.05, 8, 6), dark);
          eye.position.set(sx * 0.15, 2.94, -1.74);
          this.group.add(eye);
        }
        {
          const strap = new THREE.Mesh(new THREE.TorusGeometry(0.13, 0.015, 6, 14), leather);
          strap.position.set(0, 2.8, -2.05);
          strap.rotation.x = Math.PI / 2 - 0.5;
          this.group.add(strap);
          // single lead rein back to the saddle frame
          rein(
            new THREE.Vector3(0.14, 2.8, -2.05),
            new THREE.Vector3(0.36, 2.2, -1.0),
            new THREE.Vector3(0.08, 2.7, 0.02),
          );
        }
        // thin tail with a tuft: TOP end roots into the rump, hangs down-back
        this.horseTail = cyl(0.035, 0.025, 0.5, fur, 0, 1.5, 1.22, -0.35);
        this.horseTail.userData.animated = true; // swings per frame — exclude from the body merge
        ell(0.09, 0.17, 0.09, mane, 0, 1.2, 1.34);
        // saddle ON the hump: draped blanket with hanging sides + tassels,
        // leather seat between two frame posts (a real camel saddle shape)
        // thin blanket DRAPED over the hump crest (not a stacked ball), with
        // hanging side drapes + tassels, and a low seat between two horns
        ell(0.95, 0.3, 1.05, cloth, 0, 2.6, 0.28);
        for (const sx of [-1, 1]) {
          ell(0.12, 0.48, 0.75, cloth, sx * 0.4, 2.3, 0.28);
          for (const tz of [0.05, 0.5]) {
            this.group.add(box(0.03, 0.12, 0.03, leather, [sx * 0.4, 2.06, tz]));
            const tas = new THREE.Mesh(new THREE.SphereGeometry(0.035, 6, 5), mane);
            tas.position.set(sx * 0.4, 1.98, tz);
            this.group.add(tas);
          }
        }
        ell(0.46, 0.2, 0.62, leather, 0, 2.74, 0.28);
        cyl(0.045, 0.07, 0.18, leather, 0, 2.86, 0.0);  // saddle-frame horns
        cyl(0.045, 0.07, 0.18, leather, 0, 2.86, 0.56);
      }
      // ---- legs: hip/shoulder pivot + a SHIN pivot at the knee/hock that the
      // update loop flexes opposite the stride (real gait, not oars) ----
      this.legRootY = isCamel ? 1.62 : 1.38;
      const legXF = isCamel ? 0.3 : 0.36, legXR = isCamel ? 0.3 : 0.38;
      const legZF = isCamel ? -0.68 : -0.66, legZR = isCamel ? 0.8 : 0.8;
      let legIdx = 0;
      for (const lx of [-1, 1]) for (const lz of [legZF, legZR]) {
        const rear = lz > 0;
        const leg = new THREE.Group();
        leg.position.set(lx * (rear ? legXR : legXF), this.legRootY, lz);
        const upper = new THREE.Mesh(
          new THREE.CylinderGeometry(rear ? 0.13 : 0.11, 0.09, isCamel ? 0.82 : 0.62, 10), fur);
        upper.position.set(0, isCamel ? -0.37 : -0.28, 0);
        upper.castShadow = true;
        const knee = new THREE.Mesh(new THREE.SphereGeometry(isCamel ? 0.095 : 0.08, 8, 6), isCamel ? shade : fur);
        knee.position.set(0, isCamel ? -0.78 : -0.6, 0);
        const shin = new THREE.Group();
        shin.position.set(0, isCamel ? -0.78 : -0.6, 0);
        const cannon = new THREE.Mesh(
          new THREE.CylinderGeometry(0.06, 0.055, isCamel ? 0.68 : 0.55, 9), shade);
        cannon.position.set(0, isCamel ? -0.36 : -0.3, 0);
        cannon.castShadow = true;
        shin.add(cannon);
        if (isCamel) {
          // wide padded desert foot with a two-toe hint
          const pad = new THREE.Mesh(new THREE.CylinderGeometry(0.13, 0.145, 0.1, 10), shade);
          pad.position.set(0, -0.78, 0.02);
          shin.add(pad);
          for (const tx of [-0.055, 0.055]) {
            const toe = new THREE.Mesh(new THREE.BoxGeometry(0.09, 0.06, 0.1), shade);
            toe.position.set(tx, -0.8, -0.08);
            shin.add(toe);
          }
        } else {
          const fetlock = new THREE.Mesh(new THREE.SphereGeometry(0.065, 7, 6), shade);
          fetlock.position.set(0, -0.56, 0);
          shin.add(fetlock);
          if (legIdx === 0 || legIdx === 3) { // white socks on a diagonal pair
            const sock = new THREE.Mesh(new THREE.CylinderGeometry(0.07, 0.075, 0.18, 8), blaze);
            sock.position.set(0, -0.6, 0);
            shin.add(sock);
          }
          const hoof = new THREE.Mesh(new THREE.BoxGeometry(0.18, 0.13, 0.24), mane);
          hoof.position.set(0, -0.7, -0.02);
          shin.add(hoof);
        }
        leg.add(upper, knee, shin);
        // hwaccel: the leg (upper+knee) and the shin (cannon/fetlock/pad/toes)
        // are each a rigid cluster that moves as ONE with its pivot Group, so
        // collapse them per material like the body. Normalise every leg mesh to
        // castShadow=true first (the tiny joint blobs defaulted to false, which
        // would split the shadow bucket and block the merge — an extra 6 mm cast
        // shadow at a knee is invisible against the leg it sits inside). recv is
        // left untouched, so no surface re-shades. The leg/shin Groups stay the
        // animated pivots; only their child mesh COUNT drops.
        leg.traverse((o) => { const m = o as THREE.Mesh; if (m.isMesh) m.castShadow = true; });
        mergeVehicleBody(shin);
        mergeVehicleBody(leg);
        this.horseLegs.push(leg);
        this.horseShins.push(shin);
        this.group.add(leg);
        legIdx++;
      }
    } else if (cfg.kind === "kart") {
      // TANK look: wide hull, side tracks, a turret + forward cannon.
      // 2026-08-01 (`graphics`): armour is riveted PLATE with a sloped glacis,
      // the tracks wear a real link texture with a raised guide horn, the road
      // wheels are treated wheels merged into 2 meshes (was 8 flat cylinders),
      // and the turret shell is a rounded casting instead of a slab.
      const armour = plate(cfg.color, 0.68, 0.42);
      // The driver's SKIN/colours paint the hull via setDriver → this.bodyMat.
      // The tank built its whole armour from this private plate material, so it
      // was the ONE vehicle that ignored the driver's identity (owner-reported
      // 2026-08-08). Point bodyMat at the armour so the tank tints and wears a
      // custom skin exactly like every other machine.
      this.bodyMat = armour;
      this.defaultBodyMap = armour.map;
      const trackMat = new THREE.MeshStandardMaterial(vehicleMaps("track", { color: "#3a3d45", roughness: 0.78, metalness: 0.5 }, 1.2));
      // 2026-11-05: the hand-built realistic tank (vehicleHulls.ts) — track
      // belts round real running gear, faceted cast turret. Same paint/skin.
      const parts = buildTankHull(this.group, { body: armour, accent, dark, track: trackMat }, (spots) => wheels(
        spots,
        new THREE.MeshStandardMaterial(vehicleMaps("tire", { color: "#26282e", roughness: 0.92, metalness: 0.05 })),
        new THREE.MeshStandardMaterial(vehicleMaps("metal", { color: "#4c515b", roughness: 0.55, metalness: 0.7 })),
      ));
      this.turret = parts.turret!;
      mergeVehicleBody(this.turret);
      this.group.scale.setScalar(0.82);
    } else {
      // HIND GUNSHIP — 2026-11-05: the hand-built realistic gunship
      // (vehicleHulls.ts): lofted fuselage + boom, tandem canopies, stub wings
      // with rocket pods, wheeled gear, 5-blade rotor, spinning tail rotor.
      // Same paint (driver colour / skin via bodyMat) and accent (glow).
      const parts = buildGunshipHull(this.group, { body, accent, dark, glass: glassMat("#8fd6ff", 0.72) }, (spots) => wheels(
        spots,
        new THREE.MeshStandardMaterial(vehicleMaps("tire", { color: "#26282e", roughness: 0.92, metalness: 0.05 })),
        dark,
      ));
      mergeVehicleBody(parts.rotor!);
      mergeVehicleBody(parts.tailRotor!);
      this.rotor = parts.rotor!;
      this.rotorAxis = "y";
      this.tailRotor = parts.tailRotor!;
    }
    // hwaccel: collapse the static hull panels into one draw call per material.
    // Mounts merge too now — the rigid body/saddle/bridle meshes collapse here;
    // the head/neck cluster (headGroup), the 4 leg + shin sub-Groups and the
    // tail (`userData.animated`) are their own pivots and were merged inline as
    // they were built, so each animated sub-assembly is itself a few draw calls
    // instead of the ~20 loose meshes it used to be.
    mergeVehicleBody(this.group);
    if (this.headGroup) mergeVehicleBody(this.headGroup);
    scene.add(this.group);
  }

  /** Swivel the kart turret to a WORLD yaw (free-look camera). Relative to
   *  the body so it reads as an independently-aimed turret. Smoothed. */
  /** Elevation support — set for weapons whose barrel should also tilt. */
  private turretPitches = false;
  setTurretYaw(worldYaw: number, bodyYaw: number, dt: number, pitch = 0): void {
    if (!this.turret) return;
    let rel = worldYaw - bodyYaw;
    rel = Math.atan2(Math.sin(rel), Math.cos(rel));
    const cur = this.turret.rotation.y;
    let d = rel - cur;
    d = Math.atan2(Math.sin(d), Math.cos(d));
    // 1-exp gain, not min(1, dt*k): aim-follow speed must not depend on the
    // display refresh (same conversion as the viewmodel ADS blend)
    const ka = 1 - Math.exp(-dt * 10);
    this.turret.rotation.y = cur + d * ka;
    if (this.turretPitches) {
      // aim UP when looking up: the barrel points -Z, so +pitch = -rotation.x
      const want = Math.max(-0.5, Math.min(0.6, -pitch));
      this.turret.rotation.x += (want - this.turret.rotation.x) * ka;
    }
  }

  private arenaDrift = 0;
  private arenaSpark = 0;
  private sparkMats: THREE.MeshBasicMaterial[] | null = null;
  /** ARENA (MKDD): power-slide direction (−1/0/+1) and mini-turbo spark level
   *  (0 smoke, 1 = 2 pushes yellow, 2 = armed blue) — drives the drift FX. The
   *  slip itself is real (the hull yaw), so no visual over-steer is added. */
  setArenaDrift(dir: number, level: number): void {
    this.arenaDrift = dir;
    this.arenaSpark = level;
  }
  /** ARENA: show the driver's stocked bombs as icons floating around the kart. */
  setBombCount(n: number): void {
    n = Math.max(0, Math.min(8, Math.floor(n)));
    if (!this.bombGroup) {
      this.bombGroup = new THREE.Group();
      this.bombGroup.position.y = ARENA_KL / this.group.scale.y; // MKDD: the stack rides ~200 u (1 KL) up
      this.group.add(this.bombGroup);
    }
    if (n === this.bombCount) return;
    this.bombCount = n;
    for (const c of [...this.bombGroup.children]) {
      this.bombGroup.remove(c);
      // each bomb icon is a Group (ball + spark) — traverse to free the meshes
      c.traverse((o) => { const m = o as THREE.Mesh; m.geometry?.dispose?.(); const mat = m.material; if (mat) (Array.isArray(mat) ? mat : [mat]).forEach((x) => x.dispose?.()); });
    }
    const bombMat = new THREE.MeshStandardMaterial({ color: "#15171c", roughness: 0.5, metalness: 0.3 });
    const fuseMat = new THREE.MeshBasicMaterial({ color: "#ffb02e" });
    for (let i = 0; i < n; i++) {
      const b = new THREE.Group();
      const ball = new THREE.Mesh(new THREE.SphereGeometry(0.24, 10, 8), bombMat);
      const spark = new THREE.Mesh(new THREE.SphereGeometry(0.07, 6, 6), fuseMat);
      spark.position.set(0.1, 0.26, 0);
      b.add(ball, spark);
      const a = (i / n) * Math.PI * 2;
      b.position.set(Math.cos(a) * 1.05, 0, Math.sin(a) * 1.05);
      this.bombGroup.add(b);
    }
  }

  private slip = 0;
  /** BODY DYNAMICS (2026-08-08, realism pass): squat under throttle, dive under
   *  braking, roll with lateral G — derived from the pose stream itself (like
   *  Avatar's airborne detection) so bot- and remote-driven vehicles get it
   *  without any protocol change. */
  private dynLastX = 0;
  private dynLastZ = 0;
  private dynLastYaw = 0;
  private dynHas = false;
  private dynPrevSpeed = 0;
  private speedS = 0;
  private accelS = 0;
  private latAS = 0;
  /** Net gait animation deltas the LAST full `update()` baked on top of the raw
   *  pose (mount bob + weight-shift roll/pitch). `glide()` re-applies these held
   *  values so a throttled frame reproduces update()'s exact NET transform — the
   *  same trick Avatar.glide uses for the crouch drop + taunt bob, so a
   *  bot-driven mount doesn't vibrate between `update` (y+bob) and `glide` (y)
   *  frames. Non-mount vehicles leave these 0, so glide() writes the raw pose. */
  private gaitBobY = 0;
  private gaitRotX = 0;
  private gaitRotZ = 0;
  private vehicleT = 0;
  /** Cheap hull-pose-only write for throttled frames (parity with Avatar.glide):
   *  position + rotation ONLY — no wheel/rotor spin, no drift smoke, no leg
   *  cycle, no bomb orbit. Under the measured-frame-cost visual throttle a
   *  bot-driven REMOTE vehicle's full update() runs at ~30 Hz; calling this on
   *  the skipped frames keeps the body GLIDING every frame (it used to STEP at
   *  patch rate) while only the cheap-but-skippable spin/gait lags. Mirrors
   *  update()'s NET transform: base pose + the held drift slip + the last gait
   *  deltas. `dt` is unused (no easing on a glide frame) — kept for call parity. */
  glide(x: number, y: number, z: number, yaw: number, pitch: number, roll: number): void {
    this.group.position.set(x, y + this.gaitBobY, z);
    this.group.rotation.set(pitch + this.gaitRotX, yaw + this.slip, roll + this.gaitRotZ, "YXZ");
  }
  private immuneHidden = false;
  /** ARENA post-pop invincibility strobe (MK Balloon Battle). `hidden` toggles
   *  at the caller's blink cadence; a popped kart flickers in/out for ~1.5 s so
   *  the grace is legible. Idempotent + self-correcting: when the grace ends the
   *  caller passes `false` and the kart is restored visible. Only ever driven
   *  for arena bombkarts, so it never fights the delorean's own visibility. */
  setImmuneBlink(hidden: boolean): void {
    if (hidden === this.immuneHidden) return;
    this.immuneHidden = hidden;
    this.group.visible = !hidden;
  }
  update(x: number, y: number, z: number, yaw: number, pitch: number, roll: number, dt: number, moving: boolean): void {
    this.group.position.set(x, y, z);
    // DRIFT slip-angle: while leaning into a drift the body visibly OVERSTEERS
    // (nose points into the turn, rear slides out) — eased in/out
    // (bombkart only, and the MKDD arena kart now slides for real — its hull
    // yaw already carries the drift angle — so no extra visual slip)
    const targetSlip = 0;
    this.slip += (targetSlip - this.slip) * (1 - Math.exp(-dt * 8)); // rate-independent
    this.group.rotation.set(pitch, yaw + this.slip, roll, "YXZ");
    if (this.kind !== "horse" && this.kind !== "camel") {
      this.vehicleT += dt * (moving ? 7.5 : 1.8);
      const wheeled = this.kind === "kart" || this.kind === "bombkart" || this.kind === "delorean";
      const hover = this.rotor !== null && !wheeled;
      const travel = moving ? 1 : 0;
      // speed / longitudinal accel / lateral G from consecutive poses
      if (this.dynHas && dt > 0) {
        const inv = 1 / Math.max(dt, 1 / 240);
        const speed = Math.hypot(x - this.dynLastX, z - this.dynLastZ) * inv;
        if (speed < 45) { // teleport/respawn guard
          const accel = Math.max(-30, Math.min(30, (speed - this.dynPrevSpeed) * inv));
          let dYaw = yaw - this.dynLastYaw;
          if (dYaw > Math.PI) dYaw -= Math.PI * 2; else if (dYaw < -Math.PI) dYaw += Math.PI * 2;
          const latA = Math.max(-20, Math.min(20, (dYaw * inv) * speed)); // + = turning left
          this.speedS += (speed - this.speedS) * (1 - Math.exp(-dt * 8));
          this.accelS += (accel - this.accelS) * (1 - Math.exp(-dt * 6));
          this.latAS += (latA - this.latAS) * (1 - Math.exp(-dt * 8));
          this.dynPrevSpeed = speed;
        } else { this.speedS = 0; this.accelS = 0; this.latAS = 0; this.dynPrevSpeed = 0; }
      }
      this.dynLastX = x; this.dynLastZ = z; this.dynLastYaw = yaw; this.dynHas = true;
      // wheeled: suspension squats on throttle (+x = nose up), dives on the
      // brakes, and rolls OUTWARD with lateral G like a sprung chassis.
      // hover: banks INTO the turn and noses down under acceleration, aircraft-style.
      const dynPitch = wheeled
        ? Math.max(-0.05, Math.min(0.05, this.accelS * 0.0045))
        : hover ? Math.max(-0.1, Math.min(0.1, this.accelS * -0.008)) : 0;
      const dynRoll = wheeled
        ? Math.max(-0.07, Math.min(0.07, this.latAS * -0.0035))
        : hover ? Math.max(-0.13, Math.min(0.13, this.latAS * 0.0065)) : 0;
      this.gaitBobY = wheeled
        ? (Math.abs(Math.sin(this.vehicleT * 1.7)) * 0.035 * travel + Math.sin(this.vehicleT) * 0.008)
        : hover
          ? Math.sin(this.vehicleT * 1.35) * 0.075
          : 0;
      this.gaitRotX = (wheeled ? Math.sin(this.vehicleT * 1.7 + 0.4) * 0.018 * travel : hover ? Math.sin(this.vehicleT) * 0.018 : 0) + dynPitch;
      this.gaitRotZ = (wheeled ? Math.sin(this.vehicleT * 0.85) * 0.012 * travel : hover ? Math.sin(this.vehicleT * 0.7) * 0.018 : 0) + dynRoll;
      this.group.position.y = y + this.gaitBobY;
      this.group.rotation.set(pitch + this.gaitRotX, yaw + this.slip, roll + this.gaitRotZ, "YXZ");
    }
    // DRIFT ANIMATION: while the kart leans into a drift (|roll| beyond normal
    // steering), puff grey skid smoke off the rear — pooled, cheap, lowSpec-off
    if (this.kind === "bombkart") { // drift skid smoke is cheap + important feedback — always on
      const nowMs = performance.now();
      const drifting = this.arenaDrift !== 0 && moving;
      if (drifting && nowMs - this.lastSkidAt > 55) {
        this.lastSkidAt = nowMs;
        this.skidMat ??= new THREE.MeshBasicMaterial({ color: "#cfd4da", transparent: true, opacity: 0.5, depthWrite: false });
        this.sparkMats ??= [
          new THREE.MeshBasicMaterial({ color: "#ffd23d", transparent: true, opacity: 0.85, depthWrite: false, blending: THREE.AdditiveBlending }),
          new THREE.MeshBasicMaterial({ color: "#3dbbff", transparent: true, opacity: 0.9, depthWrite: false, blending: THREE.AdditiveBlending }),
        ];
        // SHARED geometry (size variation via scale) — ~18 fresh sphere
        // geometries/s per drifting kart was steady GPU-buffer churn
        const puff = new THREE.Mesh(skidPuffGeo, this.arenaSpark > 0 ? this.sparkMats[this.arenaSpark - 1] : this.skidMat);
        const base = (0.16 + Math.random() * 0.1) / 0.2;
        const back = 0.6, side = -this.arenaDrift * 0.35; // rear, outside wheel
        puff.position.set(
          x + Math.sin(yaw) * back + Math.cos(yaw) * side,
          y + 0.15,
          z + Math.cos(yaw) * back - Math.sin(yaw) * side,
        );
        puff.scale.setScalar(base);
        this.group.parent?.add(puff);
        this.skids.push({ m: puff, die: nowMs + 420, base });
      }
      for (let i = this.skids.length - 1; i >= 0; i--) {
        const sk = this.skids[i];
        const left = (sk.die - nowMs) / 420;
        if (left <= 0) {
          sk.m.parent?.remove(sk.m); // geometry is shared — never disposed
          this.skids.splice(i, 1);
        } else {
          sk.m.position.y += dt * 0.9;                     // smoke rises
          sk.m.scale.setScalar(sk.base * (1 + (1 - left))); // and expands as it fades
        }
      }
    }
    if (this.bombGroup && this.bombCount > 0) {
      this.bombT += dt;
      this.bombGroup.rotation.y += dt * 1.3;               // orbit
      this.bombGroup.position.y = ARENA_KL / this.group.scale.y + Math.sin(this.bombT * 3) * 0.12; // bob
      for (const c of this.bombGroup.children) c.rotation.y += dt * 2;    // spin each
    }
    if (this.kind === "horse" && !this.horseModel && horseModelArmed()) this.attachHorseModel();
    if (this.kind === "camel" && !this.camelModel && camelModelArmed()) this.attachCamelModel();
    if (this.horseModel || this.camelModel) {
      // ground speed from consecutive poses (the caller only passes `moving`)
      if (dt > 0 && Number.isFinite(this.hmX)) {
        const v = Math.hypot(x - this.hmX, z - this.hmZ) / Math.max(dt, 1 / 240);
        this.hmSpeed += (Math.min(v, 14) - this.hmSpeed) * (1 - Math.exp(-dt * 6));
      }
      this.hmX = x; this.hmZ = z;
      // the coat wears the vehicle paint (rider colour), like the fur did
      const gs = moving ? this.hmSpeed : Math.min(this.hmSpeed, 0.2);
      if (this.horseModel) {
        this.horseModel.coat.color.copy(this.bodyMat.color);
        this.horseModel.update(dt, gs, false);
      } else if (this.camelModel) {
        this.camelModel.setCoat(this.bodyMat.color);
        this.camelModel.update(dt, gs);
      }
    } else if ((this.kind === "horse" || this.kind === "camel") && this.horseLegs.length) {
      const camel = this.kind === "camel";
      this.horseT += dt * (moving ? (camel ? 5.4 : 7.8) : 1.5);
      const stride = moving ? (camel ? 0.5 : 0.6) : 0.09;
      const lift = moving ? (camel ? 0.1 : 0.13) : 0.025;
      // footfall order. horse: 4-beat WALK — LH, LF, RH, RF at even quarter
      // intervals (a natural over-ground walk, not oars). camel: 2-beat lateral
      // PACE — each side's fore+hind swing together. legs pushed lx-major:
      // 0=LF 1=LH 2=RF 3=RH.
      // for, not forEach: the callback + the two phase arrays allocated fresh
      // every frame per mount (invariant 6). Phases hoisted to module scope.
      const phaseTable = camel ? CAMEL_LEG_PHASE : HORSE_LEG_PHASE;
      for (let i = 0; i < this.horseLegs.length; i++) {
        const leg = this.horseLegs[i];
        const phase = this.horseT + phaseTable[i];
        // protract (reach forward) then plant and retract (push back)
        leg.rotation.x = Math.sin(phase) * stride;
        // foot only lifts on the forward-swing half; squared for a smooth arc
        const swing = Math.max(0, Math.cos(phase));
        leg.position.y = this.legRootY + swing * swing * lift;
        // knee/hock folds hardest mid-swing to clear the ground, straight in stance
        const shin = this.horseShins[i];
        if (shin) shin.rotation.x = moving ? -swing * (camel ? 0.55 : 0.65) : -0.04;
      }
      // body: soft vertical bob on each footfall (2x) + weight-shift roll; the
      // camel's pace rolls harder side-to-side, the horse pitches more
      const bob = moving ? Math.abs(Math.sin(this.horseT * 2)) * (camel ? 0.05 : 0.06) : Math.sin(this.horseT * 2) * 0.008;
      // capture the net gait deltas so glide() reproduces this exact transform
      // on a throttled frame (see the fields' comment) — no vibration.
      this.gaitBobY = bob;
      this.gaitRotZ = Math.sin(this.horseT) * (moving ? (camel ? 0.06 : 0.03) : 0.01);
      this.gaitRotX = Math.sin(this.horseT * 2 + 0.5) * (moving ? (camel ? 0.012 : 0.022) : 0);
      this.group.position.y = y + bob;
      this.group.rotation.z = roll + this.gaitRotZ;
      this.group.rotation.x += this.gaitRotX;
      // head/neck nods with the stride (nose dips down-forward as it plants);
      // tail swings side to side
      const nod = moving ? (camel ? 0.05 : 0.08) : 0.02;
      const nd = Math.max(0, Math.sin(this.horseT));
      // the whole head/neck cluster nods as one — translate the sub-Group by the
      // SAME per-frame delta the old per-mesh loop applied (identical result, but
      // one transform write for a merged cluster instead of N).
      if (this.headGroup) this.headGroup.position.set(0, -nd * nod, -nd * nod * 0.6);
      if (this.horseTail) this.horseTail.rotation.z = Math.sin(this.horseT * (camel ? 1.5 : 2)) * (moving ? 0.18 : 0.06);
    }
    if (this.rotor) {
      if (this.rotorAxis === "z") this.rotor.rotation.z += dt * (moving ? 45 : 25);
      else this.rotor.rotation.y += dt * (moving ? 40 : 22);
    }
    if (this.tailRotor) this.tailRotor.rotation.x += dt * (moving ? 70 : 40);
  }

  /** Park this mesh as a dormant prewarm spare: hide the body, but KEEP the
   *  borrowed pooled light COUNTED by the renderer. A PointLight under a group
   *  with `visible === false` is skipped by three's `projectObject`, so it drops
   *  out of the scene's light list — and a change in the visible light count
   *  forces a full shader recompile the instant the spare is later shown. That
   *  is the exact hitch FX_LIGHT_POOL exists to prevent, so re-parent the light
   *  to the scene root (the same place releaseFxLight keeps freed lights) at
   *  intensity 0 while dormant. Ownership is retained — it is NOT pushed back to
   *  the pool — so waking simply re-attaches it. */
  dormant(scene: THREE.Scene): void {
    this.group.visible = false;
    const l = this.pooledLight;
    if (l) { l.intensity = 0; scene.add(l); } // scene.add reparents (removes from group)
  }

  /** Wake a dormant spare (claimed by acquireVehicle): show the body and put the
   *  pooled light back under the group at its configured intensity. The light's
   *  local position survived the reparent, so it lands back where it belongs. */
  wake(): void {
    this.group.visible = true;
    const l = this.pooledLight;
    if (l) { this.group.add(l); l.intensity = this.pooledLightIntensity; }
  }

  /** Preview contexts only (previews.ts): mount the real model as soon as the
   *  GLB is LOADED, without the match's arming (that context compiles its whole
   *  scene with compileAsync after building it, so nothing compiles cold). */
  attachLoadedModel(): boolean {
    if (this.kind === "horse" && !this.horseModel && horseModelLoaded()) { this.attachHorseModel(); return true; }
    if (this.kind === "camel" && !this.camelModel && camelModelLoaded()) { this.attachCamelModel(); return true; }
    return false;
  }

  /** Mount the realistic horse/camel NOW if its module is armed (match.ts
   *  calls this on the dormant spares under the load screen, so the first live
   *  frame does not pay the clone). Same swap update() does lazily. Returns
   *  whether a model was mounted. */
  attachRealModel(): boolean {
    if (this.kind === "horse" && !this.horseModel && horseModelArmed()) { this.attachHorseModel(); return true; }
    if (this.kind === "camel" && !this.camelModel && camelModelArmed()) { this.attachCamelModel(); return true; }
    return false;
  }

  /** Swap the procedural body for the realistic model: hide every body part
   *  (the nameplate and any pooled light stay), add the model. No gait deltas
   *  are left on the group — the clips carry the motion. */
  private attachHorseModel(): void {
    this.hideProceduralMount();
    this.horseModel = new HorseModel();
    this.group.add(this.horseModel.root);
  }

  /** Same swap for the camel (camelModel.ts). */
  private attachCamelModel(): void {
    this.hideProceduralMount();
    this.camelModel = new CamelModel();
    this.group.add(this.camelModel.root);
  }

  private hideProceduralMount(): void {
    for (const c of this.group.children) {
      if (c === this.plate || (c as THREE.Light).isLight) continue;
      c.visible = false;
    }
    this.gaitBobY = 0; this.gaitRotX = 0; this.gaitRotZ = 0;
  }

  dispose(scene: THREE.Scene): void {
    // the model's geometry/textures are the shared GLB cache: detach it (and
    // free its per-instance skeleton + coat) before the group teardown
    this.horseModel?.dispose();
    this.horseModel = null;
    this.camelModel?.dispose();
    this.camelModel = null;
    // hand the borrowed light back BEFORE the group teardown (release reparents
    // it under the scene root at intensity 0 — light count never changes)
    if (this.pooledLight) { releaseFxLight(scene, this.pooledLight); this.pooledLight = null; this.underglowLight = null; }
    scene.remove(this.group);
    disposeGroup(this.group);
    for (const sk of this.skids) sk.m.parent?.remove(sk.m); // shared geometry — never disposed
    this.skids.length = 0;
    this.skidMat?.dispose();
    if (this.sparkMats) for (const m of this.sparkMats) m.dispose();
  }
}

/** A glowing bounce pad on the ground with a pulsing upward beam. */
export class JumpPadMesh {
  readonly group = new THREE.Group();
  private beam: THREE.Mesh;
  private ring: THREE.Mesh;
  private t = 0;

  constructor(scene: THREE.Scene, x: number, y: number, z: number, col = "#9dff00") {
    // THE PAD (rebuilt 2026-08-01, the prop pass) — was one flat emissive
    // cylinder. Now a bolted machine you can read as hardware: a steel housing
    // ring with eight bolts and four buttress feet, a DIAMOND-PLATE deck
    // recessed inside it (the surface the player actually stands on), and six
    // raised chevrons pointing out from the middle. Three merged meshes.
    const housing: Piece[] = [
      { g: new THREE.CylinderGeometry(1.52, 1.62, 0.2, 24), p: [0, 0.1, 0] },
      { g: new THREE.TorusGeometry(1.5, 0.07, 8, 28), p: [0, 0.2, 0], r: [Math.PI / 2, 0, 0] },
    ];
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2;
      housing.push({ g: new THREE.CylinderGeometry(0.07, 0.07, 0.1, 8), p: [Math.cos(a) * 1.46, 0.21, Math.sin(a) * 1.46] });
    }
    for (let i = 0; i < 4; i++) {
      const a = (i / 4) * Math.PI * 2 + Math.PI / 4;
      housing.push({ g: slab(0.34, 0.12, 0.5, 0.4), p: [Math.cos(a) * 1.6, 0.06, Math.sin(a) * 1.6], r: [0, -a, 0] });
    }
    const deck = new THREE.Mesh(
      new THREE.CylinderGeometry(1.42, 1.42, 0.14, 24),
      new THREE.MeshStandardMaterial(propMaps("tread", { color: col, emissive: col, emissiveIntensity: 0.55, roughness: 0.5, metalness: 0.35 })),
    );
    worldProjectUV(deck.geometry, 0.55);
    deck.position.y = 0.14;
    deck.receiveShadow = true;
    const chevrons: Piece[] = [];
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2;
      for (const [rr, w] of [[0.72, 0.5], [1.06, 0.38]] as const) {
        chevrons.push({ g: slab(w, 0.06, 0.16, 0.5), p: [Math.cos(a) * rr - Math.sin(a) * 0.1, 0.22, Math.sin(a) * rr + Math.cos(a) * 0.1], r: [0, -a + 0.6, 0] });
        chevrons.push({ g: slab(w, 0.06, 0.16, 0.5), p: [Math.cos(a) * rr + Math.sin(a) * 0.1, 0.22, Math.sin(a) * rr - Math.cos(a) * 0.1], r: [0, -a - 0.6, 0] });
      }
    }
    const disc = psurf("steel", "#2b323b", 0.5, housing, { rough: 0.48, metal: 0.7 });
    this.group.add(deck, psurf("enamel", col, 0.3, chevrons, { rough: 0.3, emissive: col, ei: 1.4 }));
    // chevrons (upward arrows) ring
    this.ring = new THREE.Mesh(
      new THREE.TorusGeometry(1.0, 0.09, 8, 20),
      new THREE.MeshBasicMaterial({ color: col }),
    );
    this.ring.rotation.x = Math.PI / 2;
    this.ring.position.y = 0.14;
    // translucent upward beam
    this.beam = new THREE.Mesh(
      new THREE.CylinderGeometry(0.9, 1.2, 3.2, 16, 1, true),
      new THREE.MeshBasicMaterial({ color: col, transparent: true, opacity: 0.12, side: THREE.DoubleSide, blending: THREE.AdditiveBlending }),
    );
    this.beam.position.y = 1.7;
    this.group.add(disc, this.ring, this.beam);
    this.group.position.set(x, y, z);
    scene.add(this.group);
  }

  update(dt: number): void {
    this.t += dt;
    const pulse = 0.5 + 0.5 * Math.sin(this.t * 4);
    (this.beam.material as THREE.MeshBasicMaterial).opacity = 0.06 + pulse * 0.12;
    this.ring.scale.setScalar(0.9 + pulse * 0.2);
    this.ring.position.y = 0.14 + pulse * 0.3;
  }

  dispose(scene: THREE.Scene): void {
    scene.remove(this.group);
    disposeGroup(this.group);
  }
}

/** A thrown ballistic knife, tumbling through the air with a glow trail. */
export class KnifeMesh {
  readonly group = new THREE.Group();
  private spin = 0;

  constructor(scene: THREE.Scene) {
    const steel = new THREE.MeshStandardMaterial({ color: "#d6dde6", metalness: 0.9, roughness: 0.25, emissive: "#88ccff", emissiveIntensity: 0.3 });
    const blade = new THREE.Mesh(new THREE.ConeGeometry(0.09, 0.7, 4), steel);
    blade.rotation.x = -Math.PI / 2;
    blade.position.z = -0.2;
    const handle = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.05, 0.32, 6), new THREE.MeshStandardMaterial({ color: "#1c1f26" }));
    handle.rotation.x = Math.PI / 2;
    handle.position.z = 0.28;
    this.group.add(blade, handle);
    this.glow = lowSpec() ? null : acquireFxLight(scene);
    if (this.glow) { this.glow.color.set("#9cddff"); this.glow.intensity = 3; this.glow.distance = 6; this.group.add(this.glow); }
    scene.add(this.group);
  }
  private glow: THREE.PointLight | null = null;

  private sx = NaN; private sy = NaN; private sz = NaN;
  private extraT = 0;
  update(x: number, y: number, z: number, vx: number, vy: number, vz: number, dt: number, capSec = 0.05, gravity = 0): void {
    // Patches land at ~30 Hz but frames render faster — extrapolate along the
    // replicated velocity between patches so the blade glides instead of
    // holding 2-3 duplicate frames per patch. GRAVITY-AWARE (2026-08-20 fluid):
    // the server integrates the arc with `gravity` (BALLISTIC.gravity = 26), so
    // a straight-line velocity extrapolation drifts ABOVE the true parabola and
    // snaps DOWN on the next patch — a periodic vertical hitch (up to ~½·g·T²,
    // ~1.3 cm at 30 Hz, ~3 cm in a relieved 20 Hz room) on a projectile people
    // watch arc. Folding −½·g·t² into the y prediction tracks the real drop, so
    // the snap collapses toward zero. Closed form from the snap base (sx/sy/sz)
    // so an irregular frame cadence can't accumulate. Snap whenever the
    // replicated position actually moves; cap the extrapolation so a
    // static-but-still-patching projectile cannot drift. `capSec` defaults to
    // the 30 Hz value (50 ms); match.ts passes an adaptive cap keyed to the
    // measured patch interval so a relieved 20 Hz room (50 ms gaps) keeps
    // gliding through late patches instead of freezing.
    if (x !== this.sx || y !== this.sy || z !== this.sz) {
      this.sx = x; this.sy = y; this.sz = z;
      this.extraT = 0;
      this.group.position.set(x, y, z);
    } else if (this.extraT < capSec) {
      this.extraT = Math.min(capSec, this.extraT + dt);
      const t = this.extraT;
      this.group.position.set(this.sx + vx * t, this.sy + vy * t - 0.5 * gravity * t * t, this.sz + vz * t);
    }
    this.group.rotation.y = Math.atan2(vx, vz);
    this.spin += dt * 30;
    this.group.rotation.x = this.spin; // tumble
  }

  dispose(scene: THREE.Scene): void {
    releaseFxLight(scene, this.glow);
    this.glow = null;
    scene.remove(this.group);
    disposeGroup(this.group);
  }
}

/** A thrown grenade tumbling through the air. */
/** ARENA danger-zone telegraph: the flat ring that marks a settled bomb's kill
 *  aura (see GunGameRoom.stepGrenades — `GRENADE.blastRadius * 1.7`). One shared
 *  geometry across every bomb (marked `shared` so disposeGroup leaves it alone);
 *  each bomb owns its own material for the per-fuse pulse colour/opacity. */
const BOMB_AURA_R = ARENA_BOMB.blastR; // MKDD blast: 1360 u = 6.8 kart lengths
let sharedBombRingGeo: THREE.RingGeometry | null = null;
function bombRingGeo(): THREE.RingGeometry {
  if (!sharedBombRingGeo) {
    sharedBombRingGeo = new THREE.RingGeometry(BOMB_AURA_R * 0.86, BOMB_AURA_R, 48);
    (sharedBombRingGeo.userData as { shared?: boolean }).shared = true;
  }
  return sharedBombRingGeo;
}

export class GrenadeMesh {
  readonly group = new THREE.Group();
  private spin = 0;
  private age = 0;
  private blink = 0;
  private mat: THREE.MeshStandardMaterial;
  private body: THREE.Mesh;
  private light: THREE.PointLight | null = null;
  // ARENA only: ground ring telegraphing the settled bomb's kill aura
  private ring: THREE.Mesh | null = null;
  private ringMat: THREE.MeshBasicMaterial | null = null;
  private ringCol = new THREE.Color();
  /** ~fuse seconds — the blink accelerates toward this then the bomb explodes. */
  private fuse: number;
  /** ARENA (Bob-omb Blast) mine: true once this bomb is a settle-then-blow floor
   *  mine (constructed with the arena flag). Drives the settle-relative telegraph. */
  private arena: boolean;
  /** ARENA: age (s) at which the mine first SETTLED, or -1 while still airborne.
   *  The server re-times a settled bomb's detonation to settle + ARENA_MINE_LIVE_MS
   *  (deferring the throw fuse for a lofted arc), so the blink/beep/ring must
   *  count from HERE, not from the throw, or a high lob's warning saturates ~0.8 s
   *  before it actually pops. */
  private settleAge = -1;
  constructor(scene: THREE.Scene, fuseSec = 1.8, lightweight = false, neon?: string, arena = false) {
    this.fuse = fuseSec;
    this.arena = arena;
    // thrower's neon (arena): the bomb + its blink wear the owner's color
    this.mat = neon
      ? new THREE.MeshStandardMaterial({ color: neon, roughness: 0.4, metalness: 0.3, emissive: neon, emissiveIntensity: 0.7 })
      : new THREE.MeshStandardMaterial({ color: "#8faa4a", roughness: 0.5, metalness: 0.3, emissive: "#3a4a1a", emissiveIntensity: 0.4 });
    this.body = new THREE.Mesh(new THREE.CapsuleGeometry(0.18, 0.22, 6, 10), this.mat);
    // The BODY spins, not the group — the group holds the flat aura ring, which
    // must stay level with the ground while the bomb tumbles.
    this.group.add(this.body);
    if (!lightweight && !lowSpec()) {
      this.light = acquireFxLight(scene);
      if (this.light) { this.light.color.set(neon ?? "#c0ff40"); this.light.distance = 4; this.group.add(this.light); }
    }
    // ARENA: the danger-zone ring. Hidden until the bomb SETTLES (a flying bomb
    // has no aura server-side — see stepGrenades) then pulses with the fuse.
    if (arena) {
      this.ringMat = new THREE.MeshBasicMaterial({
        color: neon ?? "#ff5a2a", transparent: true, opacity: 0,
        side: THREE.DoubleSide, depthWrite: false,
      });
      this.ring = new THREE.Mesh(bombRingGeo(), this.ringMat);
      this.ring.rotation.x = -Math.PI / 2; // lie flat on the ground
      this.ring.position.y = -0.12;         // drop from bomb-centre to floor level
      this.ring.renderOrder = 2;
      this.ring.visible = false;
      this.ringCol.set(neon ?? "#ff5a2a");
      this.group.add(this.ring);
    }
    // cached blink colors — .set(hexString) parsed the hex EVERY frame
    this.colHot.set("#ff2a1a"); this.colHotLight.set("#ff3a2a"); this.colBase.set(neon ?? "#c0ff40");
    scene.add(this.group);
  }
  private colHot = new THREE.Color();
  private colHotLight = new THREE.Color();
  private colBase = new THREE.Color();
  private wasOn = false;
  private sx = NaN; private sy = NaN; private sz = NaN;
  private extraT = 0;
  /** Returns the beep urgency (0..1) on each blink RISING edge, else -1 — the
   *  caller plays the accelerating fuse bip so sound and light stay in sync.
   *  Pass the replicated velocity for inter-patch extrapolation (same
   *  treatment as KnifeMesh.update); omitting it just snaps at patch rate.
   *  `capSec` is the adaptive extrapolation cap (see KnifeMesh.update). */
  update(x: number, y: number, z: number, dt: number, vx = 0, vy = 0, vz = 0, capSec = 0.05, gravity = 0): number {
    if (x !== this.sx || y !== this.sy || z !== this.sz) {
      this.sx = x; this.sy = y; this.sz = z;
      this.extraT = 0;
      this.group.position.set(x, y, z);
    } else if (this.extraT < capSec && (vx !== 0 || vy !== 0 || vz !== 0)) {
      this.extraT = Math.min(capSec, this.extraT + dt);
      const t = this.extraT;
      // GRAVITY-AWARE arc (2026-08-20 fluid; see KnifeMesh.update). A grenade
      // arcs hard (GRENADE.gravity = 22), so the −½·g·t² term matters most here.
      // But a SETTLED bomb (server aura threshold hypot(v) < 6) is at rest on
      // the ground with only a velocity residual — dropping the arc term there
      // would sink it THROUGH the floor between patches, so zero gravity once
      // settled. A bomb in flight always clears that threshold.
      const g = Math.hypot(vx, vy, vz) < 6 ? 0 : gravity;
      this.group.position.set(this.sx + vx * t, this.sy + vy * t - 0.5 * g * t * t, this.sz + vz * t);
    }
    this.spin += dt * 10;
    this.body.rotation.set(this.spin, this.spin * 0.7, 0);
    // BLINK faster and faster as the fuse burns down, reddening near the end
    this.age += dt;
    // ARENA mine: a thrown bomb is only a live hazard once it SETTLES (server
    // aura threshold hypot(v) < 6). The server then re-times its detonation to
    // settle + ARENA_MINE_LIVE_MS, so drive the telegraph from the settle moment
    // over that window rather than the fixed throw fuse — otherwise a lofted arc
    // reaches full-red saturation ~0.8 s BEFORE it actually pops. A quick flat
    // toss lands before the throw fuse matters, so `max` keeps it unchanged.
    const settled = this.arena && Math.hypot(vx, vy, vz) < 6;
    if (settled && this.settleAge < 0) this.settleAge = this.age;
    let f: number;
    if (this.arena) {
      if (this.settleAge < 0) {
        // still arcing overhead: a calm idle blink, NOT the accelerating warning
        // — the mine isn't live yet, so cap the progress low (no red, no
        // saturation) until it lands, or a long lob would max the warning out
        // in the air before its real fuse even starts.
        f = Math.min(0.5, this.age / this.fuse);
      } else {
        // landed & armed: run the warning over the SAME window the server pops
        // on — max(throw fuse, settle + ARENA_MINE_LIVE_MS) — so it peaks at the
        // real detonation for both a flat toss and a high lob.
        // MKDD: only a DROPPED bomb ever sits (a thrown one blows on landing);
        // it blows ARENA_BOMB.dropFuseMs after landing and flashes all along.
        f = Math.min(1, (this.age - this.settleAge) / (ARENA_BOMB.dropFuseMs / 1000));
      }
    } else {
      f = Math.min(1, this.age / this.fuse);
    }
    const rate = 2.5 + f * f * 12;           // 2.5 Hz → ~14.5 Hz (accelerating)
    this.blink += dt * rate;
    const on = Math.sin(this.blink * Math.PI * 2) > 0;
    const beep = on && !this.wasOn ? f : -1;
    this.wasOn = on;
    // late fuse → warning red. An arena mine only goes hot once it is a settled,
    // armed hazard — a bomb still arcing overhead can't pop anyone, so it never
    // shows the red "about to blow" state until it lands.
    const hot = f > 0.55 && (!this.arena || settled);
    this.mat.emissive.copy(hot ? this.colHot : this.colBase);
    this.mat.emissiveIntensity = on ? 2.4 : 0.15;
    if (this.light) {
      this.light.color.copy(hot ? this.colHotLight : this.colBase);
      this.light.intensity = on ? 2.2 + f * 2 : 0.2;
    }
    // ARENA danger ring: a settled bomb (server aura threshold, hypot(v) < 6) is
    // a live mine — flash its kill-radius footprint. Pulses with the blink, goes
    // red and swells in the final warning window. Off while it's still airborne.
    if (this.ring && this.ringMat) {
      this.ring.visible = settled;
      if (settled) {
        const base = hot ? 0.5 : 0.28;
        this.ringMat.opacity = base * (on ? 1 : 0.4);
        this.ringMat.color.copy(hot ? this.colHot : this.ringCol);
        this.ring.scale.setScalar(hot ? 1 + ((f - 0.55) / 0.45) * 0.1 : 1);
      }
    }
    return beep;
  }
  dispose(scene: THREE.Scene): void {
    releaseFxLight(scene, this.light);
    this.light = null;
    scene.remove(this.group);
    disposeGroup(this.group);
  }
}

/** PURGE containment door — a shimmering neon force-field wall that seals a map
 *  sector until bought. Dissolves (fades out) when opened. */
export class DoorMesh {
  readonly group = new THREE.Group();
  private mat: THREE.MeshStandardMaterial;
  private frameMats: THREE.MeshStandardMaterial[] = [];
  private t = 0;
  private open = false;
  private fade = 1; // 1 = solid field, 0 = fully dissolved
  constructor(scene: THREE.Scene, sx: number, sy: number, sz: number) {
    // THE FIELD (rebuilt 2026-08-01, the prop pass) — was a flat translucent
    // pane in one colour. Now a HEX CONTAINMENT LATTICE: the `hex` family is
    // the albedo AND the emissive map, so the cell walls glow and the cell
    // interiors stay dark instead of the whole sheet being uniformly bright.
    // World-projected UVs keep the cells the same physical size on a 3 m
    // doorway and a 12 m sector wall.
    this.mat = new THREE.MeshStandardMaterial(propMaps("hex", {
      color: "#20e0ff", emissive: "#7fe8ff", emissiveIntensity: 1.4,
      transparent: true, opacity: 0.42, roughness: 0.3, metalness: 0.1,
      side: THREE.DoubleSide, depthWrite: false,
    }, 0.6));
    this.mat.emissiveMap = propAlbedo("hex");
    const sheetGeo = new THREE.BoxGeometry(sx, sy, sz);
    worldProjectUV(sheetGeo, 1.6);
    const sheet = new THREE.Mesh(sheetGeo, this.mat);
    this.group.add(sheet);
    // EMITTER PILLARS, not glowing slabs: a machined post with a base shoe, a
    // capital, three projector heads up its inner face and a cable conduit.
    // One merged mesh per side, same count as the two boxes it replaces.
    const thin = sx < sz; // wall spans Z (thin in X) → posts at ±Z ends
    for (const sgn of [-1, 1]) {
      const pm = new THREE.MeshStandardMaterial(propMaps("steel", {
        color: "#7af0ff", emissive: "#30d0ff", emissiveIntensity: 2.2, roughness: 0.4, metalness: 0.55,
      }));
      this.frameMats.push(pm);
      const pieces: Piece[] = [
        { g: vol(0.78, sy, 0.78, 0.4) },
        { g: slab(1.0, 0.28, 1.0, 0.35), p: [0, -sy / 2 + 0.14, 0] },   // base shoe
        { g: slab(1.0, 0.22, 1.0, 0.35), p: [0, sy / 2 - 0.11, 0] },    // capital
        { g: slab(0.18, sy * 0.8, 0.18, 0.9), p: [0.3 * (thin ? 0 : 1), 0, 0.3 * (thin ? 1 : 0)] }, // conduit
      ];
      for (let i = -1; i <= 1; i++) { // projector heads facing along the field
        const off = -sgn * 0.42;
        pieces.push({
          g: new THREE.CylinderGeometry(0.13, 0.17, 0.16, 10),
          p: thin ? [0, i * sy * 0.28, off] : [off, i * sy * 0.28, 0],
          r: thin ? [Math.PI / 2, 0, 0] : [0, 0, Math.PI / 2],
        });
      }
      const post = new THREE.Mesh(bake(pieces), pm);
      worldProjectUV(post.geometry, 0.6);
      post.castShadow = true;
      if (thin) post.position.z = sgn * sz / 2;
      else post.position.x = sgn * sx / 2;
      this.group.add(post);
    }
    scene.add(this.group);
  }
  update(x: number, y: number, z: number, open: boolean, dt: number): void {
    this.group.position.set(x, y, z);
    this.t += dt;
    if (open && !this.open) this.open = true;
    // dissolve/restore
    const target = this.open ? 0 : 1;
    this.fade += (target - this.fade) * (1 - Math.exp(-dt * 3)); // rate-independent dissolve
    const shimmer = 0.32 + Math.sin(this.t * 3) * 0.1;
    this.mat.opacity = this.fade * shimmer;
    this.mat.emissiveIntensity = this.fade * (1.1 + Math.sin(this.t * 5) * 0.4);
    for (const pm of this.frameMats) pm.emissiveIntensity = this.fade * 2.2;
    this.group.visible = this.fade > 0.02;
  }
  dispose(scene: THREE.Scene): void { scene.remove(this.group); disposeGroup(this.group); }
}

/** Medieval drawbridge: wooden deck with chains/gears/lever. The server owns
 *  lowered/raised state; this only renders the replicated animation fraction. */
export class DrawbridgeMesh {
  readonly group = new THREE.Group();
  private pivot = new THREE.Group();
  private deck = new THREE.Group();
  private lever = new THREE.Group();
  private lever2: THREE.Group | null = null;
  private leverArm: THREE.Group | null = null;
  private leverArm2: THREE.Object3D | null = null;
  private gears: THREE.Object3D[] = [];
  /** Both hawsers, merged into one mesh and parented to the deck so they ride
   *  with it (see the build). */
  private hawsers: THREE.Object3D;
  private frac = 1;
  private lastFrac = 1;
  private sparkT = 0;

  constructor(scene: THREE.Scene) {
    // REBUILT 2026-08-01 (the prop pass). Was 65 flat-coloured boxes and
    // cylinders — every plank, batten, stud and gear tooth its own draw call,
    // and not one map anywhere on the biggest thing the player walks across.
    // Now: weathered `oak` timber, `steel` ironwork, `weave` hawsers, merged
    // per material. 65 -> ~15 draw calls for MORE geometry.
    const OAK = "#6a3d20", DARK_OAK = "#3b2516", IRON = "#25272c", ROPE = "#9a7a52";
    const recv = (m: THREE.Mesh): THREE.Mesh => { m.receiveShadow = true; return m; };

    // THE DECK: seven planks with a real gap and a crowned top face, three
    // cross battens, two kerb rails, fourteen iron studs.
    const planks: Piece[] = [];
    for (let i = -3; i <= 3; i++) planks.push({ g: slab(1.36, 0.22, 12, 0.12), p: [i * 1.45, 0, -6] });
    const frame: Piece[] = [
      { g: slab(10.6, 0.34, 0.34, 0.3), p: [0, 0.12, -0.6] },
      { g: slab(10.6, 0.34, 0.34, 0.3), p: [0, 0.12, -5.8] },
      { g: slab(10.6, 0.34, 0.34, 0.3), p: [0, 0.12, -10.8] },
      { g: slab(0.34, 0.46, 12.2, 0.25), p: [-5.5, 0.14, -6] },
      { g: slab(0.34, 0.46, 12.2, 0.25), p: [5.5, 0.14, -6] },
    ];
    const studs: Piece[] = [];
    for (const sx of [-5.5, 5.5]) {
      for (let j = 0; j < 7; j++) {
        studs.push({ g: new THREE.CylinderGeometry(0.12, 0.12, 0.14, 8), p: [sx, 0.45, -1.2 - j * 1.55], r: [Math.PI / 2, 0, 0] });
      }
    }
    this.deck.add(
      recv(psurf("oak", OAK, 1.1, planks, { rough: 0.88 })),
      recv(psurf("oak", DARK_OAK, 0.9, frame, { rough: 0.9 })),
      psurf("steel", IRON, 0.3, studs, { rough: 0.45, metal: 0.8 }),
    );
    this.deck.position.z = -6;
    this.pivot.add(this.deck);
    this.pivot.position.z = 6;
    this.group.add(this.pivot);

    // the winch AXLE sits OVERHEAD like a real gatehouse windlass — at waist
    // height it read as an iron bar blocking the entry ("stick in the gate")
    const axle: Piece[] = [
      { g: slab(13, 0.55, 0.5, 0.5), p: [0, 6.4, 6.25] },
      { g: new THREE.CylinderGeometry(0.16, 0.16, 12.4, 10), p: [0, 6.4, 6.1], r: [0, 0, Math.PI / 2] },
    ];
    this.group.add(psurf("steel", IRON, 0.9, axle, { rough: 0.42, metal: 0.78 }));
    for (const sx of [-5.2, 5.2]) {
      // ONE merged gear: rim, hub, four spokes and eight teeth, all in the
      // gear's own frame with the axle along X — so `rotation.x` in update()
      // now spins the whole wheel about its axle. (Before, the teeth were
      // separate meshes that stayed put while the disc turned.)
      const cog: Piece[] = [
        { g: new THREE.CylinderGeometry(0.72, 0.72, 0.22, 20), r: [0, 0, Math.PI / 2] },
        { g: new THREE.CylinderGeometry(0.26, 0.26, 0.34, 10), r: [0, 0, Math.PI / 2] },
      ];
      for (let t = 0; t < 8; t++) {
        const a = (t / 8) * Math.PI * 2;
        cog.push({ g: slab(0.26, 0.34, 0.3, 0.3), p: [0, Math.cos(a) * 0.85, Math.sin(a) * 0.85], r: [a, 0, 0] });
        if (t % 2 === 0) cog.push({ g: slab(0.16, 0.6, 0.14, 0.4), p: [0, Math.cos(a) * 0.4, Math.sin(a) * 0.4], r: [a, 0, 0] });
      }
      const gear = psurf("steel", IRON, 0.3, cog, { rough: 0.4, metal: 0.85 });
      gear.position.set(sx, 6.4, 6.1);
      this.group.add(gear);
      this.gears.push(gear);
    }
    // chains ride ON the deck (children of it) — rising from the outer corners
    // toward the gatehouse axle when the deck is down, and swinging up with the
    // deck when it raises, so they never float free in the gate. Both hawsers
    // are ONE merged mesh; each is a twisted pair so it reads as laid rope.
    const rigging: Piece[] = [];
    for (const hx of [-4.6, 4.6]) {
      for (const tw of [-1, 1]) {
        rigging.push({ g: slab(0.07, 0.07, 11, 0.9), p: [hx + tw * 0.035, 1.5 + tw * 0.035, -6], r: [0.26, 0, 0] });
      }
      rigging.push({ g: new THREE.TorusGeometry(0.13, 0.03, 5, 10), p: [hx, 2.9, -0.9], r: [0, Math.PI / 2, 0] }); // shackle
    }
    this.hawsers = psurf("weave", ROPE, 0.25, rigging, { rough: 0.95 });
    this.deck.add(this.hawsers);

    // a proper winch lever: stone plinth, iron mount + quadrant ratchet, a
    // banded wooden throw-arm with a forged grip ball
    this.lever.add(psurf("oak", DARK_OAK, 0.5, [{ g: slab(0.9, 0.34, 0.9, 0.2), p: [0, 0.17, 0] }], { rough: 0.9 }));
    const mount: Piece[] = [
      { g: slab(0.7, 0.12, 0.7, 0.3), p: [0, 0.36, 0] },                        // cap plate
      { g: slab(0.1, 0.7, 0.1, 0.5), p: [-0.22, 0.7, 0] },                      // twin posts
      { g: slab(0.1, 0.7, 0.1, 0.5), p: [0.22, 0.7, 0] },
      { g: new THREE.CylinderGeometry(0.42, 0.42, 0.06, 16, 1, false, 0, Math.PI * 0.7), p: [0, 0.62, 0], r: [Math.PI / 2, 0, 0] }, // ratchet quadrant
    ];
    this.lever.add(psurf("steel", IRON, 0.28, mount, { rough: 0.4, metal: 0.8 }));
    // throw-arm (pivots at the posts) — a banded oak bar with a grip ball
    const arm = new THREE.Group();
    arm.position.set(0, 0.68, 0);
    arm.add(psurf("oak", DARK_OAK, 0.4, [{ g: new THREE.CylinderGeometry(0.075, 0.09, 1.25, 10), p: [0, 0.55, 0] }], { rough: 0.9 }));
    const fittings: Piece[] = [
      { g: new THREE.CylinderGeometry(0.1, 0.1, 0.08, 10), p: [0, 0.35, 0] },
      { g: new THREE.CylinderGeometry(0.1, 0.1, 0.08, 10), p: [0, 0.75, 0] },
      { g: new THREE.SphereGeometry(0.15, 12, 10), p: [0, 1.2, 0] },
    ];
    arm.add(psurf("steel", IRON, 0.2, fittings, { rough: 0.35, metal: 0.85 }));
    arm.rotation.z = -0.55;
    this.lever.add(arm);
    this.leverArm = arm;
    this.group.add(this.lever);
    // second lever (outside the walls) — same look, positioned via update()
    this.lever2 = this.lever.clone(true);
    this.leverArm2 = this.lever2.children[this.lever2.children.length - 1]; // cloned arm
    this.lever2.visible = false;
    this.group.add(this.lever2);
    scene.add(this.group);
  }

  update(x: number, y: number, z: number, sx: number, _sy: number, sz: number, yaw: number, frac: number, dt: number, lever?: { x: number; y: number; z: number }, lever2?: { x: number; y: number; z: number }): boolean {
    this.lastFrac = this.frac;
    this.frac += (frac - this.frac) * (1 - Math.exp(-dt * 10)); // rate-independent raise/lower
    const deckScaleX = sx / 12;
    const deckScaleZ = sz / 12;
    this.group.position.set(x, y, z);
    this.group.rotation.y = yaw;
    this.group.scale.set(deckScaleX, 1, deckScaleZ);
    this.pivot.position.z = sz / 2 / deckScaleZ;
    // deck.position.z = 0: the planks (centred at deck-local -6) then start AT
    // the pivot and reach the moat's far edge — matching the collider. The old
    // -sz/2 double-offset shoved the visual deck ~12 m north, so it missed the
    // castle half of the moat ("does not cover the full gap").
    this.deck.position.z = 0;
    const raised = (1 - this.frac) * Math.PI / 2;
    this.pivot.rotation.x = raised;
    // for, not forEach: this runs every frame per drawbridge (invariant 6).
    // rotation.X, not .y: the merged cog's axle runs along X (see the build),
    // so this is now the wheel turning on its axle instead of the old disc
    // yawing while its teeth stood still.
    for (let i = 0; i < this.gears.length; i++) this.gears[i].rotation.x = (1 - this.frac) * Math.PI * 2 * (i ? -1 : 1);
    // chains are children of the deck now (they ride with it), so no separate
    // rotation — that's what left them floating in the gate when raised.
    this.lever.position.set(
      ((lever?.x ?? x) - x) / deckScaleX,
      (lever?.y ?? y + 0.5) - y,
      ((lever?.z ?? z) - z) / deckScaleZ,
    );
    // the BASE just orients to the wall; the throw-ARM swings with the state
    this.lever.rotation.set(0, -yaw, 0);
    const throwZ = this.frac > 0.5 ? -0.55 : 0.55; // down = lowered, up = raised
    if (this.leverArm) this.leverArm.rotation.z = throwZ;
    if (this.lever2 && lever2) {
      this.lever2.visible = true;
      this.lever2.position.set((lever2.x - x) / deckScaleX, lever2.y - y, (lever2.z - z) / deckScaleZ);
      this.lever2.rotation.set(0, -yaw, 0);
      if (this.leverArm2) this.leverArm2.rotation.z = throwZ;
    }
    this.sparkT += dt;
    if (Math.abs(this.frac - this.lastFrac) > 0.015 && this.sparkT > 0.12) {
      this.sparkT = 0;
      return true;
    }
    return false;
  }

  dispose(scene: THREE.Scene): void { scene.remove(this.group); disposeGroup(this.group); }
}

/** A neon CHINESE DRAGON — a serpentine ribbon of glowing segments that trails
 *  its head along an arc-length history buffer. Practice-field spectacle you can
 *  ride. Head takes a yaw/pitch; the body follows on a fixed-spacing trail. */
export class DragonMesh {
  readonly group = new THREE.Group();
  private head = new THREE.Group();
  private segs: THREE.Mesh[] = [];
  private eyeMats: THREE.MeshStandardMaterial[] = [];
  private trail: THREE.Vector3[] = [];
  private readonly spacing = 1.15; // metres between body segments
  private t = 0;
  /* 2026-08-01, mission `graphics` (the BEAST pass). The dragon used to be a
   * BoxGeometry snout with two cones for horns towing 22 flat-shaded UV
   * spheres, each carrying a separate cone fin — 52 draw calls of untextured
   * primitive. It is now built on the same `creature.ts` toolkit as the kaiju:
   * a real skull with a tapering snout, a hinged jaw, branched antlers, a mane
   * and barbels, towing a serpent body whose every segment is ONE merged buffer
   * (elliptical trunk + dorsal fin + ventral scutes, plus clawed limbs on two
   * of them). Surfaces are the new `serp` lamellar-scale family plus `horn`
   * and `fur`, so the thing that flies past at 60 m finally has a scale run
   * you can see. */
  constructor(scene: THREE.Scene, count = 22) {
    // NB: -z is FORWARD for the HEAD (yawPitchToDir convention) — the face
    // lives on -z, horns/mane/barbels sweep back toward +z. The BODY segments
    // are placed with lookAt(), which points local +Z at the target, so their
    // geometry is authored with forward = +Z. Do not unify the two.
    const RED = "#ff5230", GOLD = "#ffcf3a";

    // ---- HEAD ----------------------------------------------------------
    // one merged `serp` skull: cranium, tapering snout, brow shelf, cheek
    // ridges, hinged lower jaw, and the neck collar the mane sits on
    const skull: Piece[] = [
      { g: vol(1.65, 1.35, 1.7, 0.9), p: [0, 0.1, 0.35] },                        // cranium
      { g: taperVol(1.35, 2.0, 1.15, 0.62, 0.95), r: [Math.PI / 2, 0, 0], p: [0, -0.05, -1.2] }, // snout
      { g: vol(1.9, 0.42, 1.0, 0.7), p: [0, 0.78, -0.35] },                       // brow shelf
      { g: vol(0.55, 0.5, 1.1, 0.85), p: [0.72, 0.18, -0.5] },                    // cheek L
      { g: vol(0.55, 0.5, 1.1, 0.85), p: [-0.72, 0.18, -0.5] },                   // cheek R
      { g: taperVol(1.0, 1.7, 0.55, 0.7, 0.9), r: [Math.PI / 2, 0, 0], p: [0, -0.72, -1.05] }, // lower jaw
      { g: vol(1.5, 1.4, 0.9, 0.95), p: [0, 0.05, 1.15] },                        // neck collar
    ];
    const headMesh = surf("serp", RED, 0.55, skull,
      { emissive: "#c01810", ei: 0.55, rough: 0.42, metal: 0.4, shadow: false });
    this.head.add(headMesh);

    // one merged `horn` mesh: branched antlers, fangs, barbel roots, nose ridge
    const keratin: Piece[] = [
      { g: vol(0.3, 0.22, 1.5, 0.9), p: [0, 0.55, -1.3] },                        // nose ridge
    ];
    for (const sx of [-1, 1]) {
      // main antler beam sweeping up and back, with two tines off it
      keratin.push({ g: spike(0.24, 1.9), p: [sx * 0.55, 1.5, 0.35], r: [0.55, 0, sx * -0.4] });
      keratin.push({ g: spike(0.13, 0.85), p: [sx * 0.9, 2.05, 0.75], r: [0.9, 0, sx * -0.9] });
      keratin.push({ g: spike(0.1, 0.6), p: [sx * 0.35, 2.1, 0.95], r: [1.2, 0, sx * 0.3] });
      // fangs at the jaw line, pointing down and slightly forward
      keratin.push({ g: spike(0.09, 0.5), p: [sx * 0.42, -0.42, -1.55], r: [Math.PI + 0.25, 0, 0] });
      keratin.push({ g: spike(0.07, 0.36), p: [sx * 0.3, -0.4, -1.05], r: [Math.PI, 0, 0] });
      // barbels: long tapering whiskers trailing back from the snout corners
      keratin.push({
        g: taperVol(0.14, 3.4, 0.14, 0.12, 1), r: [-1.15, 0, sx * 0.5],
        p: [sx * 0.78, -0.18, -0.55],
      });
    }
    this.head.add(surf("horn", GOLD, 0.35, keratin, { rough: 0.3, metal: 0.55, shadow: false }));

    // the mane — a fur ruff of five swept-back lobes behind the skull
    const mane: Piece[] = [];
    for (let i = 0; i < 5; i++) {
      const a = (i / 4 - 0.5) * 2.2;                       // -1.1 .. 1.1 rad around the collar
      mane.push({
        g: taperVol(0.5, 1.5, 0.34, 0.25, 0.9),
        p: [Math.sin(a) * 0.95, Math.cos(a) * 0.95 + 0.1, 1.25],
        r: [0.7, 0, -a],
      });
    }
    this.head.add(surf("fur", "#ff9a2e", 0.28, mane, { emissive: "#c04a08", ei: 0.5, rough: 0.8, shadow: false }));

    // eyes: deliberately BARE (an unmapped emissive lens — a scale map on an
    // eyeball reads as dirt). One merged mesh, one material, both eyes.
    const eyeMat = new THREE.MeshStandardMaterial({
      color: "#ffffff", emissive: "#8affff", emissiveIntensity: 2.6, roughness: 0.15,
    });
    eyeMat.userData.bare = true;
    this.eyeMats.push(eyeMat);
    this.head.add(new THREE.Mesh(bake([
      { g: new THREE.SphereGeometry(0.3, 12, 12), p: [0.66, 0.4, -1.0] },
      { g: new THREE.SphereGeometry(0.3, 12, 12), p: [-0.66, 0.4, -1.0] },
    ]), eyeMat));
    this.group.add(this.head);

    // ---- BODY ----------------------------------------------------------
    // Each segment is ONE merged buffer (was: a sphere + a separate fin mesh).
    // Forward is +Z. Alternating gold/red courses survive from the old build —
    // it is the read that made the dragon legible against a night sky.
    for (let i = 0; i < count; i++) {
      const f = i / count;
      const r = 0.92 * (1 - f * 0.5);
      // Trunk is a true ELLIPSOID (a scaled sphere), not a rounded box: a
      // rounded box is elliptical in X/Z but stays flat-topped in Y, and at 15 m
      // that flat top is exactly the facet that read as a string of bricks in
      // the first capture of this pass. `zHalf` deliberately over-runs the
      // 1.15 m segment spacing so consecutive trunks INTERPENETRATE all the way
      // down the taper — otherwise the tail separates into beads.
      const zHalf = r * 1.15 + 0.55;
      const pieces: Piece[] = [
        { g: new THREE.SphereGeometry(1, 16, 11), s: [r * 0.92, r * 1.05, zHalf] },
        // dorsal fin — a swept crest blade, not a cone
        { g: taperVol(r * 0.22, r * 1.35, r * 1.5, 0.18, 0.6), p: [0, r * 1.35, -r * 0.15], r: [0.3, 0, 0] },
        // ventral scutes: two belly plates, the banded underside of a snake
        { g: vol(r * 1.0, r * 0.24, r * 0.85, 0.8), p: [0, -r * 0.92, r * 0.5] },
        { g: vol(r * 1.0, r * 0.24, r * 0.85, 0.8), p: [0, -r * 0.92, -r * 0.5] },
      ];
      // two pairs of clawed limbs, at the shoulder and the haunch, exactly
      // where a chinese dragon carries them
      if (i === 3 || i === 10) {
        for (const sx of [-1, 1]) {
          pieces.push({ g: taperVol(r * 0.42, r * 1.5, r * 0.42, 0.6, 0.9), p: [sx * r * 1.0, -r * 0.75, 0], r: [0, 0, sx * 0.6] });
          pieces.push({ g: vol(r * 0.55, r * 0.3, r * 0.7, 0.9), p: [sx * r * 1.5, -r * 1.45, 0] });
          for (const k of [-1, 0, 1]) {
            pieces.push({ g: spike(r * 0.1, r * 0.5), p: [sx * r * 1.7, -r * 1.6, k * r * 0.22], r: [-2.0, 0, sx * 0.5] });
          }
        }
      }
      const seg = surf("serp", i % 2 ? GOLD : RED, 0.5, pieces, {
        emissive: i % 2 ? "#b8860b" : "#c02010", ei: 0.85, rough: 0.4, metal: 0.5,
        // NO shadow casting, exactly as the pre-2026-08-01 build: the dragon is
        // a glowing cinematic that flies 20-40 m up, so its shadow lands off
        // the play space — but 26 extra shadow-map draw calls per light is a
        // measured cost. Turning it on cost ~50 calls/frame in the capture run.
        shadow: false,
      });
      this.segs.push(seg);
      this.group.add(seg);
    }
    scene.add(this.group);
  }
  /** Drive the head to (x,y,z) facing yaw/pitch; body eases along the trail. */
  update(x: number, y: number, z: number, yaw: number, pitch: number, dt: number): void {
    this.t += dt;
    this.head.position.set(x, y, z);
    this.head.rotation.set(pitch, yaw, Math.sin(this.t * 3) * 0.12, "YXZ");
    for (const e of this.eyeMats) e.emissiveIntensity = 2.2 + Math.sin(this.t * 6) * 0.8;
    // push a new trail sample whenever the head has moved a full spacing step
    const hp = this.head.position;
    if (this.trail.length === 0 || this.trail[0].distanceTo(hp) >= this.spacing * 0.5) {
      this.trail.unshift(hp.clone());
      const maxLen = this.segs.length * 2 + 4;
      if (this.trail.length > maxLen) this.trail.length = maxLen;
    }
    // place each body segment at a fixed arc-length behind the head
    let segIdx = 0;
    const want = this.spacing;
    let acc = 0;
    for (let i = 1; i < this.trail.length && segIdx < this.segs.length; i++) {
      acc += this.trail[i].distanceTo(this.trail[i - 1]);
      while (acc >= want * (segIdx + 1) && segIdx < this.segs.length) {
        const seg = this.segs[segIdx];
        seg.position.copy(this.trail[i]);
        seg.lookAt(this.trail[i - 1]);
        // gentle serpentine sway so the ribbon undulates
        seg.position.y += Math.sin(this.t * 4 - segIdx * 0.5) * 0.25;
        // BANK ROLL (2026-08-01): the wave that travels down the body now also
        // ROLLS each segment about its own axis, a quarter-phase behind the
        // vertical sway. Before this the dorsal fin stayed rigidly world-up
        // through every undulation, which is what made a flying serpent read as
        // a string of beads. rotateZ is post-lookAt and local, so it composes
        // with the orientation instead of fighting it.
        seg.rotateZ(Math.sin(this.t * 4 - segIdx * 0.5 - 1.57) * 0.34);
        segIdx++;
      }
    }
    // any leftover segments (trail still filling) stack at the tail-most point
    const last = this.trail[this.trail.length - 1] ?? hp;
    for (; segIdx < this.segs.length; segIdx++) this.segs[segIdx].position.copy(last);
  }
  /** World position of the mouth, for spawning beams. */
  mouthWorld(out: THREE.Vector3): THREE.Vector3 {
    return this.head.localToWorld(out.set(0, 0, 1.6));
  }
  /** Hide/show without disposing — the dragon re-lights after a respawn timer,
   *  so the ~50-mesh build must not repeat mid-match. Hiding clears the trail
   *  so the next appearance unfolds from the new spot instead of sweeping the
   *  body across the map from the old one. */
  setVisible(v: boolean): void {
    this.group.visible = v;
    if (!v) this.trail.length = 0;
  }
  dispose(scene: THREE.Scene): void { scene.remove(this.group); disposeGroup(this.group); }
}

/** A vehicle rocket in flight — glowing warhead + smoke-ish trail. */
export class RocketMesh {
  readonly group = new THREE.Group();
  private light: THREE.PointLight | null;

  constructor(scene: THREE.Scene) {
    const body = new THREE.Mesh(
      new THREE.CylinderGeometry(0.16, 0.16, 0.8, 8),
      new THREE.MeshStandardMaterial({ color: "#d8d8d8", metalness: 0.6, roughness: 0.4 }),
    );
    body.rotation.x = Math.PI / 2;
    const tip = new THREE.Mesh(new THREE.ConeGeometry(0.16, 0.3, 8), new THREE.MeshStandardMaterial({ color: "#c23a2a" }));
    tip.rotation.x = Math.PI / 2; tip.position.z = -0.55;
    // EXHAUST PLUME (2026-08-01): was one flat additive sphere stuck on the
    // tail — a glowing ball, with no direction and no length, on an object whose
    // entire read is "this thing is going that way fast". Now the fire-puff
    // texture on two quads crossed about the flight axis, stretched back along
    // it: a plume with a hot root, a torn edge and actual extent. Crossed quads
    // need no billboarding, so `update()` stays what it was.
    const plumeGeo = new THREE.PlaneGeometry(0.46, 1.15);
    plumeGeo.translate(0, -0.5, 0); // pivot at the nozzle, plume trails behind
    const plumeMat = new THREE.MeshBasicMaterial({
      color: "#ffd9a0", map: firePuffTexture(), transparent: true, opacity: 0.85,
      blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide,
    });
    const plumeA = new THREE.Mesh(plumeGeo, plumeMat);
    plumeA.rotation.x = -Math.PI / 2; // quad's -Y -> +Z (astern)
    plumeA.position.z = 0.42;
    const plumeB = new THREE.Mesh(plumeGeo, plumeMat);
    plumeB.rotation.x = -Math.PI / 2;
    plumeB.rotation.z = Math.PI / 2;
    plumeB.position.z = 0.42;
    const flame = new THREE.Mesh(new THREE.SphereGeometry(0.14, 8, 8), new THREE.MeshBasicMaterial({ color: "#ffe3b0", transparent: true, opacity: 0.9, blending: THREE.AdditiveBlending }));
    flame.position.z = 0.42;
    this.group.add(body, tip, flame, plumeA, plumeB);
    this.light = lowSpec() ? null : acquireFxLight(scene);
    if (this.light) { this.light.color.set("#ffa030"); this.light.intensity = 3; this.light.distance = 7; this.group.add(this.light); }
    scene.add(this.group);
  }

  private sx = NaN; private sy = NaN; private sz = NaN;
  private extraT = 0;
  update(x: number, y: number, z: number, vx: number, vy: number, vz: number, dt: number, capSec = 0.05, gravity = 0): void {
    // Inter-patch extrapolation — same treatment as KnifeMesh.update: a rocket
    // at ~60 m/s steps a full metre per 30 Hz patch without it. GRAVITY-AWARE
    // (see KnifeMesh.update): a rocket's arc is shallow (ROCKET.gravity = 6) but
    // the −½·g·t² term still keeps the drop on the true parabola. `capSec` is
    // the adaptive extrapolation cap (see KnifeMesh.update).
    if (x !== this.sx || y !== this.sy || z !== this.sz) {
      this.sx = x; this.sy = y; this.sz = z;
      this.extraT = 0;
      this.group.position.set(x, y, z);
    } else if (this.extraT < capSec) {
      this.extraT = Math.min(capSec, this.extraT + dt);
      const t = this.extraT;
      this.group.position.set(this.sx + vx * t, this.sy + vy * t - 0.5 * gravity * t * t, this.sz + vz * t);
    }
    const len = Math.hypot(vx, vy, vz) || 1;
    const p = this.group.position;
    this.group.lookAt(p.x + vx / len, p.y + vy / len, p.z + vz / len);
  }

  dispose(scene: THREE.Scene): void {
    releaseFxLight(scene, this.light);
    this.light = null;
    scene.remove(this.group);
    disposeGroup(this.group);
  }
}

// Scratch for InstancedMesh matrix composition — hoisted so the per-frame spark
// update allocates nothing (invariant 6). Reused synchronously within a single
// update call, so concurrent bursts never interleave through them.
const _burstPos = new THREE.Vector3();
const _burstQuat = new THREE.Quaternion();
const _burstScale = new THREE.Vector3();
const _burstEuler = new THREE.Euler();
const _burstMat = new THREE.Matrix4();
// Billboard basis scratch (see `orientBillboard`) — hoisted for invariant 6.
const _bbUp = new THREE.Vector3();
const _bbRight = new THREE.Vector3();
const _bbFwd = new THREE.Vector3();
const _bbView = new THREE.Vector3();
const _bbCam = new THREE.Vector3();

/** The camera every FX billboard turns to face. Set once per session by
 *  `match.ts` right after the renderer is built; null until then, in which case
 *  billboards fall back to a world-axis quad (the pre-2026-08-01 behaviour). */
let fxCamera: THREE.Camera | null = null;
export function setFxCamera(cam: THREE.Camera | null): void { fxCamera = cam; }

/**
 * Write `_burstMat` as a camera-facing quad at (px,py,pz) whose local +Y runs
 * along (ax,ay,az) — the particle's velocity — scaled `sx` across and `sy`
 * along. `axisLen` is the length of that axis vector (0 => no preferred axis,
 * use a pure screen-aligned billboard with `roll` radians of spin).
 *
 * Why this exists (2026-08-01, the combat-FX pass): every particle in the game
 * was a `PlaneGeometry` left at its default orientation, i.e. permanently facing
 * WORLD +Z. Strafe around a kill burst and its sparks turn edge-on and vanish;
 * an explosion seen from the side was a line of invisible confetti. Facing the
 * camera is not a stylistic choice for an alpha-blended particle, it is the
 * whole contract. The velocity axis on top of that gives the streak: a spark
 * stretched along its own motion is a motion blur the GPU renders for free.
 *
 * Allocation-free: all six vectors are module scratch, reused synchronously
 * inside one update call.
 */
function orientBillboard(
  px: number, py: number, pz: number,
  ax: number, ay: number, az: number, axisLen: number,
  sx: number, sy: number, roll: number,
): void {
  const cam = fxCamera;
  if (cam) {
    cam.getWorldPosition(_bbCam);
    _bbView.set(_bbCam.x - px, _bbCam.y - py, _bbCam.z - pz);
    if (_bbView.lengthSq() < 1e-8) _bbView.set(0, 0, 1); else _bbView.normalize();
  } else {
    _bbView.set(0, 0, 1);
  }
  if (axisLen > 1e-5) {
    _bbUp.set(ax / axisLen, ay / axisLen, az / axisLen);
  } else {
    // no motion axis: spin the quad in screen space by `roll`
    _bbRight.set(1, 0, 0);
    if (Math.abs(_bbView.y) < 0.99) _bbUp.crossVectors(_bbView, _bbRight).normalize();
    else _bbUp.set(1, 0, 0);
    _bbRight.crossVectors(_bbUp, _bbView).normalize();
    const c = Math.cos(roll), s = Math.sin(roll);
    // rotate (right, up) within the screen plane
    const rx = _bbRight.x * c + _bbUp.x * s, ry = _bbRight.y * c + _bbUp.y * s, rz = _bbRight.z * c + _bbUp.z * s;
    const ux = _bbUp.x * c - _bbRight.x * s, uy = _bbUp.y * c - _bbRight.y * s, uz = _bbUp.z * c - _bbRight.z * s;
    _bbRight.set(rx, ry, rz);
    _bbUp.set(ux, uy, uz);
    _bbFwd.copy(_bbView);
    _burstMat.makeBasis(_bbRight, _bbUp, _bbFwd);
    _burstScale.set(sx, sy, 1);
    _burstMat.scale(_burstScale);
    _burstMat.setPosition(px, py, pz);
    return;
  }
  _bbRight.crossVectors(_bbUp, _bbView);
  if (_bbRight.lengthSq() < 1e-8) {
    // motion is straight at/away from the camera — any perpendicular will do
    _bbRight.set(_bbUp.y, -_bbUp.x, 0);
    if (_bbRight.lengthSq() < 1e-8) _bbRight.set(1, 0, 0);
  }
  _bbRight.normalize();
  _bbFwd.crossVectors(_bbRight, _bbUp);
  _burstMat.makeBasis(_bbRight, _bbUp, _bbFwd);
  _burstScale.set(sx, sy, 1);
  _burstMat.scale(_burstScale);
  _burstMat.setPosition(px, py, pz);
}

/** A quick PARTICLE BURST (sparkles, shards, dust) — pure juice, auto-fades.
 *  Returns an updater like spawnExplosion: call each frame until it's true.
 *
 *  `soft` picks the DUST variant: a normal-blended smoke puff that grows and
 *  drags to a stop, instead of an additive velocity-streaked ember. Landing
 *  dust, hoof kick-up and skid grit are dust, not sparks, and drawing them
 *  additive made them glow. */
export function spawnBurst(
  scene: THREE.Scene, x: number, y: number, z: number,
  color: string, n = 10, speed = 4, up = 3, life = 0.55, size = 0.14,
  soft = false,
  /** EMISSION AXIS — the cone the particles are thrown along, default straight
   *  up. Bullet impacts pass the surface normal they just measured (decals.ts),
   *  so debris blows back OUT of a wall or DOWN off a ceiling instead of always
   *  puffing skyward; that upward puff was the tell that the impact FX had no
   *  idea what it had hit. The `up` argument becomes speed ALONG this axis and
   *  `speed` the spread across it, so every existing call is unchanged when the
   *  axis is (0,1,0). */
  axX = 0, axY = 1, axZ = 0,
): (dt: number) => boolean {
  // cost ladder: struggling machines get the same burst with fewer sparks
  const lvl = gfxLevel();
  if (lvl >= 1) n = Math.max(3, Math.round(n * (lvl >= 2 ? 0.35 : 0.6)));
  const mat = soft
    ? new THREE.MeshBasicMaterial({ color, map: smokePuffTexture(), transparent: true, opacity: 0.8, depthWrite: false, side: THREE.DoubleSide })
    : new THREE.MeshBasicMaterial({ color, map: emberTexture(), transparent: true, opacity: 0.95, blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide });
  // ONE InstancedMesh instead of n separate Meshes. A burst used to be n draw
  // calls AND n scene.add/remove — and bursts are the highest-frequency combat
  // FX (every kill/hit/pickup), with portal/dragon cinematics stacking several
  // per frame. Instancing collapses each burst to a single draw call; the spark
  // motion is byte-identical, just written into an instance matrix. The
  // per-instance kinematics live in flat typed arrays (no per-spark object).
  const im = new THREE.InstancedMesh(burstGeo, mat, n);
  im.instanceMatrix.setUsage(THREE.DynamicDrawUsage); // updated every frame
  // Instances spread from the origin under gravity while the InstancedMesh
  // itself stays at (0,0,0), so per-instance frustum culling (which tests the
  // geometry sphere at the mesh origin) would mis-cull. Bursts are tiny and
  // last <0.7 s, so skip culling rather than cull wrongly.
  im.frustumCulled = false;
  const px = new Float32Array(n), py = new Float32Array(n), pz = new Float32Array(n);
  const vx = new Float32Array(n), vy = new Float32Array(n), vz = new Float32Array(n);
  const rot = new Float32Array(n);
  const jit = new Float32Array(n); // per-particle size jitter (dust reads dead uniform without it)
  // orthonormal basis around the emission axis (the default axis reproduces the
  // original world-XZ spread + world-Y lift exactly)
  let axl = Math.hypot(axX, axY, axZ);
  if (axl < 1e-6) { axX = 0; axY = 1; axZ = 0; axl = 1; }
  const anx = axX / axl, any = axY / axl, anz = axZ / axl;
  let t0x = -anz, t0y = 0, t0z = anx; // axis × world-up, i.e. horizontal tangent
  let t0l = Math.hypot(t0x, t0y, t0z);
  if (t0l < 1e-6) { t0x = 1; t0y = 0; t0z = 0; t0l = 1; } // axis IS world-up
  t0x /= t0l; t0y /= t0l; t0z /= t0l;
  const t1x = any * t0z - anz * t0y, t1y = anz * t0x - anx * t0z, t1z = anx * t0y - any * t0x;
  for (let i = 0; i < n; i++) {
    px[i] = x; py[i] = y; pz[i] = z;
    const a = Math.random() * Math.PI * 2;
    const sp = speed * (0.4 + Math.random() * 0.8);
    const cs = Math.cos(a) * sp, sn = Math.sin(a) * sp;
    const al = up * (0.4 + Math.random());
    vx[i] = t0x * cs + t1x * sn + anx * al;
    vy[i] = t0y * cs + t1y * sn + any * al;
    vz[i] = t0z * cs + t1z * sn + anz * al;
    rot[i] = Math.random() * Math.PI * 2;
    jit[i] = 0.7 + Math.random() * 0.75;
    orientBillboard(x, y, z, 0, 0, 0, 0, size * jit[i], size * jit[i], rot[i]);
    im.setMatrixAt(i, _burstMat);
  }
  im.instanceMatrix.needsUpdate = true;
  scene.add(im);
  let t = 0;
  return (dt: number): boolean => {
    t += dt;
    const k = t / life;
    // dust puffs BALLOON as they slow; sparks keep their size and streak
    const grow = soft ? 1 + k * 1.9 : 1;
    for (let i = 0; i < n; i++) {
      if (soft) {
        // aerodynamic drag: a dust puff throws out fast and stops, it does not
        // fly a clean parabola like a spark does
        const drag = Math.max(0, 1 - 3.4 * dt);
        vx[i] *= drag; vz[i] *= drag; vy[i] = vy[i] * drag - 1.6 * dt;
      } else {
        vy[i] -= 9 * dt;
      }
      px[i] += vx[i] * dt; py[i] += vy[i] * dt; pz[i] += vz[i] * dt;
      rot[i] += dt * (soft ? 0.9 : 6);
      const s = size * jit[i] * grow;
      if (soft) {
        orientBillboard(px[i], py[i], pz[i], 0, 0, 0, 0, s, s, rot[i]);
      } else {
        // stretch along velocity: 1x at rest, up to ~3.2x at high speed. This
        // is the motion streak — a still spark stays a round ember.
        const spd = Math.hypot(vx[i], vy[i], vz[i]);
        orientBillboard(px[i], py[i], pz[i], vx[i], vy[i], vz[i], spd,
          s, s * Math.min(3.2, 1 + spd * 0.16), 0);
      }
      im.setMatrixAt(i, _burstMat);
    }
    im.instanceMatrix.needsUpdate = true;
    mat.opacity = soft
      ? Math.max(0, 0.8 * Math.min(1, k * 6) * (1 - k * k))
      : Math.max(0, 0.95 * (1 - k * k));
    if (t >= life) {
      scene.remove(im);
      mat.dispose(); // geometry is the shared burstGeo — never disposed
      im.dispose(); // frees the per-instance matrix buffer
      return true;
    }
    return false;
  };
}

/** Sandworm acid vomit: a thick green-brown gicle with ropes, blobs, and splat.
 *  It intentionally avoids the explosion helper so it reads as liquid. */
export function spawnWormVomitGicle(
  scene: THREE.Scene,
  ox: number, oy: number, oz: number,
  tx: number, ty: number, tz: number,
): (dt: number) => boolean {
  const start = new THREE.Vector3(ox, oy, oz);
  const hit = new THREE.Vector3(tx, ty, tz);
  const delta = new THREE.Vector3().subVectors(hit, start);
  const len = Math.max(1, delta.length());
  const dir = delta.clone().multiplyScalar(1 / len);
  const up = new THREE.Vector3(0, 1, 0);
  const right = new THREE.Vector3().crossVectors(dir, up);
  if (right.lengthSq() < 0.001) right.set(1, 0, 0);
  else right.normalize();
  const lift = new THREE.Vector3().crossVectors(right, dir).normalize();
  const cheap = gfxLevel() >= 1 || lowSpec();
  const group = new THREE.Group();
  scene.add(group);

  const geos = new Set<THREE.BufferGeometry>();
  const mats = new Set<THREE.Material>();
  const addMesh = (mesh: THREE.Mesh): void => {
    group.add(mesh);
    geos.add(mesh.geometry);
    const material = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    for (const m of material) mats.add(m);
  };
  const makeMat = (color: string, opacity: number): THREE.MeshBasicMaterial => {
    const m = new THREE.MeshBasicMaterial({ color, transparent: true, opacity, depthWrite: false, side: THREE.DoubleSide });
    mats.add(m);
    return m;
  };

  const colors = ["#b7d73a", "#7da51f", "#d7ef55", "#5e7d18", "#b9972a"];
  const ropes: { mesh: THREE.Mesh; mat: THREE.MeshBasicMaterial; wobble: number }[] = [];
  const arc = Math.min(8, len * 0.13);
  for (let i = 0, n = cheap ? 4 : 8; i < n; i++) {
    const side = (Math.random() - 0.5) * (1.1 + len * 0.035);
    const end = hit.clone()
      .addScaledVector(right, side * (1.6 + Math.random() * 0.9))
      .addScaledVector(lift, (Math.random() - 0.5) * 0.9)
      .addScaledVector(dir, -Math.random() * 2.4);
    const p1 = start.clone().lerp(end, 0.28).addScaledVector(right, side * 0.55).addScaledVector(up, arc * (0.8 + Math.random() * 0.45));
    const p2 = start.clone().lerp(end, 0.64).addScaledVector(right, side * 0.9).addScaledVector(up, arc * (0.35 + Math.random() * 0.35));
    const geo = new THREE.TubeGeometry(new THREE.CatmullRomCurve3([start, p1, p2, end]), cheap ? 8 : 14, 0.09 + Math.random() * 0.13, cheap ? 4 : 6, false);
    const m = makeMat(colors[i % colors.length], 0.82);
    const mesh = new THREE.Mesh(geo, m);
    addMesh(mesh);
    ropes.push({ mesh, mat: m, wobble: Math.random() * Math.PI * 2 });
  }

  const blobGeo = new THREE.IcosahedronGeometry(1, cheap ? 0 : 1);
  geos.add(blobGeo);
  const groundY = Math.max(0.06, hit.y + 0.035);
  // The gicle blobs used to be `n` separate THREE.Mesh (up to 62) sharing
  // blobGeo but each with its own per-color material — up to 62 draw calls in a
  // single worm-attack frame. They are now ONE THREE.InstancedMesh: per-blob
  // colour rides `instanceColor` (setColorAt), per-blob kinematics live in flat
  // Float32Arrays written into the instance matrix each frame (no per-blob
  // object, invariant 6). instanced+instanceColor+MeshBasic+fog is warmed by
  // renderer.ts ensureWarmGroup (bench-warm-vomit-blobs.mjs), so no cold compile.
  const nBlobs = cheap ? 28 : 62;
  const blobMat = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.92, depthWrite: false });
  mats.add(blobMat);
  const blobIM = new THREE.InstancedMesh(blobGeo, blobMat, nBlobs);
  blobIM.instanceMatrix.setUsage(THREE.DynamicDrawUsage); // updated every frame
  blobIM.frustumCulled = false; // instances spread near `hit`; the mesh sits at origin
  group.add(blobIM);
  const bpx = new Float32Array(nBlobs), bpy = new Float32Array(nBlobs), bpz = new Float32Array(nBlobs);
  const bvx = new Float32Array(nBlobs), bvy = new Float32Array(nBlobs), bvz = new Float32Array(nBlobs);
  const brad = new Float32Array(nBlobs), brx = new Float32Array(nBlobs), brz = new Float32Array(nBlobs);
  const bsx = new Float32Array(nBlobs), bsy = new Float32Array(nBlobs), bsz = new Float32Array(nBlobs);
  const blanded = new Uint8Array(nBlobs);
  const _blobColor = new THREE.Color();
  for (let i = 0; i < nBlobs; i++) {
    const k = Math.random();
    const p = start.clone().lerp(hit, k)
      .addScaledVector(up, Math.sin(k * Math.PI) * arc + (Math.random() - 0.4) * 0.8)
      .addScaledVector(right, (Math.random() - 0.5) * (0.7 + len * 0.035))
      .addScaledVector(lift, (Math.random() - 0.5) * 0.8);
    const r = 0.07 + Math.pow(Math.random(), 1.9) * 0.34;
    bpx[i] = p.x; bpy[i] = p.y; bpz[i] = p.z;
    brad[i] = r; bsx[i] = r; bsy[i] = r; bsz[i] = r;
    const v = dir.clone().multiplyScalar((11 + Math.random() * 19) * (0.45 + k * 0.8))
      .addScaledVector(right, (Math.random() - 0.5) * 9)
      .addScaledVector(lift, (Math.random() - 0.5) * 5);
    v.y += 5 + Math.random() * 7 - k * 8;
    bvx[i] = v.x; bvy[i] = v.y; bvz[i] = v.z;
    _burstPos.set(p.x, p.y, p.z);
    _burstQuat.identity();
    _burstScale.setScalar(r);
    _burstMat.compose(_burstPos, _burstQuat, _burstScale);
    blobIM.setMatrixAt(i, _burstMat);
    blobIM.setColorAt(i, _blobColor.set(colors[(i + 1) % colors.length]));
  }
  blobIM.instanceMatrix.needsUpdate = true;
  if (blobIM.instanceColor) blobIM.instanceColor.needsUpdate = true;

  const puddleGeo = new THREE.CircleGeometry(1, cheap ? 16 : 28);
  geos.add(puddleGeo);
  const splats: { mesh: THREE.Mesh; mat: THREE.MeshBasicMaterial; sx: number; sy: number }[] = [];
  for (let i = 0, n = cheap ? 5 : 10; i < n; i++) {
    const m = makeMat(colors[(i + 2) % colors.length], 0);
    const mesh = new THREE.Mesh(puddleGeo, m);
    mesh.rotation.x = -Math.PI / 2;
    mesh.rotation.z = Math.random() * Math.PI * 2;
    mesh.position.copy(hit)
      .addScaledVector(right, (Math.random() - 0.5) * (1.5 + i * 0.32))
      .addScaledVector(dir, (Math.random() - 0.5) * (1.2 + i * 0.22));
    mesh.position.y = groundY + i * 0.004;
    mesh.scale.setScalar(0.001);
    addMesh(mesh);
    splats.push({ mesh, mat: m, sx: 0.9 + Math.random() * 2.3, sy: 0.35 + Math.random() * 1.0 });
  }

  // pooled — a raw `new PointLight` per vomit changed the scene light count
  // twice per worm attack (add + remove = two full shader recompiles)
  const light = cheap ? null : acquireFxLight(scene);
  if (light) {
    light.color.set("#b7ff2a");
    light.distance = 10;
    light.intensity = 1.2;
    light.position.copy(start).lerp(hit, 0.55).addScaledVector(up, 1.5);
  }

  let age = 0;
  return (dt: number): boolean => {
    age += dt;
    const ropeFade = age < 0.48 ? 1 : Math.max(0, 1 - (age - 0.48) / 0.62);
    for (const r of ropes) {
      r.mat.opacity = 0.82 * ropeFade;
      r.mesh.rotation.z = Math.sin(age * 18 + r.wobble) * 0.012;
    }
    const dropFade = age < 1.05 ? 1 : Math.max(0, 1 - (age - 1.05) / 0.55);
    for (let i = 0; i < nBlobs; i++) {
      const r = brad[i];
      if (!blanded[i]) {
        bvy[i] -= 22 * dt;
        bpx[i] += bvx[i] * dt; bpy[i] += bvy[i] * dt; bpz[i] += bvz[i] * dt;
        brx[i] += dt * (4 + r * 9);
        brz[i] += dt * (5 + r * 8);
        if (bpy[i] <= groundY + r * 0.16) {
          blanded[i] = 1;
          bpy[i] = groundY + 0.025;
          bsx[i] = r * 2.0; bsy[i] = r * 0.18; bsz[i] = r * 1.55;
        }
      } else {
        bsx[i] = Math.min(r * 2.8, bsx[i] + dt * r * 1.4);
        bsz[i] = Math.min(r * 2.3, bsz[i] + dt * r * 1.1);
      }
      _burstPos.set(bpx[i], bpy[i], bpz[i]);
      _burstEuler.set(brx[i], 0, brz[i]);
      _burstQuat.setFromEuler(_burstEuler);
      _burstScale.set(bsx[i], bsy[i], bsz[i]);
      _burstMat.compose(_burstPos, _burstQuat, _burstScale);
      blobIM.setMatrixAt(i, _burstMat);
    }
    blobIM.instanceMatrix.needsUpdate = true;
    blobMat.opacity = 0.92 * dropFade; // uniform across all blobs
    const grow = Math.min(1, age / 0.18);
    const splatFade = Math.min(1, age / 0.14) * (age < 1.25 ? 1 : Math.max(0, 1 - (age - 1.25) / 0.6));
    for (const s of splats) {
      s.mesh.scale.set(s.sx * grow, s.sy * grow, 1);
      s.mat.opacity = 0.62 * splatFade;
    }
    if (light) light.intensity = Math.max(0, 1.2 * (1 - age / 1.85));
    if (age < 1.85) return false;
    releaseFxLight(scene, light);
    scene.remove(group);
    blobIM.dispose(); // frees the instanceMatrix / instanceColor GPU buffers
    group.clear();
    for (const g of geos) g.dispose();
    for (const m of mats) m.dispose();
    return true;
  };
}

/** A brief expanding explosion sphere + light. `big` for rockets. */
// Explosion lights come from a FIXED pool created up front: adding/removing
// lights changes the scene's light count, which forces three.js to recompile
// every shader — a guaranteed frame hitch exactly when a bomb goes off.
// Pool lights live in the scene permanently at intensity 0.
const lightPool: THREE.PointLight[] = [];
let lightPoolScene: THREE.Scene | null = null;
// (the old explosion `ballGeo` sphere is gone — spawnExplosion draws puff
//  billboards on the shared `burstGeo` quad since 2026-08-01)

/** Call once at map load so the light-count shader compile happens there. */
export function prewarmExplosions(scene: THREE.Scene): void {
  // scene-only guard: an empty array means all 3 are borrowed — rebuilding
  // then would grow the light count (the recompile this pool prevents)
  if (lightPoolScene === scene) return;
  lightPool.length = 0;
  lightPoolScene = scene;
  for (let i = 0; i < 3; i++) {
    const l = new THREE.PointLight("#ff8a2a", 0, lowSpec() ? 0.01 : 24, 2);
    scene.add(l);
    lightPool.push(l);
  }
}

// shared per-module geometries — one alloc ever, never disposed (an aura ring
// per arena kill + 14 debris boxes per mega used to leak their geometries)
const auraRingGeo = new THREE.RingGeometry(0.72, 1, 48);
auraRingGeo.userData.shared = true;
const megaRingGeo = new THREE.RingGeometry(0.8, 1.35, 40);
megaRingGeo.userData.shared = true;
const debrisGeo = new THREE.BoxGeometry(0.35, 0.35, 0.35);
debrisGeo.userData.shared = true;
/** Shared drift-smoke puff (r=0.2 — per-puff size comes from mesh scale). */
const skidPuffGeo = new THREE.SphereGeometry(0.2, 6, 5);
skidPuffGeo.userData.shared = true;
/** Shared spark-burst quad. spawnBurst is the highest-frequency combat FX (hit
 *  sparks, kills, pickups, door breaches, dust) and used to allocate a fresh
 *  PlaneGeometry per call — a GL buffer upload+free every burst, i.e. churn
 *  during exactly the firefights where a hitch is felt. The heavier
 *  spawnExplosion already shares its geometry (ballGeo/debrisGeo); this brings
 *  spawnBurst into line. Unit quad — per-spark `size` comes from mesh scale. */
const burstGeo = new THREE.PlaneGeometry(1, 1);
burstGeo.userData.shared = true;

export function spawnExplosion(scene: THREE.Scene, x: number, y: number, z: number, big = false, mega = false, neon?: string, aura = 0): (dt: number) => boolean {
  const scale = mega ? 6 : big ? 2.4 : 1;
  // MEGA (truck detonation): an expanding ground shockwave ring + debris chunks
  const extras: { obj: THREE.Object3D; mat: THREE.Material }[] = [];
  // MEGA debris: ONE InstancedMesh (14 chunks) instead of 14 separate Mesh
  // draw calls. Per-chunk kinematics live in flat typed arrays (no per-chunk
  // object), written into instance matrices — motion byte-identical to the old
  // per-mesh loop. The chunk material is OPAQUE MeshBasic (no transparent /
  // NormalBlending), a DISTINCT program from the additive spark burst — `opaque`
  // is program-cache-key layer 17 in three r172. ensureWarmGroup() warms this
  // exact variant (opaque instanced MeshBasic, `warmInstOpaque`) so the first
  // mega blast does not cold-compile it mid-fight. Allocated only when `mega`.
  const DEBRIS_N = 14;
  let debrisIM: THREE.InstancedMesh | null = null;
  let debrisMat: THREE.MeshBasicMaterial | null = null;
  let dpx!: Float32Array, dpy!: Float32Array, dpz!: Float32Array;
  let dvx!: Float32Array, dvy!: Float32Array, dvz!: Float32Array;
  let drx!: Float32Array, drz!: Float32Array;
  // ARENA: the kill AURA — an expanding ground ring in the THROWER'S neon so
  // everyone reads whose bomb owned that zone
  let auraRing: THREE.Mesh | null = null;
  let auraMat: THREE.MeshBasicMaterial | null = null;
  if (neon && aura > 0) {
    auraMat = new THREE.MeshBasicMaterial({ color: neon, map: shockRingTexture(), transparent: true, opacity: 0.75, side: THREE.DoubleSide, blending: THREE.AdditiveBlending, depthWrite: false });
    auraRing = new THREE.Mesh(auraRingGeo, auraMat);
    auraRing.rotation.x = -Math.PI / 2;
    auraRing.position.set(x, Math.max(0.35, y - 0.6), z);
    scene.add(auraRing);
    extras.push({ obj: auraRing, mat: auraMat });
  }
  if (mega) {
    const ringMat = new THREE.MeshBasicMaterial({ color: "#ffd9a0", map: shockRingTexture(), transparent: true, opacity: 0.9, side: THREE.DoubleSide, blending: THREE.AdditiveBlending, depthWrite: false });
    const ring = new THREE.Mesh(megaRingGeo, ringMat);
    ring.rotation.x = -Math.PI / 2;
    ring.position.set(x, Math.max(0.4, y - 1), z);
    scene.add(ring);
    extras.push({ obj: ring, mat: ringMat });
    // charred rubble with hot seams, not flat orange dice (2026-08-01)
    debrisMat = new THREE.MeshBasicMaterial({ color: "#c9b8ac", map: debrisTexture() });
    debrisIM = new THREE.InstancedMesh(debrisGeo, debrisMat, DEBRIS_N);
    debrisIM.instanceMatrix.setUsage(THREE.DynamicDrawUsage); // updated every frame
    debrisIM.frustumCulled = false; // chunks fly out from the origin; mesh stays at (0,0,0)
    dpx = new Float32Array(DEBRIS_N); dpy = new Float32Array(DEBRIS_N); dpz = new Float32Array(DEBRIS_N);
    dvx = new Float32Array(DEBRIS_N); dvy = new Float32Array(DEBRIS_N); dvz = new Float32Array(DEBRIS_N);
    drx = new Float32Array(DEBRIS_N); drz = new Float32Array(DEBRIS_N);
    _burstScale.setScalar(1); // debrisGeo already carries the 0.35 chunk size
    for (let i = 0; i < DEBRIS_N; i++) {
      dpx[i] = x; dpy[i] = y; dpz[i] = z;
      const a = (i / DEBRIS_N) * Math.PI * 2;
      dvx[i] = Math.cos(a) * (6 + (i % 4) * 3);
      dvy[i] = 9 + (i % 5) * 3;
      dvz[i] = Math.sin(a) * (6 + ((i + 2) % 4) * 3);
      _burstPos.set(x, y, z);
      _burstQuat.identity();
      _burstMat.compose(_burstPos, _burstQuat, _burstScale);
      debrisIM.setMatrixAt(i, _burstMat);
    }
    debrisIM.instanceMatrix.needsUpdate = true;
    scene.add(debrisIM);
  }
  // THE FIREBALL, 2026-08-01. Was: one additive `ballGeo` sphere scaled 18x/s
  // plus (for `big`) a second flat-grey sphere. A constant-colour sphere is a
  // perfect circle at every size and every angle — it read as an expanding
  // balloon, and the "smoke" as a grey balloon inside it.
  //
  // Now: two clouds of camera-facing textured PUFFS, each with its own outward
  // velocity, drag, buoyancy, spin and size curve, plus (for big/mega) a shower
  // of velocity-streaked embers. Three InstancedMeshes = three draw calls, the
  // same order as the two spheres it replaces, and the puff counts ride the
  // same cost ladder as spawnBurst so a struggling machine gets a thinner blast
  // rather than a different one.
  const lvl = gfxLevel();
  const thin = (v: number): number => Math.max(2, Math.round(v * (lvl >= 2 ? 0.35 : lvl >= 1 ? 0.6 : 1)));
  const FIRE_N = thin(mega ? 16 : big ? 10 : 6);
  const SMOKE_N = thin(mega ? 18 : big ? 11 : 5);
  const EMBER_N = big || mega ? thin(mega ? 26 : 16) : 0;
  const fireLife = mega ? 0.9 : big ? 0.55 : 0.36;
  const smokeLife = mega ? 2.6 : big ? 1.7 : 1.05;
  const emberLife = mega ? 1.35 : 0.95;

  /** Per-puff kinematics in flat typed arrays — no per-particle object. */
  const cloud = (n: number, mat: THREE.MeshBasicMaterial, spread: number, rise: number) => {
    const im = new THREE.InstancedMesh(burstGeo, mat, n);
    im.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    im.frustumCulled = false; // puffs fly out from the origin; the mesh stays at (0,0,0)
    const cpx = new Float32Array(n), cpy = new Float32Array(n), cpz = new Float32Array(n);
    const cvx = new Float32Array(n), cvy = new Float32Array(n), cvz = new Float32Array(n);
    const crot = new Float32Array(n), cspin = new Float32Array(n), cjit = new Float32Array(n);
    for (let i = 0; i < n; i++) {
      // even-ish spherical spread (golden-angle in azimuth, stratified in cos)
      const u = (i + 0.5) / n * 2 - 1;
      const az = i * 2.399963;
      const s = Math.sqrt(Math.max(0, 1 - u * u));
      const sp = spread * (0.45 + Math.random() * 0.85);
      cpx[i] = x; cpy[i] = y; cpz[i] = z;
      cvx[i] = Math.cos(az) * s * sp;
      cvy[i] = u * sp * 0.75 + rise;
      cvz[i] = Math.sin(az) * s * sp;
      crot[i] = Math.random() * Math.PI * 2;
      cspin[i] = (Math.random() - 0.5) * 2.4;
      cjit[i] = 0.72 + Math.random() * 0.7;
      orientBillboard(x, y, z, 0, 0, 0, 0, 0.001, 0.001, crot[i]);
      im.setMatrixAt(i, _burstMat);
    }
    im.instanceMatrix.needsUpdate = true;
    scene.add(im);
    return { im, mat, cpx, cpy, cpz, cvx, cvy, cvz, crot, cspin, cjit, n };
  };

  const fireMat = new THREE.MeshBasicMaterial({
    color: neon ?? "#ffffff", map: firePuffTexture(), transparent: true,
    opacity: 1, blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide,
  });
  // outward fast, barely any lift — a fireball's first 100 ms is pressure, not heat
  const fire = cloud(FIRE_N, fireMat, mega ? 20 : big ? 7.6 : 4.0, mega ? 3 : big ? 1.2 : 0.6);
  const smokeMat = new THREE.MeshBasicMaterial({
    color: neon ? "#6b6b78" : "#5a5048", map: smokePuffTexture(), transparent: true,
    opacity: 0, depthWrite: false, side: THREE.DoubleSide,
  });
  // slower, and it KEEPS rising after the fire is out — that lift is what makes
  // the column read as hot gas instead of a dirty sphere
  const smoke = cloud(SMOKE_N, smokeMat, mega ? 12 : big ? 4.4 : 2.3, mega ? 4 : big ? 2 : 1.2);
  let emberMat: THREE.MeshBasicMaterial | null = null;
  let ember: ReturnType<typeof cloud> | null = null;
  if (EMBER_N > 0) {
    emberMat = new THREE.MeshBasicMaterial({
      color: neon ?? "#ffd39a", map: emberTexture(), transparent: true,
      opacity: 0.95, blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide,
    });
    ember = cloud(EMBER_N, emberMat, mega ? 26 : 12, mega ? 9 : 5);
  }
  prewarmExplosions(scene); // no-op after the first call per scene
  const light = lightPool.find((l) => l.intensity <= 0.01) ?? null; // none free → skip light, never add one
  if (light) {
    light.position.set(x, y, z);
    light.distance = 24 * scale;
    light.intensity = 30 * scale;
    light.color.set(neon ?? "#ffa64d"); // pooled — recolor every use
  }
  // characteristic puff radii — the fire cloud is tight and the smoke column
  // outgrows it, which is what gives the blast a silhouette that keeps changing
  const fireRad = mega ? 6.5 : big ? 2.4 : 1.05;
  const smokeRad = mega ? 8 : big ? 3.0 : 1.35;
  const emberRad = mega ? 0.5 : 0.26;
  let life = 0;
  let debrisDone = false;
  return (dt: number): boolean => {
    life += dt;
    // FIRE: fast outward, heavy drag, grows, snuffs out into the smoke
    if (life <= fireLife) {
      const k = life / fireLife;
      const drag = Math.max(0, 1 - 5.5 * dt);
      for (let i = 0; i < fire.n; i++) {
        fire.cvx[i] *= drag; fire.cvz[i] *= drag; fire.cvy[i] = fire.cvy[i] * drag + 2.2 * dt;
        fire.cpx[i] += fire.cvx[i] * dt; fire.cpy[i] += fire.cvy[i] * dt; fire.cpz[i] += fire.cvz[i] * dt;
        fire.crot[i] += fire.cspin[i] * dt;
        const s = fireRad * (0.3 + 0.8 * k) * fire.cjit[i];
        orientBillboard(fire.cpx[i], fire.cpy[i], fire.cpz[i], 0, 0, 0, 0, s, s, fire.crot[i]);
        fire.im.setMatrixAt(i, _burstMat);
      }
      fire.im.instanceMatrix.needsUpdate = true;
      // hold full brightness through the first third, then die fast
      fireMat.opacity = Math.max(0, Math.min(1, (1 - k) * 2.1));
    } else if (fire.im.visible) {
      fire.im.visible = false; // out — stop paying for it a frame at a time
    }
    // SMOKE: slower, keeps rising, balloons, fades in then out over the tail
    {
      const k = Math.min(1, life / smokeLife);
      const drag = Math.max(0, 1 - 1.9 * dt);
      for (let i = 0; i < smoke.n; i++) {
        smoke.cvx[i] *= drag; smoke.cvz[i] *= drag; smoke.cvy[i] = smoke.cvy[i] * drag + 1.4 * dt;
        smoke.cpx[i] += smoke.cvx[i] * dt; smoke.cpy[i] += smoke.cvy[i] * dt; smoke.cpz[i] += smoke.cvz[i] * dt;
        smoke.crot[i] += smoke.cspin[i] * 0.45 * dt;
        const s = smokeRad * (0.4 + 1.7 * k) * smoke.cjit[i];
        orientBillboard(smoke.cpx[i], smoke.cpy[i], smoke.cpz[i], 0, 0, 0, 0, s, s, smoke.crot[i]);
        smoke.im.setMatrixAt(i, _burstMat);
      }
      smoke.im.instanceMatrix.needsUpdate = true;
      smokeMat.opacity = Math.max(0, 0.85 * Math.min(1, k * 9) * (1 - k) * (1 - k * 0.6));
    }
    // EMBERS: ballistic, streaked along their own velocity
    if (ember && emberMat) {
      const k = Math.min(1, life / emberLife);
      if (k < 1) {
        for (let i = 0; i < ember.n; i++) {
          ember.cvy[i] -= 14 * dt;
          ember.cpx[i] += ember.cvx[i] * dt; ember.cpy[i] += ember.cvy[i] * dt; ember.cpz[i] += ember.cvz[i] * dt;
          const spd = Math.hypot(ember.cvx[i], ember.cvy[i], ember.cvz[i]);
          orientBillboard(ember.cpx[i], ember.cpy[i], ember.cpz[i], ember.cvx[i], ember.cvy[i], ember.cvz[i], spd,
            emberRad * ember.cjit[i], emberRad * ember.cjit[i] * Math.min(4, 1 + spd * 0.14), 0);
          ember.im.setMatrixAt(i, _burstMat);
        }
        ember.im.instanceMatrix.needsUpdate = true;
        emberMat.opacity = Math.max(0, 0.95 * (1 - k * k));
      } else if (ember.im.visible) {
        ember.im.visible = false;
      }
    }
    if (light) light.intensity = Math.max(0, 30 * scale - life * 90);
    for (const e of extras) {
      if (e.obj === auraRing && auraMat) {
        // sweep out to the true kill radius in ~0.3s, then linger + fade
        const r = Math.min(aura, (life / 0.3) * aura);
        e.obj.scale.setScalar(Math.max(0.001, r));
        auraMat.opacity = Math.max(0, 0.75 - life * 1.0);
      } else if ((e.obj as THREE.Mesh).geometry instanceof THREE.RingGeometry) {
        e.obj.scale.setScalar(1 + life * 30);
        (e.mat as THREE.MeshBasicMaterial).opacity = Math.max(0, 0.9 - life * 1.1);
      }
    }
    if (debrisIM && !debrisDone) {
      _burstScale.setScalar(1);
      for (let i = 0; i < DEBRIS_N; i++) {
        dvy[i] -= 24 * dt;
        dpx[i] += dvx[i] * dt; dpy[i] += dvy[i] * dt; dpz[i] += dvz[i] * dt;
        drx[i] += dt * 7; drz[i] += dt * 5;
        if (dpy[i] < 0.2) { dpy[i] = 0.2; dvy[i] = 0; dvx[i] *= 0.9; dvz[i] *= 0.9; }
        _burstPos.set(dpx[i], dpy[i], dpz[i]);
        _burstEuler.set(drx[i], 0, drz[i]);
        _burstQuat.setFromEuler(_burstEuler);
        _burstMat.compose(_burstPos, _burstQuat, _burstScale);
        debrisIM.setMatrixAt(i, _burstMat);
      }
      debrisIM.instanceMatrix.needsUpdate = true;
    }
    // PHASE 1 teardown at the old lifetime: the light goes back to the pool and
    // the rings/debris leave. The smoke column outlives all of it (that is the
    // point of it), so it gets its own, later teardown below.
    if (!debrisDone && life > (mega ? 1.6 : big ? 0.8 : 0.5)) {
      debrisDone = true;
      if (light) light.intensity = 0; // back to the pool — stays in the scene
      for (const e of extras) { scene.remove(e.obj); e.mat.dispose(); }
      extras.length = 0;
      if (debrisIM) { scene.remove(debrisIM); debrisIM.dispose(); debrisMat?.dispose(); debrisIM = null; }
    }
    if (life > smokeLife) {
      if (light) light.intensity = 0;
      scene.remove(fire.im); fire.im.dispose(); fireMat.dispose();
      scene.remove(smoke.im); smoke.im.dispose(); smokeMat.dispose();
      if (ember) { scene.remove(ember.im); ember.im.dispose(); }
      emberMat?.dispose();
      for (const e of extras) { scene.remove(e.obj); e.mat.dispose(); }
      if (debrisIM) { scene.remove(debrisIM); debrisIM.dispose(); debrisMat?.dispose(); }
      return true;
    }
    return false;
  };
}


/** Rooftop cable gondolas (Skyhaven): platform meshes + the cable lines.
 *  Pure function of the shared route list + replicated clock, and it feeds
 *  the SAME dynamic colliders the server uses so prediction agrees. */
export class CablePlatforms {
  private plats: THREE.Mesh[] = [];
  private group = new THREE.Group();

  constructor(
    private scene: THREE.Scene,
    private routes: CableRoute[],
    private opts: { color: string; half: number; thick: number; period: number; solid?: number } = { color: "#00f0ff", half: CABLE.half, thick: CABLE.thick, period: CABLE.period },
  ) {
    // DIAMOND ANTI-SLIP PLATE (2026-08-01, the prop pass): a cable car and a
    // lift deck are the two surfaces in the game the player stands on at
    // point-blank range for seconds at a time, and both were a flat grey box.
    const platMat = new THREE.MeshStandardMaterial(propMaps("tread", { color: "#2a2f3d", roughness: 0.62, metalness: 0.5 }));
    const railMat = new THREE.MeshBasicMaterial({ color: opts.color });
    for (const r of routes) {
      const platGeo = new RoundedBoxGeometry(opts.half * 2, opts.thick, opts.half * 2, 1, 0.08);
      worldProjectUV(platGeo, 0.7);
      const plat = new THREE.Mesh(platGeo, platMat);
      plat.castShadow = true;
      plat.receiveShadow = true;
      const rail = new THREE.Mesh(new THREE.BoxGeometry(opts.half * 2, 0.06, 0.06), railMat);
      rail.position.y = 0.55;
      const armGeo = new THREE.BoxGeometry(0.12, 1.2, 0.12);
      worldProjectUV(armGeo, 0.7);
      const arm = new THREE.Mesh(armGeo, platMat);
      arm.position.y = 0.75;
      plat.add(rail, arm);
      this.plats.push(plat);
      this.group.add(plat);
      // the cable itself
      const a = new THREE.Vector3(...r.a).add(new THREE.Vector3(0, 1.3, 0));
      const b = new THREE.Vector3(...r.b).add(new THREE.Vector3(0, 1.3, 0));
      const line = new THREE.Line(
        new THREE.BufferGeometry().setFromPoints([a, b]),
        new THREE.LineBasicMaterial({ color: opts.color, transparent: true, opacity: 0.7 }),
      );
      this.group.add(line);
    }
    scene.add(this.group);
  }

  sync(tSec: number, prevSec: number, dynamic: DynBox[], pool: DynBox[]): void {
    const isElev = this.opts.period !== CABLE.period;
    const phaseStep = isElev ? 0.35 : 0.5; // matches the server
    const dwell = isElev ? ELEVATOR.dwell : CABLE.dwell; // both park at each end (cables 2 s)
    // for, not forEach, and pooled boxes (invariant 6): this runs EVERY frame
    // (60–240 Hz) for cables/elevators. `pushDyn` refills a pooled object indexed
    // by dynamic.length instead of allocating a fresh literal each frame.
    for (let i = 0; i < this.routes.length; i++) {
      const r = this.routes[i];
      const phase = i * phaseStep;
      const cur = cablePos(r, tSec, phase, this.opts.period, dwell), prev = cablePos(r, prevSec, phase, this.opts.period, dwell);
      this.plats[i].position.set(cur[0], cur[1] - this.opts.thick / 2, cur[2]);
      pushDyn(dynamic, pool,
        cur[0] - this.opts.half, cur[0] + this.opts.half,
        cur[1] - (this.opts.solid ?? this.opts.thick), cur[1],
        cur[2] - this.opts.half, cur[2] + this.opts.half,
        cur[0] - prev[0], cur[1] - prev[1], cur[2] - prev[2]);
    }
  }

  dispose(): void {
    this.scene.remove(this.group);
    // cables/elevators are rebuilt EVERY round — platform boxes, rails and
    // line geometries leaked per round without the deep dispose
    disposeGroup(this.group);
  }
}

/** Practice Field floating islands. These use the same shared clock + collider
 *  generation as the server so the visible islands and predicted collision stay
 *  locked together in real matches. */
export class PracticeMovingPlatforms {
  private group = new THREE.Group();
  readonly movers: PracticeMover[];

  constructor(private scene: THREE.Scene, map: MapDef) {
    this.movers = practiceMovers(map);
    const tex = mapTextures();
    for (const mover of this.movers) {
      const island = new THREE.Group();
      const [x, y, z] = practiceMoverPose(mover, 0);
      island.position.set(x, y, z);
      for (const part of mover.parts) {
        const [pw, ph, pd] = part.s;
        // any TALL part is a building wall (incl. thin hollow-tower walls and the
        // narrow door lintel): give it the Skyhaven facade skin (lit windows)
        // instead of a flat colour — the flying tower was a blue block
        const isBuilding = ph >= 5;
        const geo = new RoundedBoxGeometry(pw, ph, pd, 2, 0.12);
        // the DECK parts (everything that is not a facade) carry the prop
        // pass's weathered timber now — these are floating islands the player
        // lands on and walks around, and they were a flat brown box.
        worldProjectUV(geo, isBuilding ? 4.5 : 1.4);
        const surface: THREE.MeshStandardMaterialParameters = isBuilding
          ? { color: "#ffffff", map: tex.facade.map, emissiveMap: tex.facadeGlow }
          : propMaps("oak", { color: part.color ?? "#6b5b48" });
        const mat = new THREE.MeshStandardMaterial({
          ...surface,
          // neon-violet lit windows — the flying tower belongs to the neon
          // dimension, so its facade glows violet rather than plain white
          emissive: isBuilding ? "#9b5cff" : (part.glow ?? "#000000"),
          emissiveIntensity: isBuilding ? 0.95 : (part.glow ? 0.45 : 0),
          roughness: isBuilding ? 0.7 : 0.78,
          metalness: 0.1,
        });
        const mesh = new THREE.Mesh(geo, mat);
        mesh.position.set(part.offset[0], part.offset[1], part.offset[2]);
        mesh.castShadow = true;
        mesh.receiveShadow = true;
        island.add(mesh);
      }
      this.group.add(island);
    }
    if (this.movers.length) scene.add(this.group);
  }

  get active(): boolean { return this.movers.length > 0; }

  sync(tSec: number, prevSec: number, dynamic: DynBox[], pool: DynBox[]): void {
    practiceMoverColliderBoxes(this.movers, tSec, prevSec, dynamic, pool);
    for (let i = 0; i < this.movers.length; i++) {
      const [x, y, z] = practiceMoverPose(this.movers[i], tSec);
      this.group.children[i]?.position.set(x, y, z);
    }
  }

  dispose(): void {
    this.scene.remove(this.group);
    disposeGroup(this.group);
  }
}

function discoTileTexture(): THREE.CanvasTexture {
  const cvs = document.createElement("canvas");
  cvs.width = cvs.height = 256;
  const g = cvs.getContext("2d")!;
  const colors = ["#f7fbff", "#aeb8c7", "#54e8ff", "#ff4fd8", "#1a2434"];
  for (let y = 0; y < 16; y++) {
    for (let x = 0; x < 16; x++) {
      const i = (x * 7 + y * 11 + ((x + y) % 3) * 5) % colors.length;
      g.fillStyle = colors[i];
      g.fillRect(x * 16, y * 16, 15, 15);
      g.fillStyle = "rgba(255,255,255,0.35)";
      g.fillRect(x * 16 + 2, y * 16 + 2, 5, 3);
    }
  }
  const tex = new THREE.CanvasTexture(cvs);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  return tex;
}

/** Skyhaven disco ball: visual sphere + a small hidden top deck so players can
 *  land on it without the whole ball becoming an invisible collision wall. */
export class SkyhavenDiscoBallPlatform {
  private cfg: SkyhavenDiscoBall | null;
  private group = new THREE.Group();
  private ball = new THREE.Group();
  private halo: THREE.Mesh | null = null;

  constructor(private scene: THREE.Scene, map: MapDef) {
    this.cfg = skyhavenDiscoBall(map);
    if (!this.cfg) return;
    const tex = discoTileTexture();
    const mirror = new THREE.MeshStandardMaterial({
      color: "#ffffff",
      map: tex,
      metalness: 0.88,
      roughness: 0.18,
      emissive: "#7af8ff",
      emissiveIntensity: 0.18,
    });
    const shell = new THREE.Mesh(new THREE.SphereGeometry(this.cfg.radius, 48, 28), mirror);
    shell.castShadow = true;
    shell.receiveShadow = true;
    this.ball.add(shell);
    const ringMat = new THREE.MeshBasicMaterial({ color: "#ff4fd8", transparent: true, opacity: 0.62, blending: THREE.AdditiveBlending });
    const equator = new THREE.Mesh(new THREE.TorusGeometry(this.cfg.radius * 1.03, 0.08, 6, 96), ringMat);
    equator.rotation.x = Math.PI / 2;
    this.ball.add(equator);
    this.halo = new THREE.Mesh(
      new THREE.TorusGeometry(this.cfg.platformHalf, 0.16, 8, 96),
      new THREE.MeshBasicMaterial({ color: "#8fffff", transparent: true, opacity: 0.55, blending: THREE.AdditiveBlending }),
    );
    this.halo.rotation.x = Math.PI / 2;
    this.halo.position.y = this.cfg.radius * 0.82 + 0.05;
    this.group.add(this.ball, this.halo);
    const glow = new THREE.PointLight("#8fffff", 32, 48, 2);
    glow.position.y = this.cfg.radius * 0.3;
    this.group.add(glow);
    const p = skyhavenDiscoPose(this.cfg, 0);
    this.group.position.set(p.x, p.y, p.z);
    this.scene.add(this.group);
  }

  get active(): boolean { return this.cfg !== null; }

  sync(tSec: number, prevSec: number, dynamic: DynBox[], pool: DynBox[]): void {
    if (!this.cfg) return;
    const p = skyhavenDiscoPose(this.cfg, tSec);
    this.group.position.set(p.x, p.y, p.z);
    this.ball.rotation.set(p.roll, p.yaw, p.roll * 0.6);
    if (this.halo) this.halo.rotation.z = -p.yaw * 0.45;
    skyhavenDiscoColliderBoxes(this.cfg, tSec, prevSec, dynamic, pool);
  }

  dispose(): void {
    this.scene.remove(this.group);
    disposeGroup(this.group);
  }
}

/** Wild-West SANDWORM (Dune): a long, armoured, segmented worm. Each segment
 *  follows the broadcast head trail and is oriented (lookAt) toward the segment
 *  AHEAD of it, so the body curves along its path and the maw always leads.
 *  The whole thing arcs up out of the sand head-first by `surfaced`. */
export class WormMesh {
  readonly group = new THREE.Group();
  private segs: THREE.Group[] = [];
  private trail: { x: number; z: number }[] = [];
  private static readonly N = 15;
  private static readonly SPACING = 4;
  private tmp = new THREE.Vector3();
  /* 2026-08-01, mission `graphics` (the BEAST pass). The worm was the single
   * most expensive entity in the game to draw: 15 segments x (a flat-shaded UV
   * sphere + a torus + 3 cones) plus a maw of 30 individually-meshed cone
   * teeth and 3 more tori — 100 draw calls, not one of them carrying a
   * texture. Every segment is now ONE merged `grit` buffer (tapered trunk +
   * leading armour ring + a chevron of dorsal and lateral spines + ventral
   * plates) and the whole maw is three: shell, gullet, teeth. Same animation,
   * ~6x fewer draw calls, and a sand-caked carapace you can read up close. */
  constructor(private scene: THREE.Scene) {
    const N = WormMesh.N;
    const R = 3.7 * 1.12;      // head radius
    const MOUTH_Z = R * 1.35;  // the lip plane, in head-local +Z
    const THROAT_Z = R * 0.5;  // where the head trunk ends and the maw flare starts
    for (let i = 0; i < N; i++) {
      const t = i / (N - 1);
      const r = 3.7 * (1 - t * 0.72) * (i === 0 ? 1.12 : 1); // head biggest, tapers to a thin tail
      const seg = new THREE.Group();
      // Forward is +Z: Three.js lookAt() points local +Z at the target, so the
      // maw side must live there. The trunk tapers slightly toward the tail-ward
      // end of each segment so the joins read as overlapping rings, not beads.
      //
      // Trunk length is keyed to SPACING, NOT to the segment radius. It used to
      // be `2.6 x diameter`, which for the fat front segments meant an 18 m
      // trunk on a 4 m spacing: segment 1's nose landed at z = +5.1 in head
      // space and stuck straight out through the maw (the "block in the mouth"),
      // and the head's own trunk cap sat 6 m in FRONT of its teeth. Keyed to
      // spacing, every trunk overlaps its neighbour by ~2 m and nothing reaches
      // the lips.
      const isHead = i === 0;
      const len = WormMesh.SPACING * (isHead ? 1.7 : 1.9);
      const pieces: Piece[] = [
        {
          g: taperVol(r * 2, len, r * 2, isHead ? 0.84 : 0.9, 1),
          r: [Math.PI / 2, 0, 0],
          // the head's trunk stops at THROAT_Z so the maw has somewhere to open
          ...(isHead ? { p: [0, 0, THROAT_Z - len / 2] as [number, number, number] } : {}),
        },
      ];
      // leading armour ring — the overlapping chitin collar (the head wears the
      // maw flare instead)
      if (!isHead) pieces.push({ g: new THREE.TorusGeometry(r * 1.04, r * 0.22, 8, 20), p: [0, 0, r * 0.95] });
      if (i > 0 && i < N - 2) {
        // a chevron of spines: 3 dorsal, 2 lateral, swept back
        for (const [sx, sy, len] of [
          [0, 1, 1.15], [-0.5, 0.9, 0.85], [0.5, 0.9, 0.85],
          [-0.95, 0.35, 0.6], [0.95, 0.35, 0.6],
        ] as const) {
          pieces.push({
            g: spike(r * 0.17, r * len),
            p: [sx * r * 0.92, sy * r * 0.92, -r * 0.1],
            r: [-0.4, 0, Math.atan2(-sx, sy)],
          });
        }
        // ventral gripper plates — how a worm actually pulls itself through rock
        for (const sx of [-0.5, 0.5]) {
          pieces.push({ g: vol(r * 0.7, r * 0.28, r * 1.5, 0.8), p: [sx * r * 0.7, -r * 0.92, 0] });
        }
      }
      seg.add(surf("grit", i % 2 ? "#c9a869" : "#d8b87a", 1.9, pieces, { rough: 0.95 }));
      this.group.add(seg);
      this.segs.push(seg);
    }
    // HEAD maw: the trunk ends at THROAT_Z and flares open into a ring of
    // armour, and the hole in the middle is a real recessed funnel — a cone
    // narrowing from the lip plane back INSIDE the trunk, capped by a small
    // dark disc. Because the funnel narrows monotonically and is capped, no
    // frontal ray can reach the tan trunk cap behind it: you see throat, not a
    // plug. (Previous builds either left a solid snout sticking through the
    // teeth, or deleted the throat entirely and showed the flat trunk cap.)
    const head = this.segs[0];
    const toothPiece = (th: number, rr: number, zz: number, rad: number, len: number, inward: number, forward: number): Piece => {
      const g = spike(rad, len);
      const dir = new THREE.Vector3(-Math.cos(th) * inward, -Math.sin(th) * inward, forward).normalize();
      const q = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir);
      const e = new THREE.Euler().setFromQuaternion(q);
      return { g, p: [Math.cos(th) * rr, Math.sin(th) * rr, zz], r: [e.x, e.y, e.z] };
    };
    const shell: Piece[] = [];
    const fangs: Piece[] = [];
    // outer lip wall: the trunk widening back out into the mouth rim
    shell.push({
      g: new THREE.CylinderGeometry(R * 1.0, R * 0.86, MOUTH_Z - THROAT_Z, 20, 1, true),
      r: [Math.PI / 2, 0, 0],
      p: [0, 0, (MOUTH_Z + THROAT_Z) / 2],
    });
    shell.push({ g: new THREE.TorusGeometry(R * 0.99, R * 0.09, 6, 24), p: [0, 0, MOUTH_Z] });
    for (let k = 0; k < 12; k++) {
      const th = (k / 12) * Math.PI * 2;
      const upper = Math.sin(th) > 0.1;
      fangs.push(toothPiece(
        th, R * 0.9, MOUTH_Z - R * 0.06,
        R * (upper ? 0.085 : 0.065),
        R * (upper ? 0.6 : 0.44),
        0.5,
        1.0,
      ));
    }
    // A broad upper brow, a shorter lower jaw and two side cutters: less
    // flower-like than four equal plates around a circle, and readable at 80 m.
    shell.push(
      { g: taperVol(R * 1.5, R * 0.5, R * 1.2, 0.52, 0.9), p: [0, R * 0.86, MOUTH_Z - R * 0.5], r: [0.28, 0, 0] },
      { g: taperVol(R * 1.05, R * 0.3, R * 0.8, 0.48, 0.85), p: [0, -R * 0.8, MOUTH_Z - R * 0.45], r: [-0.28, 0, Math.PI] },
    );
    for (const side of [-1, 1] as const) {
      shell.push({
        g: taperVol(R * 0.38, R * 1.18, R * 0.32, 0.38, 0.85),
        p: [side * R * 0.98, 0, MOUTH_Z - R * 0.35],
        r: [1.05, 0, side * 0.62],
      });
    }
    // the throat: funnel + cap, one dark merged buffer, drawn double-sided so
    // the inside of the cone is what you actually look at
    const throatBack = -R * 0.6;
    const gullet = surf("flesh", "#5e1e1e", 0.9, [
      {
        g: new THREE.CylinderGeometry(R * 0.96, R * 0.13, MOUTH_Z - throatBack, 20, 1, true),
        r: [Math.PI / 2, 0, 0],
        p: [0, 0, (MOUTH_Z + throatBack) / 2],
      },
      { g: new THREE.CircleGeometry(R * 0.14, 12), p: [0, 0, throatBack] },
    ], { rough: 0.7 });
    (gullet.material as THREE.MeshStandardMaterial).side = THREE.DoubleSide;
    head.add(gullet);
    head.add(surf("grit", "#6f5330", 1.4, shell, { rough: 0.8, metal: 0.1 }));
    head.add(surf("horn", "#efe6cf", 0.9, fangs, { rough: 0.45, metal: 0.05 }));
    scene.add(this.group);
  }

  update(x: number, z: number, _yaw: number, surfaced: number, _dt: number): void {
    // teleport (re-arm across the map / round reset): restart the trail so the
    // body doesn't stretch a rubber band to the new spot
    const last = this.trail[0];
    if (last && Math.hypot(x - last.x, z - last.z) > 20) this.trail.length = 0;
    if (!this.trail.length || Math.hypot(x - this.trail[0].x, z - this.trail[0].z) > 1.0) {
      this.trail.unshift({ x, z });
      if (this.trail.length > 160) this.trail.pop();
    }
    const { N, SPACING } = WormMesh;
    const GROUND = 0.4, RISE = 13;
    const yAt = (i: number): number => {
      const front = 1 - i / N;
      return GROUND - 9 + surfaced * (9 + RISE * front * front); // buried, arcs up head-first
    };
    // CONTINUOUS follow-the-leader: walk the breadcrumb polyline by ARC LENGTH
    // starting from the LIVE head position. The old per-index placement pinned
    // every segment (head included) to discrete ~1 m breadcrumbs, so the whole
    // worm advanced in 1 m jerks on top of the 30 Hz patch steps.
    let cx = x, cz = z; // polyline cursor, `acc` metres behind the head
    let acc = 0;
    let idx = 0;
    for (let i = 0; i < this.segs.length; i++) {
      const want = i * SPACING; // arc distance behind the head for THIS segment
      while (acc < want && idx < this.trail.length) {
        const q = this.trail[idx];
        const d = Math.hypot(q.x - cx, q.z - cz);
        if (acc + d >= want && d > 0) {
          const f = (want - acc) / d;
          cx += (q.x - cx) * f;
          cz += (q.z - cz) * f;
          acc = want;
          break; // cursor sits exactly at `want`; trail[idx] is still ahead
        }
        acc += d; cx = q.x; cz = q.z; idx++;
      }
      this.segs[i].position.set(cx, yAt(i), cz);
    }
    // orientation pass: +Z (the maw side) leads — each segment looks at the
    // one ahead; the head looks along its own direction of motion
    const h = this.segs[0].position, s1 = this.segs[1].position;
    // The body arc already raises the head above the neck. Adding a large
    // extra look-up target made the maw point skyward and read as a vertical
    // pipe from the Wild West sightline; keep only a slight breaching lift.
    this.tmp.set(h.x + (h.x - s1.x), h.y + surfaced * 1.2, h.z + (h.z - s1.z));
    this.segs[0].lookAt(this.tmp);
    for (let i = 1; i < this.segs.length; i++) {
      this.segs[i].lookAt(this.segs[i - 1].position);
    }
    this.group.visible = surfaced > 0.02;
  }

  /** Hide without disposing — the worm re-arms every round on its map, so the
   *  ~90-mesh build must not repeat at round start. update() re-shows it via
   *  the `surfaced` gate; clearing the trail keeps a near-old-spot re-arm from
   *  tracing the previous circuit. */
  setVisible(v: boolean): void {
    this.group.visible = v;
    if (!v) this.trail.length = 0;
  }

  dispose(scene: THREE.Scene): void {
    scene.remove(this.group);
    disposeGroup(this.group);
  }
}
